import React, { useState, useEffect, useMemo, useRef, useCallback, startTransition, memo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Alert,
  CircularProgress,
  LinearProgress,
  IconButton,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Container,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Link,
  Checkbox,
  FormControlLabel,
  Switch,
  AlertTitle,
  Snackbar,
  Stack,
  Autocomplete,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Sync as SyncIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  FilterList as FilterListIcon,
  BarChart as BarChartIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Chat as ChatIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  TableRows as TableRowsIcon,
  CalendarViewMonth as CalendarViewMonthIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import { api } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { getUserUiState, putUserUiState } from '../utils/userUiStateApi';
import {
  BookingFlightDetails,
  buildFlightPayload,
  flightToFormFields,
  formatFlightDisplay,
  formatFlightStatusDisplay,
  flightStatusChipColor,
} from '../utils/bookingFlightDetails';
import BookingCommsTab from './BookingCommsTab';
import BookingCommsPanel from './BookingCommsPanel';
import PropertyNicknameChip from './PropertyNicknameChip';
import BookingsStayCalendar from './BookingsStayCalendar';
import { isExportSource, isManualOrExportSource } from '../utils/bookingSources';
import { airbnbConfirmationCodeFromBooking } from '../utils/airbnbConfirmationCode';
import { createToast, toastFromApiError, type AppToast } from '../utils/appToast';
import { APP_TOP_TOAST_SNACKBAR_SX } from '../utils/appSnackbarSx';
import { mergeHighConfidenceCombinedBookings } from '../utils/mergeConfirmedCombinedBookings';
import { isCancelledBookingStatus } from '../utils/bookingStatus';
import { exportBookingsToPdf, exportBookingsToXlsx } from '../utils/exportBookingsTable';
import {
  bookingIdMatchesFilter,
  formatBookingIdDisplay,
  mergedBookingIdTitle,
} from '../utils/bookingIdDisplay';
import {
  classifyStaleIcalGhosts,
  filterBookingsForStayCalendar,
  STALE_ICAL_GHOST_LEGEND,
  STALE_ICAL_GHOST_ROW_SX,
  type StaleIcalGhostCategory,
} from '../utils/staleIcalGhostCategory';
import {
  classifyIcalNestingRemnants,
  ICAL_NESTING_REMNANT_LEGEND,
  ICAL_NESTING_REMNANT_ROW_SX,
} from '../utils/icalNestingRemnants';
import { resolveBookingPlatformLink } from '../utils/bookingPlatformUrl';
import { mergeDayIntervals, addDays, startOfMonth, daysInMonth, ymdKey } from '../utils/bookingsStayCalendar';
import { ToastCodeCaption } from './ToastCodeCaption';

const BOOKING_LINK_PRED_SCOPE_KEY = 'bookingLinkPredictionScope';
const BOOKING_LINK_PRED_SCOPE_DB_KEY = 'calendar.booking_link_prediction_scope';

function loadStoredPredictionScope(): { min: string; max: string; propertyId: string } {
  if (typeof window === 'undefined') {
    return { min: '2025-11-01', max: '', propertyId: '' };
  }
  try {
    const raw = sessionStorage.getItem(BOOKING_LINK_PRED_SCOPE_KEY);
    if (raw) {
      const o = JSON.parse(raw) as Record<string, unknown>;
      return {
        min: typeof o.min === 'string' && o.min ? o.min : '2025-11-01',
        max: typeof o.max === 'string' ? o.max : '',
        propertyId: typeof o.propertyId === 'string' ? o.propertyId : '',
      };
    }
  } catch {
    /* ignore */
  }
  return { min: '2025-11-01', max: '', propertyId: '' };
}

let initialPredictionScopeCache: { min: string; max: string; propertyId: string } | undefined;
function getInitialPredictionScope() {
  if (initialPredictionScopeCache === undefined) {
    initialPredictionScopeCache = loadStoredPredictionScope();
  }
  return initialPredictionScopeCache;
}

function persistPredictionScope(min: string, max: string, propertyId: string) {
  try {
    sessionStorage.setItem(
      BOOKING_LINK_PRED_SCOPE_KEY,
      JSON.stringify({ min, max, propertyId })
    );
  } catch {
    /* ignore */
  }
  void putUserUiState(
    BOOKING_LINK_PRED_SCOPE_DB_KEY,
    { min, max, propertyId },
    'sessionStorage',
    true
  );
}

/** True for cold start / proxy errors we should retry (includes “CORS” when response never reached the app). */
function isRetryableBackendError(err: any): boolean {
  const status = err.response?.status;
  if (status === 503 || status === 502 || status === 504) return true;
  if (err.code === 'ERR_NETWORK') return true;
  const msg = String(err.message || '');
  if (msg === 'Network Error' || msg.includes('Network Error')) return true;
  if (msg.includes('CORS') || msg.includes('Failed to fetch')) return true;
  return false;
}

const API_TRANSIENT_EXPLAIN =
  'The API may be restarting or temporarily unavailable. First responses can fail until health returns JSON with "status":"healthy".';

type BackendReachabilityBanner = {
  severity: 'info' | 'warning' | 'error' | 'success';
  title: string;
  body: string;
};

function classifyHealthCheckFailure(err: any): BackendReachabilityBanner {
  const status = err.response?.status;
  const msg = String(err.message || '');
  const noResponse = !err.response;

  if (status === 503 || status === 502 || status === 504) {
    return {
      severity: 'warning',
      title: `Health check: HTTP ${status} (service unavailable)`,
      body: `${API_TRANSIENT_EXPLAIN} This screen will keep retrying with backoff and longer timeouts—waiting here is normal.`,
    };
  }
  if (err.code === 'ECONNABORTED' || msg.toLowerCase().includes('timeout')) {
    return {
      severity: 'warning',
      title: 'Health check timed out (15s)',
      body: `${API_TRANSIENT_EXPLAIN} The main bookings request waits longer and retries; a timeout here does not always mean the API is down.`,
    };
  }
  if (
    noResponse &&
    (err.code === 'ERR_NETWORK' ||
      msg === 'Network Error' ||
      msg.includes('Network Error') ||
      msg.includes('CORS') ||
      msg.includes('Failed to fetch'))
  ) {
    return {
      severity: 'warning',
      title: 'Chrome shows “CORS” or “network error” — usually not a broken app',
      body: `While the API is restarting, the first HTTP responses are sometimes empty error pages **without** CORS headers. The browser then reports a CORS failure even though the real issue is “server still starting.” ${API_TRANSIENT_EXPLAIN} Use the health link below: when you see JSON with "status":"healthy", come back—retries here should succeed.`,
    };
  }
  return {
    severity: 'error',
    title: 'Health check failed',
    body: status
      ? `HTTP ${status}. If this keeps happening, check the API container logs on the Hetzner VPS or restart rrltd-api.`
      : `${msg || 'Unknown error'}. Open the health URL below or check the API on the VPS.`,
  };
}

function userFacingDataLoadError(err: any, context: 'bookings' | 'predictions' | 'payments'): string {
  const status = err.response?.status;
  const msg = String(err.message || '');
  if (err.code === 'ECONNABORTED' || msg.toLowerCase().includes('timeout')) {
    const narrow =
      context === 'payments'
        ? 'On the Payments tab: set a check-in date range, pick one property, or enter a smaller “Max bookings” (e.g. 100–300), then click Load workspace again.'
        : context === 'predictions'
          ? 'Try a narrower prediction scope (dates / property), then run again.'
          : 'Try again after the API is responsive, or reduce filters if the request is very large.';
    return ['The request timed out before the server finished.', '', narrow, '', API_TRANSIENT_EXPLAIN].join('\n');
  }
  const isUnavailable = status === 503 || status === 502 || status === 504;
  const isNetShape =
    err.code === 'ERR_NETWORK' ||
    msg.includes('CORS') ||
    msg === 'Network Error' ||
    msg.includes('Network Error') ||
    (!err.response && isRetryableBackendError(err));

  if (msg.includes('Max retries exceeded')) {
    const hint =
      context === 'predictions'
        ? 'Try Run predictions again in a minute.'
        : context === 'payments'
          ? 'Open the Payments tab again after the API responds, or refresh the page.'
          : 'Use Retry below or refresh the page.';
    return [
      'The API did not become ready in time after several automatic retries.',
      '',
      API_TRANSIENT_EXPLAIN,
      '',
      hint,
      '',
      'Tip: open GET /api/health in a new tab until it returns JSON with "status":"healthy".',
    ].join('\n');
  }

  if (isUnavailable) {
    const hint =
      context === 'predictions'
        ? 'Wait a minute and click Run predictions again.'
        : context === 'payments'
          ? 'Retry loading the Payments tab after the API responds.'
          : 'Use Retry below or keep this page open while retries run.';
    return [
      `The server returned HTTP ${status} (unavailable).`,
      '',
      API_TRANSIENT_EXPLAIN,
      '',
      hint,
    ].join('\n');
  }
  if (isNetShape) {
    return [
      'Your browser could not complete the request.',
      '',
      'If DevTools mentions CORS, the failing response may never have reached your Flask app, so CORS headers were missing (often means the API was restarting).',
      '',
      API_TRANSIENT_EXPLAIN,
      '',
      context === 'predictions'
        ? 'When the health URL works, run predictions again.'
        : context === 'payments'
          ? 'When the health URL works, reopen Payments.'
          : 'When the health URL works, use Retry.',
    ].join('\n');
  }
  return err.response?.data?.error || err.message || 'Request failed';
}

/**
 * Default Revolut payment matching parameters — keep in sync with
 * backend/routes/calendar.py (BOOKING_REVOLUT_* and UK_HOST_PROPERTY_NICKNAME).
 * Used so the Payments tab can show scope before the workspace API returns.
 */
const REVOLUT_PAYMENTS_SCOPE_DEFAULT = {
  payoutLagDays: 2,
  paymentWindowDaysAfterExpectation: 12,
  vrboIrelandWindowDaysAfterExpectation: 300,
  ukPropertyNickname: '67RR',
  matchingPreset: '1-3-3',
} as const;

const PAYMENTS_WORKSPACE_SCOPE_FORM_KEY = 'paymentsWorkspaceScopeForm';
const PAYMENTS_WORKSPACE_SCOPE_DB_KEY = 'calendar.payments_workspace_scope_form';

function loadPaymentsWorkspaceScopeForm(): {
  min: string;
  max: string;
  propertyId: string;
  maxBookings: string;
} {
  if (typeof window === 'undefined') {
    return { min: '', max: '', propertyId: '', maxBookings: '200' };
  }
  try {
    const raw = sessionStorage.getItem(PAYMENTS_WORKSPACE_SCOPE_FORM_KEY);
    if (raw) {
      const o = JSON.parse(raw) as Record<string, unknown>;
      return {
        min: typeof o.min === 'string' ? o.min : '',
        max: typeof o.max === 'string' ? o.max : '',
        propertyId: typeof o.propertyId === 'string' ? o.propertyId : '',
        maxBookings:
          o.maxBookings !== undefined && o.maxBookings !== null && String(o.maxBookings).trim() !== ''
            ? String(o.maxBookings)
            : '',
      };
    }
  } catch {
    /* ignore */
  }
  return { min: '', max: '', propertyId: '', maxBookings: '200' };
}

/** Shown while loading bookings or predictions when we have a status message */
function BackendReachabilityAlert({
  banner,
  healthUrl,
}: {
  banner: BackendReachabilityBanner | null;
  healthUrl: string;
}) {
  if (!banner) return null;
  return (
    <Alert severity={banner.severity} sx={{ maxWidth: 640, textAlign: 'left' }} variant="outlined">
      <AlertTitle>{banner.title}</AlertTitle>
      <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-line' }}>
        {banner.body}
      </Typography>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5, display: 'block' }}>
        <Link href={healthUrl} target="_blank" rel="noopener noreferrer">
          GET /api/health in a new tab
        </Link>{' '}
        — you want JSON with &quot;status&quot;: &quot;healthy&quot; when the API is ready.
      </Typography>
    </Alert>
  );
}

interface Booking {
  id: number;
  property_id: number;
  property_nickname?: string;
  platform: string;
  platform_id?: number | null;
  platform_slug?: string | null;
  account_owner?: string | null;
  account_owner_id?: number | null;
  account_owner_slug?: string | null;
  source: string;
  external_booking_id?: string;
  ical_uid?: string;
  guest_name?: string;
  guest_email?: string;
  guest_phone_last_4?: string;
  reservation_url?: string;
  check_in_date: string;
  check_out_date: string;
  nights: number;
  adults_count: number;
  children_count: number;
  total_payout_amount?: number;
  host_payout_amount?: number;
  currency: string;
  status: string;
  booking_type: string;
  summary?: string;
  description?: string;
  notes?: string;
  last_sync?: string;
  booked_at?: string;
  booked_at_fallback?: string;
  booked_at_display?: string;
  /** Computed Airbnb confirmation / booking reference (e.g. HMYKK2QX43). */
  booking_ref?: string;
  /** Days from booked date to check-in (null when either date is missing). */
  lead_time?: number | null;
  /** Combined-tab merge of a confirmed iCal↔Manual pair. */
  is_merged_pair?: boolean;
  merged_booking_ids?: number[];
  linked_ical_booking_id?: number;
  linked_manual_booking_id?: number;
  booking_link_id?: number;
  merged_from_sources?: string[];
  flight_in?: BookingFlightDetails | null;
  flight_out?: BookingFlightDetails | null;
  flight_in_display?: string;
  flight_out_display?: string;
  flight_in_status_display?: string;
  flight_out_status_display?: string;
  created_at?: string;
  updated_at?: string;
  booking_history?: Array<{ date_fetched: string; data: Record<string, unknown> }>;
  updates_count?: number;  // Computed: number of times re-fetched (booking_history.length - 1)
}

type Order = 'asc' | 'desc';

interface HeadCell {
  id: keyof Booking | 'actions';
  label: string;
  numeric: boolean;
  disableSorting?: boolean;
  /** CSS class for fit-to-viewport exceptions (actions stay visible). */
  cellClassName?: string;
}

/**
 * Fit-to-viewport column policy (same idea as VAT TX table):
 * - table width 100% + table-layout:fixed
 * - % widths via colgroup (Guest Name is a dedicated wide column)
 * - ellipsis truncate; never set a table minWidth that forces horizontal scroll
 */
const BOOKINGS_TABLE_SX = {
  width: '100%',
  maxWidth: '100%',
  tableLayout: 'fixed' as const,
  '& .MuiTableCell-root': {
    px: 0.5,
    py: 0.4,
    fontSize: '0.75rem',
    verticalAlign: 'middle',
    overflow: 'hidden',
  },
  '& .MuiTableBody-root .MuiTableCell-root:not(.bookings-tx-actions):not(.bookings-tx-status):not(.bookings-tx-select)': {
    maxWidth: 0,
  },
  '& .MuiTableCell-root.bookings-tx-actions': {
    maxWidth: 'none',
    overflow: 'visible',
  },
  '& .MuiTableCell-root.bookings-tx-status': {
    maxWidth: 'none',
    overflow: 'visible',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableCell-root.bookings-tx-select': {
    width: 48,
    minWidth: 48,
    maxWidth: 48,
    px: 0,
    overflow: 'visible',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    textAlign: 'center',
    whiteSpace: 'normal',
    lineHeight: 1.15,
    verticalAlign: 'bottom',
    maxWidth: 'none',
    overflow: 'visible',
  },
  '& .MuiTableHead-root .MuiTableSortLabel-root': {
    justifyContent: 'center',
    alignItems: 'flex-end',
    width: '100%',
    color: 'inherit',
    height: 'auto',
    whiteSpace: 'normal',
  },
  '& .MuiTableHead-root .MuiTableSortLabel-root.Mui-active': {
    color: 'inherit',
  },
  '& .MuiTableHead-root .MuiTableSortLabel-icon': {
    position: 'static',
    marginLeft: '2px',
    marginBottom: '2px',
    opacity: 0.7,
  },
};

const bookingsHeaderLabel = (label: string) => (
  <Box
    component="span"
    sx={{
      display: 'block',
      textAlign: 'center',
      whiteSpace: 'pre-line',
      lineHeight: 1.15,
      fontWeight: 700,
      overflow: 'visible',
      textOverflow: 'clip',
      wordBreak: 'keep-all',
      hyphens: 'manual',
    }}
  >
    {label}
  </Box>
);

const bookingsTruncateSx = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
  maxWidth: '100%',
};

const bookingsChipSx = {
  maxWidth: '100%',
  height: 22,
  '& .MuiChip-label': {
    px: 0.6,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
  },
};

/** Status chips must show the full label (e.g. "cancelled by guest"). */
const bookingsStatusChipSx = {
  maxWidth: 'none',
  height: 22,
  '& .MuiChip-label': {
    px: 0.75,
    overflow: 'visible',
    textOverflow: 'clip',
    whiteSpace: 'nowrap' as const,
    display: 'block',
  },
};

/** Fixed % columns sized so each header title is readable; Guest Name is wide enough for typical names. */
const BOOKINGS_COL_PCT = {
  /** Checkbox uses a fixed px width in colgroup so it never clips under table-layout:fixed. */
  id: 3.2,
  /** "Last Synced" (2 lines) + en-GB datetime body. */
  synced: 7.0,
  property: 5.0,
  platform: 4.5,
  /** "Booking Ref" (2 lines) + confirmation code. */
  bookingRef: 6.0,
  source: 4.5,
  /** "Guest Name" (2 lines) — room for full guest names. */
  guest: 9.0,
  /** "19 Aug 2026" style dates must not ellipsize. */
  checkIn: 6.5,
  checkOut: 6.5,
  nights: 4.5,
  adults: 4.5,
  children: 4.5,
  /** Currency amounts e.g. "€5,202.35" / "£1,142.34". */
  amount: 6.0,
  /** Full status labels e.g. "awaiting guest review". */
  status: 7.0,
  updates: 4.0,
  booked: 7.0,
  /** "Lead Time" (2 lines) — days booked before check-in. */
  leadTime: 4.0,
  actions: 5.0,
};

const bookingsColgroup = () => {
  const p = BOOKINGS_COL_PCT;
  return [
    { key: 'select', width: '48px' },
    { key: 'id', width: `${p.id}%` },
    { key: 'synced', width: `${p.synced}%` },
    { key: 'property', width: `${p.property}%` },
    { key: 'platform', width: `${p.platform}%` },
    { key: 'bookingRef', width: `${p.bookingRef}%` },
    { key: 'source', width: `${p.source}%` },
    { key: 'guest', width: `${p.guest}%` },
    { key: 'checkIn', width: `${p.checkIn}%` },
    { key: 'checkOut', width: `${p.checkOut}%` },
    { key: 'nights', width: `${p.nights}%` },
    { key: 'adults', width: `${p.adults}%` },
    { key: 'children', width: `${p.children}%` },
    { key: 'amount', width: `${p.amount}%` },
    { key: 'status', width: `${p.status}%` },
    { key: 'updates', width: `${p.updates}%` },
    { key: 'booked', width: `${p.booked}%` },
    { key: 'leadTime', width: `${p.leadTime}%` },
    { key: 'actions', width: `${p.actions}%` },
  ];
};

const headCells: HeadCell[] = [
  { id: 'id', numeric: true, label: 'ID' },
  { id: 'last_sync', numeric: false, label: 'Last\nSynced' },
  { id: 'property_nickname', numeric: false, label: 'Property' },
  { id: 'platform', numeric: false, label: 'Platform' },
  { id: 'booking_ref', numeric: false, label: 'Booking\nRef' },
  { id: 'source', numeric: false, label: 'Source' },
  { id: 'guest_name', numeric: false, label: 'Guest\nName' },
  { id: 'check_in_date', numeric: false, label: 'Check\nIn' },
  { id: 'check_out_date', numeric: false, label: 'Check\nOut' },
  { id: 'nights', numeric: true, label: 'No. of\nNights' },
  { id: 'adults_count', numeric: true, label: 'No. of\nAdults' },
  { id: 'children_count', numeric: true, label: 'No. of\nChildren' },
  { id: 'total_payout_amount', numeric: true, label: 'Amount' },
  { id: 'status', numeric: false, label: 'Status', cellClassName: 'bookings-tx-status' },
  { id: 'updates_count', numeric: true, label: 'Updates' },
  { id: 'booked_at', numeric: false, label: 'Booked' },
  { id: 'lead_time', numeric: true, label: 'Lead\nTime' },
  { id: 'actions', numeric: false, disableSorting: true, label: 'Actions', cellClassName: 'bookings-tx-actions' },
];

/** Parse a booking date string to a local calendar date (date-only). */
function parseBookingDateOnly(value?: string | null): Date | null {
  if (!value) return null;
  const s = String(value).trim();
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Days between booked date and check-in (lead time). */
function bookingLeadTimeDays(booking: {
  check_in_date?: string;
  booked_at_display?: string;
  booked_at_fallback?: string;
  booked_at?: string;
}): number | null {
  const checkIn = parseBookingDateOnly(booking.check_in_date);
  const booked = parseBookingDateOnly(
    booking.booked_at_display || booking.booked_at_fallback || booking.booked_at
  );
  if (!checkIn || !booked) return null;
  return Math.round((checkIn.getTime() - booked.getTime()) / 86400000);
}

const LEAD_TIME_BUCKETS: Array<{ key: string; label: string; match: (n: number) => boolean }> = [
  { key: 'neg', label: '< 0', match: (n) => n < 0 },
  { key: '0_7', label: '0–7', match: (n) => n >= 0 && n <= 7 },
  { key: '8_14', label: '8–14', match: (n) => n >= 8 && n <= 14 },
  { key: '15_30', label: '15–30', match: (n) => n >= 15 && n <= 30 },
  { key: '31_60', label: '31–60', match: (n) => n >= 31 && n <= 60 },
  { key: '61_90', label: '61–90', match: (n) => n >= 61 && n <= 90 },
  { key: '91_180', label: '91–180', match: (n) => n >= 91 && n <= 180 },
  { key: '181_365', label: '181–365', match: (n) => n >= 181 && n <= 365 },
  { key: '365_plus', label: '365+', match: (n) => n > 365 },
];

/** Stay length buckets for “lead time vs booking size” charts. */
const STAY_SIZE_BUCKETS: Array<{ key: string; label: string; match: (nights: number) => boolean }> = [
  { key: '1', label: '1 night', match: (n) => n === 1 },
  { key: '2_3', label: '2–3', match: (n) => n >= 2 && n <= 3 },
  { key: '4_6', label: '4–6', match: (n) => n >= 4 && n <= 6 },
  { key: '7_13', label: '7–13', match: (n) => n >= 7 && n <= 13 },
  { key: '14_27', label: '14–27', match: (n) => n >= 14 && n <= 27 },
  { key: '28_plus', label: '28+', match: (n) => n >= 28 },
];

function bookingNightsValue(b: {
  nights?: number | null;
  check_in_date?: string;
  check_out_date?: string;
}): number | null {
  if (b.nights != null && Number.isFinite(Number(b.nights)) && Number(b.nights) > 0) {
    return Number(b.nights);
  }
  const ci = parseBookingDateOnly(b.check_in_date);
  const co = parseBookingDateOnly(b.check_out_date);
  if (!ci || !co) return null;
  const nights = Math.round((co.getTime() - ci.getTime()) / 86400000);
  return nights > 0 ? nights : null;
}

/**
 * Average lead time and lead÷nights ratio by stay-length bucket.
 * Ratio = lead_time_days / nights (how many lead days per night of stay).
 */
function buildLeadTimeByStaySizeChart(
  sourceBookings: Array<{
    check_in_date?: string;
    check_out_date?: string;
    nights?: number | null;
    booked_at_display?: string;
    booked_at_fallback?: string;
    booked_at?: string;
    lead_time?: number | null;
  }>
) {
  const buckets = STAY_SIZE_BUCKETS.map((b) => ({
    key: b.key,
    size: b.label,
    sumLead: 0,
    sumNights: 0,
    count: 0,
  }));
  let skipped = 0;

  for (const b of sourceBookings) {
    const lead = b.lead_time != null ? Number(b.lead_time) : bookingLeadTimeDays(b);
    const nights = bookingNightsValue(b);
    if (lead == null || Number.isNaN(lead) || nights == null) {
      skipped += 1;
      continue;
    }
    const bucket = STAY_SIZE_BUCKETS.find((x) => x.match(nights));
    if (!bucket) {
      skipped += 1;
      continue;
    }
    const row = buckets.find((x) => x.key === bucket.key)!;
    row.sumLead += lead;
    row.sumNights += nights;
    row.count += 1;
  }

  const data = buckets
    .filter((r) => r.count > 0)
    .map((r) => ({
      size: r.size,
      count: r.count,
      avgLeadTime: Math.round((r.sumLead / r.count) * 10) / 10,
      /** Weighted lead days per stay-night. */
      leadPerNight: Math.round((r.sumLead / r.sumNights) * 100) / 100,
    }));

  return {
    data,
    stats: {
      withBoth: data.reduce((s, r) => s + r.count, 0),
      skipped,
    },
  };
}

function buildLeadTimeChartData(
  sourceBookings: Array<{
    check_in_date?: string;
    booked_at_display?: string;
    booked_at_fallback?: string;
    booked_at?: string;
    lead_time?: number | null;
  }>,
  monthMode: 'check_in' | 'booked',
  monthKeyFromDateStr: (dateStr: string) => { monthKey: string; monthLabel: string } | null,
  bookingChartDateStr: (
    b: {
      check_in_date?: string;
      booked_at_display?: string;
      booked_at_fallback?: string;
      booked_at?: string;
    },
    mode: 'check_in' | 'booked'
  ) => string
) {
  const bucketCounts = Object.fromEntries(LEAD_TIME_BUCKETS.map((b) => [b.key, 0])) as Record<string, number>;
  let unknown = 0;
  const values: number[] = [];
  const byMonth = new Map<string, { month: string; monthKey: string; sum: number; count: number }>();

  for (const b of sourceBookings) {
    const days = b.lead_time != null ? b.lead_time : bookingLeadTimeDays(b);
    if (days == null || Number.isNaN(Number(days))) {
      unknown += 1;
      continue;
    }
    const n = Number(days);
    values.push(n);
    const bucket = LEAD_TIME_BUCKETS.find((x) => x.match(n));
    if (bucket) bucketCounts[bucket.key] += 1;

    const monthMeta = monthKeyFromDateStr(bookingChartDateStr(b, monthMode));
    if (monthMeta) {
      if (!byMonth.has(monthMeta.monthKey)) {
        byMonth.set(monthMeta.monthKey, {
          month: monthMeta.monthLabel,
          monthKey: monthMeta.monthKey,
          sum: 0,
          count: 0,
        });
      }
      const row = byMonth.get(monthMeta.monthKey)!;
      row.sum += n;
      row.count += 1;
    }
  }

  values.sort((a, b) => a - b);
  const avg = values.length ? Math.round(values.reduce((s, v) => s + v, 0) / values.length) : null;
  const median = values.length
    ? values.length % 2 === 1
      ? values[(values.length - 1) / 2]
      : Math.round((values[values.length / 2 - 1] + values[values.length / 2]) / 2)
    : null;

  return {
    histogram:
      values.length || unknown
        ? [
            ...LEAD_TIME_BUCKETS.map((b) => ({
              bucket: b.label,
              count: bucketCounts[b.key] || 0,
            })),
            ...(unknown > 0 ? [{ bucket: 'Unknown', count: unknown }] : []),
          ]
        : [],
    byMonth: Array.from(byMonth.values())
      .map((r) => ({
        month: r.month,
        monthKey: r.monthKey,
        avgLeadTime: r.count ? Math.round(r.sum / r.count) : 0,
        count: r.count,
      }))
      .sort((a, b) => a.monthKey.localeCompare(b.monthKey)),
    stats: {
      withLeadTime: values.length,
      unknown,
      avg,
      median,
    },
  };
}


/** Compare last two booking_history entries; return set of field keys that changed */
function getUpdatedFields(history?: Array<{ date_fetched: string; data: Record<string, unknown> }>): Set<string> {
  const changed = new Set<string>();
  if (!history || history.length < 2) return changed;
  const prev = history[history.length - 2]?.data || {};
  const curr = history[history.length - 1]?.data || {};
  const keys = Array.from(new Set([...Object.keys(prev), ...Object.keys(curr)]));
  const normalize = (v: unknown): unknown => {
    if (v == null) return null;
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10); // normalize date strings
    if (typeof v === 'number') return v;
    return v;
  };
  for (const k of keys) {
    // Ignore fallback-only churn; "booked_at" should not mark an update badge.
    if (k === 'booked_at' || k === 'booked_at_fallback') continue;
    const p = normalize(prev[k]);
    const c = normalize(curr[k]);
    if (p !== c) changed.add(k);
  }
  return changed;
}

/** Count semantic updates only (ignores booked_at / fallback churn). */
function getMeaningfulUpdatesCount(history?: Array<{ date_fetched: string; data: Record<string, unknown> }>): number {
  if (!history || history.length < 2) return 0;
  let count = 0;
  for (let i = 1; i < history.length; i += 1) {
    const prev = history[i - 1]?.data || {};
    const curr = history[i]?.data || {};
    const keys = Array.from(new Set([...Object.keys(prev), ...Object.keys(curr)]));
    const normalize = (v: unknown): unknown => {
      if (v == null) return null;
      if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
      return v;
    };
    const changed = keys.some((k) => {
      if (k === 'booked_at' || k === 'booked_at_fallback') return false;
      return normalize(prev[k]) !== normalize(curr[k]);
    });
    if (changed) count += 1;
  }
  return count;
}

type FlightFormFields = {
  airport: string;
  flight_number: string;
  departure_date: string;
  departure_time: string;
  pnr: string;
};

const EMPTY_FLIGHT_FORM: FlightFormFields = {
  airport: '',
  flight_number: '',
  departure_date: '',
  departure_time: '',
  pnr: '',
};

function FlightDetailsFormSection({
  title,
  value,
  onChange,
}: {
  title: string;
  value: FlightFormFields;
  onChange: (next: FlightFormFields) => void;
}) {
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>{title}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Airport"
            value={value.airport}
            onChange={(e) => onChange({ ...value, airport: e.target.value.toUpperCase() })}
            placeholder="BIO"
            sx={{ flex: '1 1 120px' }}
          />
          <TextField
            label="Flight number"
            value={value.flight_number}
            onChange={(e) => onChange({ ...value, flight_number: e.target.value })}
            placeholder="EI 749"
            sx={{ flex: '2 1 160px' }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Departure date"
            type="date"
            value={value.departure_date}
            onChange={(e) => onChange({ ...value, departure_date: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: '1 1 160px' }}
          />
          <TextField
            label="Departure time"
            type="time"
            value={value.departure_time}
            onChange={(e) => onChange({ ...value, departure_time: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: '1 1 140px' }}
          />
          <TextField
            label="PNR"
            value={value.pnr}
            onChange={(e) => onChange({ ...value, pnr: e.target.value.toUpperCase() })}
            placeholder="2N52HN"
            sx={{ flex: '1 1 120px' }}
          />
        </Box>
      </Box>
    </Box>
  );
}

/** Stable row key for linking predictions (checkbox selection). */
function predictionLinkKey(p: { uid?: string; ical_booking_id?: number; manual_booking_id?: number }) {
  return p.uid || `${p.ical_booking_id}-${p.manual_booking_id}`;
}

/**
 * Stale cancelled iCal mega-ranges (e.g. ids 9/64/67/71: ~190–203 nights) are feed junk,
 * not real long stays — show nights as "unknown" instead of the inflated span.
 */
function isSuspiciousLongIcalStay(booking: {
  source?: string | null;
  status?: string | null;
  nights?: number | null;
  check_in_date?: string;
  check_out_date?: string;
}): boolean {
  if (String(booking.source || '').trim() !== 'iCal') return false;
  if (!isCancelledBookingStatus(booking.status)) return false;
  let nights = typeof booking.nights === 'number' && !Number.isNaN(booking.nights) ? booking.nights : null;
  if (nights == null) {
    const cin = parseBookingDateOnly(booking.check_in_date);
    const cout = parseBookingDateOnly(booking.check_out_date);
    if (cin && cout) {
      nights = Math.round((cout.getTime() - cin.getTime()) / 86400000);
    }
  }
  return nights != null && nights >= 60;
}

function displayBookingNights(booking: {
  source?: string | null;
  status?: string | null;
  nights?: number | null;
  check_in_date?: string;
  check_out_date?: string;
}): string | number {
  if (isSuspiciousLongIcalStay(booking)) return 'unknown';
  if (booking.nights == null || Number.isNaN(Number(booking.nights))) return '—';
  return booking.nights;
}

function formatBookingTableDate(dateString: string) {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatBookingTableDateTime(dateString: string) {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatBookingTableAmount(amount: number | null | undefined, currency: string = 'EUR') {
  if (amount == null) return 'No amount';
  const sym: Record<string, string> = { EUR: '€', GBP: '£', USD: '$' };
  const s = sym[currency] ?? `${currency} `;
  return `${s}${Number(amount).toLocaleString('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** YYYY-MM-DD for API query params */
function formatYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Default window for unified calendar fetch: avoids loading entire history (~multi-MB JSON on cloud).
 * Backend uses overlap: stay appears if it intersects [start_date, end_date].
 * Lookback is long enough to cover Niall / early STL host history (~2020), not only the last 2 years.
 */
function getDefaultUnifiedCalendarRange(): { start_date: string; end_date: string } {
  const now = new Date();
  const start = new Date(now.getFullYear() - 8, now.getMonth(), now.getDate());
  const end = new Date(now.getFullYear() + 3, now.getMonth(), now.getDate());
  return { start_date: formatYmd(start), end_date: formatYmd(end) };
}

type PredictionBookingLike = {
  property_id?: number;
  check_in_date?: string;
  check_out_date?: string;
  guest_name?: string | null;
  nights?: number | null;
};

function normalizeYmd(value: unknown): string {
  if (value == null) return '';
  const s = String(value);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : s;
}

/** Match dimensions aligned with backend booking-link scoring (for Yes/No columns). */
function computePredictionReasonFlags(ical: PredictionBookingLike, manual: PredictionBookingLike) {
  const sameProperty =
    ical.property_id != null && manual.property_id != null && ical.property_id === manual.property_id;
  const ci = normalizeYmd(ical.check_in_date);
  const cm = normalizeYmd(manual.check_in_date);
  const sameCheckIn = ci !== '' && ci === cm;
  const oi = normalizeYmd(ical.check_out_date);
  const om = normalizeYmd(manual.check_out_date);
  const sameCheckOut = oi !== '' && oi === om;
  const gi = String(ical.guest_name ?? '').trim().toLowerCase();
  const gm = String(manual.guest_name ?? '').trim().toLowerCase();
  const sameGuestName = gi !== '' && gm !== '' && gi === gm;
  const sameNights =
    ical.nights != null &&
    manual.nights != null &&
    Number(ical.nights) === Number(manual.nights);
  return { sameProperty, sameCheckIn, sameCheckOut, sameGuestName, sameNights };
}

type PredictionReasonFlagKey = keyof ReturnType<typeof computePredictionReasonFlags>;

/** All booking-link scorer dimensions; labels match backend `calendar.py` match_reasons strings. */
const BOOKING_LINK_INSPECT_CRITERIA: readonly { key: PredictionReasonFlagKey; label: string }[] = [
  { key: 'sameProperty', label: 'Same property' },
  { key: 'sameCheckIn', label: 'Same check-in date' },
  { key: 'sameCheckOut', label: 'Same check-out date' },
  { key: 'sameGuestName', label: 'Same guest name' },
  { key: 'sameNights', label: 'Same number of nights' },
];

/** "Must be Yes" filters for the Predicted Matches table (aligns with ReasonYesNo columns). */
type PredictionMatchFilterKey =
  | 'sameProperty'
  | 'sameCheckIn'
  | 'sameCheckOut'
  | 'sameGuest'
  | 'sameNights';

function predictionPassesYesFilters(
  prediction: any,
  required: readonly PredictionMatchFilterKey[]
): boolean {
  if (required.length === 0) return true;
  const flags = computePredictionReasonFlags(
    prediction?.ical_booking ?? {},
    prediction?.manual_booking ?? {}
  );
  for (const key of required) {
    switch (key) {
      case 'sameProperty':
        if (!flags.sameProperty) return false;
        break;
      case 'sameCheckIn':
        if (!flags.sameCheckIn) return false;
        break;
      case 'sameCheckOut':
        if (!flags.sameCheckOut) return false;
        break;
      case 'sameGuest':
        if (!flags.sameGuestName) return false;
        break;
      case 'sameNights':
        if (!flags.sameNights) return false;
        break;
      default:
        break;
    }
  }
  return true;
}

function ReasonYesNo({ ok }: { ok: boolean }) {
  return (
    <Chip
      label={ok ? 'Yes' : 'No'}
      size="small"
      color={ok ? 'success' : 'error'}
      sx={{ fontWeight: 600, minWidth: 48, '& .MuiChip-label': { px: 1 } }}
    />
  );
}

/** Sortable columns for Predicted Matches (linking tab). */
type PredictionSortKey =
  | 'confidence'
  | 'sameProperty'
  | 'sameCheckIn'
  | 'sameCheckOut'
  | 'sameGuest'
  | 'sameNights'
  | 'icalCheckIn'
  | 'manualCheckIn';

function getPredictionSortValue(prediction: any, key: PredictionSortKey): number | string {
  const ical = prediction?.ical_booking ?? {};
  const manual = prediction?.manual_booking ?? {};
  const flags = computePredictionReasonFlags(ical, manual);
  switch (key) {
    case 'confidence':
      return Number(prediction?.confidence_score ?? 0);
    case 'sameProperty':
      return flags.sameProperty ? 1 : 0;
    case 'sameCheckIn':
      return flags.sameCheckIn ? 1 : 0;
    case 'sameCheckOut':
      return flags.sameCheckOut ? 1 : 0;
    case 'sameGuest':
      return flags.sameGuestName ? 1 : 0;
    case 'sameNights':
      return flags.sameNights ? 1 : 0;
    case 'icalCheckIn':
      return normalizeYmd(ical.check_in_date) || '';
    case 'manualCheckIn':
      return normalizeYmd(manual.check_in_date) || '';
    default:
      return 0;
  }
}

const BOOKING_COMPARE_FIELD_ORDER = [
  'id',
  'property_id',
  'property_nickname',
  'check_in_date',
  'check_out_date',
  'platform',
  'source',
  'external_booking_id',
  'guest_name',
  'guest_email',
  'guest_phone_last_4',
  'reservation_url',
  'nights',
  'adults_count',
  'children_count',
  'total_payout_amount',
  'host_payout_amount',
  'currency',
  'status',
  'booking_type',
  'summary',
  'description',
  'notes',
  'last_sync',
  'booked_at',
  'ical_uid',
  'booking_history',
  'created_at',
  'updated_at',
];

function formatInspectValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v, null, 2);
  return String(v);
}

function compareRowsDiffer(icalFormatted: string, manualFormatted: string): boolean {
  return icalFormatted.trim() !== manualFormatted.trim();
}

function buildBookingCompareRows(
  ical: Record<string, unknown> | undefined,
  manual: Record<string, unknown> | undefined
): { key: string; ical: string; manual: string }[] {
  const a = ical || {};
  const b = manual || {};
  const keySet = new Set([...Object.keys(a), ...Object.keys(b)]);
  const ordered: string[] = [];
  for (const k of BOOKING_COMPARE_FIELD_ORDER) {
    if (keySet.has(k)) {
      ordered.push(k);
      keySet.delete(k);
    }
  }
  ordered.push(...Array.from(keySet).sort((x, y) => x.localeCompare(y)));
  return ordered.map((key) => ({
    key,
    ical: formatInspectValue(a[key]),
    manual: formatInspectValue(b[key]),
  }));
}

/** Stable sort (ties keep original index). Used by bookings table and prediction sort. */
function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number): T[] {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const ord = comparator(a[0], b[0]);
    if (ord !== 0) return ord;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function EnhancedTableHead(props: {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Booking | 'actions') => void;
  order: Order;
  orderBy: string;
  numSelected: number;
  rowCount: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  const { order, orderBy, onRequestSort, numSelected, rowCount, onSelectAllClick } = props;
  const createSortHandler = (property: keyof Booking | 'actions') => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1,
        backgroundColor: 'background.paper',
        boxShadow: '0 1px 0 0 rgba(224, 224, 224, 1)',
      }}
    >
      <TableRow>
        <TableCell
          padding="checkbox"
          align="center"
          className="bookings-tx-select"
          sx={{ backgroundColor: 'background.paper' }}
        >
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{ 'aria-label': 'select all bookings on this page' }}
            size="small"
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align="center"
            padding="none"
            className={headCell.cellClassName}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{ backgroundColor: 'background.paper' }}
          >
            {headCell.disableSorting ? (
              bookingsHeaderLabel(headCell.label)
            ) : (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {bookingsHeaderLabel(headCell.label)}
              </TableSortLabel>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const BookingsDataTable = memo(function BookingsDataTable(props: {
  ariaLabel: string;
  order: Order;
  orderBy: string;
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Booking | 'actions') => void;
  bookings: Booking[];
  ghostCategoryById?: Map<number, StaleIcalGhostCategory>;
  nestingRemnantById?: Map<number, true>;
  selectedIds: Set<number>;
  onToggleSelect: (bookingId: number) => void;
  onSelectAllVisible: (checked: boolean) => void;
  loadingEmpty: boolean;
  emptyTitle: string;
  emptyHint: string;
  formatDate: (dateString: string) => string;
  formatAmount: (amount: number | null | undefined, currency?: string) => string;
  onOpenComms?: (booking: Booking) => void;
}) {
  const {
    ariaLabel,
    order,
    orderBy,
    onRequestSort,
    bookings,
    ghostCategoryById,
    nestingRemnantById,
    selectedIds,
    onToggleSelect,
    onSelectAllVisible,
    loadingEmpty,
    emptyTitle,
    emptyHint,
    formatDate,
    formatAmount,
    onOpenComms,
  } = props;
  const cols = bookingsColgroup();
  const numSelected = bookings.reduce((n, b) => n + (selectedIds.has(b.id) ? 1 : 0), 0);

  const amountTotalsByCurrency = (() => {
    const byCcy = new Map<string, number>();
    for (const b of bookings) {
      const raw = b.total_payout_amount ?? b.host_payout_amount;
      if (raw == null || !Number.isFinite(Number(raw))) continue;
      const ccy = String(b.currency || 'EUR').trim().toUpperCase() || 'EUR';
      byCcy.set(ccy, (byCcy.get(ccy) || 0) + Number(raw));
    }
    return Array.from(byCcy.entries())
      .map(([currency, total]) => ({ currency, total }))
      .sort((a, b) => a.currency.localeCompare(b.currency));
  })();
  const nightsTotal = bookings.reduce((sum, b) => {
    const n = displayBookingNights(b);
    return sum + (typeof n === 'number' && Number.isFinite(n) ? n : 0);
  }, 0);
  // select + ID … Check-out (before Nights)
  const footerLabelColSpan = 1 + headCells.findIndex((c) => c.id === 'nights');
  // Status … Actions (after Amount)
  const footerTailColSpan = headCells.length - (headCells.findIndex((c) => c.id === 'total_payout_amount') + 1);

  return (
    <TableContainer
      component={Paper}
      sx={{
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        // Grow with content so scrolling is the page (Layout main), not an inner panel.
        overflowY: 'visible',
      }}
    >
      <Table size="small" stickyHeader aria-label={ariaLabel} sx={BOOKINGS_TABLE_SX}>
        <colgroup>
          {cols.map((col) => (
            <col key={col.key} style={col.width ? { width: col.width } : undefined} />
          ))}
        </colgroup>
        <EnhancedTableHead
          order={order}
          orderBy={orderBy}
          onRequestSort={onRequestSort}
          numSelected={numSelected}
          rowCount={bookings.length}
          onSelectAllClick={(e) => onSelectAllVisible(e.target.checked)}
        />
        <TableBody>
          {bookings.map((booking) => {
            const guestLabel = booking.guest_name || 'No guest name';
            const syncedLabel = formatDate(booking.last_sync || booking.created_at || '');
            const bookedLabel = formatDate(
              booking.booked_at_display || booking.booked_at_fallback || booking.booked_at || ''
            );
            const amountLabel = formatAmount(booking.total_payout_amount, booking.currency);
            const ghostCat = ghostCategoryById?.get(booking.id);
            const isNestingRemnant = Boolean(nestingRemnantById?.has(booking.id));
            const ghostRowSx = ghostCat ? STALE_ICAL_GHOST_ROW_SX[ghostCat] : null;
            const remnantRowSx = !ghostCat && isNestingRemnant ? ICAL_NESTING_REMNANT_ROW_SX : null;
            const isSelected = selectedIds.has(booking.id);
            return (
              <TableRow
                key={booking.id}
                selected={isSelected}
                hover
                title={
                  ghostCat === 'A'
                    ? 'Stale iCal ghost A — duplicate of a real iCal on the same dates'
                    : ghostCat === 'B'
                      ? 'Stale iCal ghost B — Export/Manual twin only (no modern iCal)'
                      : ghostCat === 'C'
                        ? 'Stale iCal ghost C — orphan (no same-date twin)'
                        : isNestingRemnant
                          ? 'iCal nesting remnant (category N) — shrinking calendar UID with no Export twin'
                          : undefined
                }
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  ...(ghostRowSx || remnantRowSx || {}),
                }}
              >
                <TableCell padding="checkbox" align="center" className="bookings-tx-select">
                  <Checkbox
                    color="primary"
                    size="small"
                    checked={isSelected}
                    onChange={() => onToggleSelect(booking.id)}
                    inputProps={{ 'aria-label': `select booking ${booking.id}` }}
                  />
                </TableCell>
                <TableCell align="center">
                  {booking.is_merged_pair &&
                  booking.linked_ical_booking_id != null &&
                  booking.linked_manual_booking_id != null ? (
                    <Typography
                      component="a"
                      href={`/bookings/${booking.linked_manual_booking_id}?tab=metadata`}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="caption"
                      title={mergedBookingIdTitle(booking)}
                      sx={{
                        color: 'primary.main',
                        textDecoration: 'underline',
                        fontSize: 'inherit',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatBookingIdDisplay(booking)}
                    </Typography>
                  ) : (
                    <Box
                      component="span"
                      sx={bookingsTruncateSx}
                      title={
                        ghostCat
                          ? `${formatBookingIdDisplay(booking)} · ghost ${ghostCat}`
                          : formatBookingIdDisplay(booking)
                      }
                    >
                      {formatBookingIdDisplay(booking)}
                    </Box>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Box component="span" sx={bookingsTruncateSx} title={syncedLabel}>
                    {syncedLabel}
                  </Box>
                </TableCell>
                <TableCell component="th" scope="row" align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center', maxWidth: '100%' }}>
                    <PropertyNicknameChip
                      nickname={booking.property_nickname}
                      propertyId={booking.property_id}
                    />
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={booking.platform}
                    size="small"
                    color={booking.platform === 'Airbnb' ? 'primary' : 'secondary'}
                    sx={bookingsChipSx}
                    title={booking.platform}
                  />
                </TableCell>
                <TableCell align="center">
                  {(() => {
                    const ref = (booking.booking_ref || '').trim();
                    if (!ref) {
                      return (
                        <Box component="span" sx={bookingsTruncateSx} title="—">
                          —
                        </Box>
                      );
                    }
                    if (booking.reservation_url) {
                      return (
                        <Typography
                          variant="body2"
                          component="a"
                          href={booking.reservation_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={ref}
                          sx={{
                            ...bookingsTruncateSx,
                            color: 'primary.main',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            fontSize: 'inherit',
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                            '&:hover': { color: 'primary.dark' },
                          }}
                        >
                          {ref}
                        </Typography>
                      );
                    }
                    return (
                      <Box
                        component="span"
                        sx={{
                          ...bookingsTruncateSx,
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        }}
                        title={ref}
                      >
                        {ref}
                      </Box>
                    );
                  })()}
                </TableCell>
                <TableCell align="center">
                  {booking.is_merged_pair || booking.source === 'Merged' ? (
                    <Chip
                      label="Merged"
                      size="small"
                      color="success"
                      sx={bookingsChipSx}
                      title={
                        booking.linked_ical_booking_id != null && booking.linked_manual_booking_id != null
                          ? `iCal #${booking.linked_ical_booking_id} + Manual/Export #${booking.linked_manual_booking_id}`
                          : 'Linked iCal + Manual/Export'
                      }
                    />
                  ) : (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <Chip
                        label={booking.source}
                        size="small"
                        color={
                          booking.source === 'iCal'
                            ? 'info'
                            : isExportSource(booking.source)
                              ? 'warning'
                              : 'default'
                        }
                        sx={bookingsChipSx}
                        title={booking.source}
                      />
                      {isNestingRemnant ? (
                        <Chip
                          label="N"
                          size="small"
                          color="default"
                          sx={{ ...bookingsChipSx, fontWeight: 700 }}
                          title={ICAL_NESTING_REMNANT_LEGEND.label}
                        />
                      ) : null}
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  {booking.reservation_url ? (
                    <Typography
                      variant="body2"
                      component="a"
                      href={booking.reservation_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={guestLabel}
                      sx={{
                        ...bookingsTruncateSx,
                        color: 'primary.main',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        fontSize: 'inherit',
                        '&:hover': { color: 'primary.dark' },
                      }}
                    >
                      {guestLabel}
                    </Typography>
                  ) : (
                    <Box component="span" sx={bookingsTruncateSx} title={guestLabel}>
                      {guestLabel}
                    </Box>
                  )}
                  {booking.guest_email ? (
                    <Box
                      component="span"
                      sx={{ ...bookingsTruncateSx, color: 'text.secondary', fontSize: '0.7rem' }}
                      title={booking.guest_email}
                    >
                      {booking.guest_email}
                    </Box>
                  ) : null}
                </TableCell>
                <TableCell align="center">
                  <Box component="span" sx={bookingsTruncateSx} title={formatDate(booking.check_in_date)}>
                    {formatDate(booking.check_in_date)}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box component="span" sx={bookingsTruncateSx} title={formatDate(booking.check_out_date)}>
                    {formatDate(booking.check_out_date)}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  {(() => {
                    const nightsLabel = displayBookingNights(booking);
                    const title =
                      nightsLabel === 'unknown'
                        ? 'Suspicious cancelled iCal date range — nights not trusted'
                        : String(nightsLabel);
                    return (
                      <Box component="span" sx={bookingsTruncateSx} title={title}>
                        {nightsLabel}
                      </Box>
                    );
                  })()}
                </TableCell>
                <TableCell align="center">{booking.adults_count}</TableCell>
                <TableCell align="center">{booking.children_count}</TableCell>
                <TableCell align="center">
                  <Box component="span" sx={{ ...bookingsTruncateSx, fontWeight: 600 }} title={amountLabel}>
                    {amountLabel}
                  </Box>
                </TableCell>
                <TableCell
                  className="bookings-tx-status"
                  align="center"
                  sx={{ overflow: 'visible', whiteSpace: 'nowrap' }}
                >
                  <Chip
                    label={booking.status}
                    size="small"
                    color={
                      booking.status === 'confirmed'
                        ? 'success'
                        : isCancelledBookingStatus(booking.status)
                          ? 'error'
                          : 'warning'
                    }
                    sx={bookingsStatusChipSx}
                    title={booking.status}
                  />
                </TableCell>
                <TableCell align="center">{booking.updates_count ?? 0}</TableCell>
                <TableCell align="center">
                  <Box component="span" sx={bookingsTruncateSx} title={bookedLabel}>
                    {bookedLabel}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  {booking.lead_time == null ? (
                    <Box component="span" sx={bookingsTruncateSx} title="—">
                      —
                    </Box>
                  ) : (
                    <Box
                      component="span"
                      sx={bookingsTruncateSx}
                      title={`${booking.lead_time} day${booking.lead_time === 1 || booking.lead_time === -1 ? '' : 's'} before check-in`}
                    >
                      {booking.lead_time}
                    </Box>
                  )}
                </TableCell>
                <TableCell className="bookings-tx-actions" align="center">
                  <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'center' }}>
                    <Tooltip title={booking.is_merged_pair ? 'View linked pair (Metadata)' : 'View Details'}>
                      <IconButton
                        size="small"
                        href={
                          booking.is_merged_pair
                            ? `/bookings/${booking.id}?tab=metadata`
                            : `/bookings/${booking.id}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        color="info"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {onOpenComms ? (
                      <Tooltip title="Comms">
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => onOpenComms(booking)}
                          aria-label="Open booking Comms"
                        >
                          <ChatIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    {(() => {
                      const platformLink = resolveBookingPlatformLink(booking);
                      if (!platformLink) return null;
                      return (
                        <Tooltip title={`Open on ${platformLink.platformLabel}`}>
                          <IconButton
                            size="small"
                            href={platformLink.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            color="primary"
                            aria-label={`Open booking on ${platformLink.platformLabel}`}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      );
                    })()}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
          {bookings.length === 0 && !loadingEmpty && (
            <TableRow>
              <TableCell colSpan={headCells.length + 1} sx={{ textAlign: 'center', py: 4, maxWidth: 'none' }}>
                <CalendarIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {emptyTitle}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {emptyHint}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {bookings.length === 0 && loadingEmpty && (
            <TableRow>
              <TableCell colSpan={headCells.length + 1} sx={{ textAlign: 'center', py: 6, maxWidth: 'none' }}>
                <CircularProgress size={28} />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        {bookings.length > 0 ? (
          <TableFooter>
            <TableRow
              sx={{
                bgcolor: 'action.hover',
                '& td': {
                  fontWeight: 700,
                  borderTop: 2,
                  borderColor: 'divider',
                  py: 1.25,
                },
              }}
            >
              <TableCell colSpan={footerLabelColSpan} align="left" sx={{ pl: 2 }}>
                Total · {bookings.length} stay{bookings.length === 1 ? '' : 's'}
              </TableCell>
              <TableCell align="center">{nightsTotal}</TableCell>
              <TableCell align="center" />
              <TableCell align="center" />
              <TableCell align="center">
                {amountTotalsByCurrency.length === 0 ? (
                  <Box component="span" sx={bookingsTruncateSx} title="—">
                    —
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.25,
                    }}
                  >
                    {amountTotalsByCurrency.map(({ currency, total }) => {
                      const label = formatAmount(total, currency);
                      return (
                        <Box
                          key={currency}
                          component="span"
                          sx={{ ...bookingsTruncateSx, fontWeight: 700 }}
                          title={label}
                        >
                          {label}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </TableCell>
              <TableCell colSpan={footerTailColSpan} />
            </TableRow>
          </TableFooter>
        ) : null}
      </Table>
    </TableContainer>
  );
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`bookings-tabpanel-${index}`}
      aria-labelledby={`bookings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `bookings-tab-${index}`,
    'aria-controls': `bookings-tabpanel-${index}`,
  };
}

const PAYMENTS_DETAIL_LONG_KEYS = new Set([
  'booking_history',
  'description',
  'raw_data',
  'merchant_data',
  'card_data',
  'counterparty_data',
  'legs_data',
  'notes',
  'summary',
]);

function formatPaymentsDetailValue(v: unknown): { text: string; isLong: boolean } {
  if (v === null || v === undefined || v === '') return { text: '—', isLong: false };
  if (typeof v === 'boolean') return { text: v ? 'Yes' : 'No', isLong: false };
  if (typeof v === 'number') return { text: String(v), isLong: false };
  if (Array.isArray(v) || (typeof v === 'object' && v !== null)) {
    try {
      const text = JSON.stringify(v, null, 2);
      return { text, isLong: text.length > 120 };
    } catch {
      return { text: String(v), isLong: true };
    }
  }
  const s = String(v);
  return { text: s, isLong: s.length > 160 };
}

function PaymentsDetailFieldGrid({ data }: { data: Record<string, unknown> | null | undefined }) {
  if (!data || typeof data !== 'object') {
    return (
      <Typography variant="body2" color="text.secondary">
        None
      </Typography>
    );
  }
  const entries = Object.entries(data)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => a.localeCompare(b));
  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No fields
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'minmax(140px, 180px) 1fr' },
        gap: 1,
        alignItems: 'start',
      }}
    >
      {entries.map(([k, v]) => {
        const { text, isLong } = formatPaymentsDetailValue(v);
        const long = isLong || PAYMENTS_DETAIL_LONG_KEYS.has(k);
        return (
          <React.Fragment key={k}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, pr: 1, pt: long ? 0.5 : 0 }}>
              {k}
            </Typography>
            {long ? (
              <Typography
                component="pre"
                variant="caption"
                sx={{
                  m: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 240,
                  overflow: 'auto',
                  bgcolor: 'action.hover',
                  p: 1,
                  borderRadius: 1,
                }}
              >
                {text}
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {text}
              </Typography>
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}

const CalendarViewTable: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Tab names mapping (Bookings list is first / default; legacy combined/ical/manual → Bookings)
  const tabNameToIndex: { [key: string]: number } = {
    bookings: 0,
    combined: 0,
    ical: 0,
    manual: 0,
    linking: 1,
    payments: 2,
    comms: 3,
  };
  const tabIndexToName = ['bookings', 'linking', 'payments', 'comms'] as const;
  const BOOKINGS_LIST_TAB_ALIASES = new Set(['bookings', 'combined', 'ical', 'manual']);
  
  // Determine initial tab from URL param
  let initialTab = 0;
  const tabParam = searchParams.get('tab');
  if (tabParam && tabNameToIndex.hasOwnProperty(tabParam.toLowerCase())) {
    initialTab = tabNameToIndex[tabParam.toLowerCase()];
  }
  const parseBookingsViewParam = (raw: string | null): 'table' | 'calendar' | null => {
    if (!raw) return null;
    const v = raw.trim().toLowerCase();
    if (v === 'table') return 'table';
    if (v === 'calendar' || v === 'graph') return 'calendar';
    return null;
  };
  const initialViewMode = parseBookingsViewParam(searchParams.get('view')) || 'calendar';
  const focusBookingIdParam = (searchParams.get('focus_booking_id') || '').trim();
  const focusExternalBookingId = (searchParams.get('focus_external_booking_id') || '').trim();
  const focusIcalUid = (searchParams.get('focus_ical_uid') || '').trim();
  const parsedFocusBookingId = Number.parseInt(focusBookingIdParam, 10);
  const focusBookingId = Number.isFinite(parsedFocusBookingId) ? parsedFocusBookingId : null;
  
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncLoading, setSyncLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);
  const [selectedBookingIds, setSelectedBookingIds] = useState<Set<number>>(() => new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  /** Linking tab: full prediction object for side-by-side JSON field inspect modal */
  const [inspectPrediction, setInspectPrediction] = useState<any | null>(null);
  const [sourceFilters, setSourceFilters] = useState<string[]>([]);
  const [platformFilters, setPlatformFilters] = useState<string[]>([]);
  const [accountOwnerFilters, setAccountOwnerFilters] = useState<string[]>([]);
  const [propertyFilters, setPropertyFilters] = useState<string[]>([]);
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  /** When true, exclude cancelled / "cancelled by guest" (etc.) rows. Default off = show them. */
  const [hideCancelled, setHideCancelled] = useState(false);
  /** Table-only: hide iCal nesting remnants (category N). Calendar always hides them. Default off. */
  const [hideNestingRemnants, setHideNestingRemnants] = useState(false);
  const [idFilter, setIdFilter] = useState('');
  const [bookingRefFilter, setBookingRefFilter] = useState('');
  const [guestFilter, setGuestFilter] = useState('');
  const [nightsMin, setNightsMin] = useState('');
  const [nightsMax, setNightsMax] = useState('');
  const [leadTimeMin, setLeadTimeMin] = useState('');
  const [leadTimeMax, setLeadTimeMax] = useState('');
  const [checkOutFrom, setCheckOutFrom] = useState('');
  const [checkOutTo, setCheckOutTo] = useState('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [selectedMonthFilter, setSelectedMonthFilter] = useState<{ monthKey: string; monthLabel: string } | null>(null);
  /** Chart X-axis: check-in month (stay) vs month the booking was made. */
  const [chartMonthMode, setChartMonthMode] = useState<'check_in' | 'booked'>('check_in');
  /** Chart Y-axis metric: count, amount, occupancy %, or lead time by stay size. */
  const [chartValueMode, setChartValueMode] = useState<
    'count' | 'amount' | 'occupancy' | 'lead_by_size'
  >('count');
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof Booking>('check_in_date');
  const [showFilters, setShowFilters] = useState(false);
  const [showChart, setShowChart] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [exportingBookings, setExportingBookings] = useState(false);
  /** Bookings list tab: table grid vs property stay-bar calendar. Synced to ?view=. */
  const [bookingsViewMode, setBookingsViewMode] = useState<'table' | 'calendar'>(initialViewMode);
  const [tabValue, setTabValue] = useState(initialTab);
  /** Bookings tab: which sources to include (at least one; both by default). */
  const [combinedSources, setCombinedSources] = useState<Array<'iCal' | 'Manual'>>(['iCal', 'Manual']);
  const [toast, setToast] = useState<AppToast | null>(null);
  const [commsModalBooking, setCommsModalBooking] = useState<Booking | null>(null);
  
  // Linking tab state
  const [predictions, setPredictions] = useState<any[]>([]);
  const [confirmedLinks, setConfirmedLinks] = useState<any[]>([]);
  const [rejectedLinks, setRejectedLinks] = useState<any[]>([]);
  /** Payments tab: manual bookings ↔ Revolut (optional iCal pair from Linking) */
  const [revolutWorkspace, setRevolutWorkspace] = useState<{
    payout_lag_days: number;
    payment_window_days_after_expectation: number;
    payment_window_days_vrbo_ireland_after_expectation?: number;
    uk_property_nickname: string;
    payments_matching_preset?: string;
    scope_applied?: {
      min_check_in?: string | null;
      max_check_in?: string | null;
      property_id?: number | null;
      max_bookings?: number | null;
      manual_bookings_fetched?: number;
      workspace_rows?: number;
    };
    uk_ledger_alternate_note?: string;
    vrbo_ireland_counterparty_note?: string;
    rows: any[];
  } | null>(null);
  const [revolutWorkspaceLoading, setRevolutWorkspaceLoading] = useState(false);
  const [revolutWorkspaceError, setRevolutWorkspaceError] = useState('');
  const paymentsWsFormInit = useMemo(() => loadPaymentsWorkspaceScopeForm(), []);
  const [paymentsWsMinCheckIn, setPaymentsWsMinCheckIn] = useState(paymentsWsFormInit.min);
  const [paymentsWsMaxCheckIn, setPaymentsWsMaxCheckIn] = useState(paymentsWsFormInit.max);
  const [paymentsWsPropertyId, setPaymentsWsPropertyId] = useState(paymentsWsFormInit.propertyId);
  const [paymentsWsMaxBookings, setPaymentsWsMaxBookings] = useState(paymentsWsFormInit.maxBookings);
  const [paymentsDetailOpen, setPaymentsDetailOpen] = useState(false);
  const [paymentsDetailRow, setPaymentsDetailRow] = useState<any | null>(null);
  /** manual unified_booking id while saving Revolut link */
  const [revolutSavingBookingId, setRevolutSavingBookingId] = useState<number | null>(null);
  /** manual booking id → selected revolut_transactions.id ('' = none) */
  const [revolutPendingTxId, setRevolutPendingTxId] = useState<Record<number, number | ''>>({});
  const [loadingPredictions, setLoadingPredictions] = useState(false);
  const [linkingError, setLinkingError] = useState<string>('');
  const [importingFiles, setImportingFiles] = useState(false);
  /** Shown on full-page loader while fetching unified calendar */
  const [calendarLoadAttemptLine, setCalendarLoadAttemptLine] = useState('');
  /** Structured status from GET /api/health during load (restart, CORS confusion, etc.) */
  const [backendReachabilityBanner, setBackendReachabilityBanner] = useState<BackendReachabilityBanner | null>(
    null
  );
  const [predictionsAttemptLine, setPredictionsAttemptLine] = useState('');
  const [selectedPredictionUids, setSelectedPredictionUids] = useState<Set<string>>(new Set());
  const [bulkLinking, setBulkLinking] = useState(false);
  const [predictionSortOrder, setPredictionSortOrder] = useState<Order>('desc');
  const [predictionSortBy, setPredictionSortBy] = useState<PredictionSortKey>('confidence');
  const [predictionScopeMin, setPredictionScopeMin] = useState(() => getInitialPredictionScope().min);
  const [predictionScopeMax, setPredictionScopeMax] = useState(() => getInitialPredictionScope().max);
  const [predictionScopePropertyId, setPredictionScopePropertyId] = useState(
    () => getInitialPredictionScope().propertyId
  );
  const [predictionScopeApplied, setPredictionScopeApplied] = useState<{
    min_check_in: string;
    max_check_in: string | null;
    property_id: number | null;
  } | null>(null);
  /** True after a successful predictions API call (even when result is empty). */
  const [predictionsQueryCompleted, setPredictionsQueryCompleted] = useState(false);
  /** Predicted Matches: row must have Yes on every selected criterion (empty = no filter). */
  const [predictionYesRequiredKeys, setPredictionYesRequiredKeys] = useState<PredictionMatchFilterKey[]>([]);

  const [formData, setFormData] = useState({
    property_id: '',
    platform: 'Airbnb',
    source: 'Manual',
    external_booking_id: '',
    guest_name: '',
    guest_email: '',
    guest_phone_last_4: '',
    reservation_url: '',
    check_in_date: '',
    check_out_date: '',
    nights: 0,
    adults_count: 1,
    children_count: 0,
    total_payout_amount: '',
    host_payout_amount: '',
    currency: 'EUR',
    status: 'confirmed',
    booking_type: 'reservation',
    summary: '',
    description: '',
    notes: '',
    booked_at: ''
  });

  const [flightInForm, setFlightInForm] = useState<FlightFormFields>(EMPTY_FLIGHT_FORM);
  const [flightOutForm, setFlightOutForm] = useState<FlightFormFields>(EMPTY_FLIGHT_FORM);

  const getApiHealthUrl = (): string => {
    const base = (api.defaults.baseURL || process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    return `${base}/api/health`;
  };

  const checkApiHealth = async () => {
    setBackendReachabilityBanner({
      severity: 'info',
      title: 'Checking API health…',
      body: 'Quick request to /api/health. If this is slow, the full calendar load may still succeed with retries.',
    });
    try {
      const r = await api.get('/api/health', { timeout: 15000 });
      const st = r.data?.status ?? 'unknown';
      setBackendReachabilityBanner({
        severity: 'success',
        title: `API is up (health status: ${String(st)})`,
        body:
          'Loading your data next. A large calendar can still take a while—this message will clear when the request finishes.',
      });
    } catch (e: any) {
      setBackendReachabilityBanner(classifyHealthCheckFailure(e));
    }
  };

  type RetryOptions = {
    onAttempt?: (attempt: number, total: number) => void;
    afterFailure?: () => void | Promise<void>;
  };

  const retryOnUnavailable = async <T,>(
    fn: () => Promise<T>,
    maxRetries = 5,
    options?: RetryOptions
  ): Promise<T> => {
    // Delays for transient API unavailability: total ~2 min wait
    const delays = [0, 10000, 25000, 40000, 55000]; // 0, 10s, 25s, 40s, 55s
    const total = maxRetries + 1;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        if (attempt > 0) {
          await new Promise((r) => setTimeout(r, delays[Math.min(attempt, delays.length - 1)]));
        }
        options?.onAttempt?.(attempt + 1, total);
        return await fn();
      } catch (err: any) {
        const retryable = isRetryableBackendError(err);
        if (!retryable || attempt === maxRetries) throw err;
        await options?.afterFailure?.();
        console.log(`[BOOKINGS] Backend unavailable (attempt ${attempt + 1}/${total}), retrying...`);
      }
    }
    throw new Error('Max retries exceeded');
  };

  const fetchData = async () => {
    try {
      console.log('[BOOKINGS] Fetching bookings data...');
      setLoading(true);
      setError('');
      setCalendarLoadAttemptLine('');
      setBackendReachabilityBanner(null);
      await checkApiHealth();

      const defaults = getDefaultUnifiedCalendarRange();
      // Check-in from/to also expand/narrow the API window (Clear all resets to the default lookback).
      const range = {
        start_date: dateFrom || defaults.start_date,
        end_date: dateTo || defaults.end_date,
      };
      const response = await retryOnUnavailable(
        () =>
          api.get('/api/calendar/unified/calendar', {
            params: range,
            timeout: 95000,
          }),
        5,
        {
          onAttempt: (a, t) =>
            setCalendarLoadAttemptLine(
              `Loading bookings… attempt ${a} of ${t}. If the API is restarting, waiting a moment is normal.`
            ),
          afterFailure: checkApiHealth,
        }
      );
      const bookings = response.data.bookings || [];
      console.log(`[BOOKINGS] Fetched ${bookings.length} bookings`, range);
      setBookings(bookings);
      setBackendReachabilityBanner(null);
      setCalendarLoadAttemptLine('');
    } catch (err: any) {
      console.error('[BOOKINGS] Error fetching bookings:', err);
      setBackendReachabilityBanner(null);
      setError(userFacingDataLoadError(err, 'bookings'));
    } finally {
      setLoading(false);
      setCalendarLoadAttemptLine('');
    }
  };

  const buildPredictionScopeParams = (): Record<string, string> => {
    const p: Record<string, string> = {};
    if (predictionScopeMin.trim()) p.min_check_in = predictionScopeMin.trim();
    if (predictionScopeMax.trim()) p.max_check_in = predictionScopeMax.trim();
    if (predictionScopePropertyId.trim()) p.property_id = predictionScopePropertyId.trim();
    return p;
  };

  const scopeMatchesLastPredictionRun =
    predictionScopeApplied != null &&
    predictionScopeApplied.min_check_in === predictionScopeMin.trim() &&
    (predictionScopeApplied.max_check_in || '') === (predictionScopeMax.trim() || '') &&
    String(predictionScopeApplied.property_id ?? '') === (predictionScopePropertyId.trim() || '');

  // Fetch predictions for linking tab (uses scope fields in query string)
  const fetchPredictions = async (options?: { refresh?: boolean }) => {
    try {
      setLoadingPredictions(true);
      setLinkingError('');
      setPredictionsAttemptLine('');
      await checkApiHealth();
      const params: Record<string, string> = {
        ...buildPredictionScopeParams(),
      };
      if (options?.refresh) params.refresh = '1';
      const response = await retryOnUnavailable(
        () =>
          api.get('/api/calendar/booking-links/predictions', {
            params,
            timeout: 95000,
          }),
        5,
        {
          onAttempt: (a, t) =>
            setPredictionsAttemptLine(
              `Loading predictions… attempt ${a} of ${t}. Automatic retries are in progress.`
            ),
          afterFailure: checkApiHealth,
        }
      );
      setPredictions(response.data.predictions || []);
      setPredictionScopeApplied(response.data.scope_applied ?? null);
      setPredictionsQueryCompleted(true);
      persistPredictionScope(
        predictionScopeMin.trim() || '2025-11-01',
        predictionScopeMax.trim(),
        predictionScopePropertyId.trim()
      );
      console.log('[BOOKINGS] Fetched predictions:', response.data.predictions?.length || 0);
      setPredictionsAttemptLine('');
      setBackendReachabilityBanner(null);
    } catch (err: any) {
      console.error('[BOOKINGS] Error fetching predictions:', err);
      setBackendReachabilityBanner(null);
      setLinkingError(userFacingDataLoadError(err, 'predictions'));
    } finally {
      setLoadingPredictions(false);
      setPredictionsAttemptLine('');
    }
  };

  // Fetch confirmed links
  const fetchConfirmedLinks = async () => {
    try {
      const response = await retryOnUnavailable(() =>
        api.get('/api/calendar/booking-links?status=confirmed', { timeout: 95000 })
      );
      setConfirmedLinks(response.data.links || []);
      console.log('[BOOKINGS] Fetched confirmed links:', response.data.links?.length || 0);
    } catch (err: any) {
      console.error('[BOOKINGS] Error fetching confirmed links:', err);
    }
  };

  const fetchRejectedLinks = async () => {
    try {
      const response = await retryOnUnavailable(() =>
        api.get('/api/calendar/booking-links?status=rejected', { timeout: 95000 })
      );
      setRejectedLinks(response.data.links || []);
      console.log('[BOOKINGS] Fetched rejected links:', response.data.links?.length || 0);
    } catch (err: any) {
      console.error('[BOOKINGS] Error fetching rejected links:', err);
    }
  };

  useEffect(() => {
    setSelectedPredictionUids((prev) => {
      const valid = new Set(predictions.map((p) => predictionLinkKey(p)));
      return new Set(Array.from(prev).filter((k) => valid.has(k)));
    });
  }, [predictions]);

  useEffect(() => {
    console.log('[BOOKINGS] Page loaded - starting auto-sync');
    
    // Load existing data immediately (fast); confirmed links needed for Combined merge.
    fetchData();
    void fetchConfirmedLinks();
    
    // Auto-sync on page load - sync in the background (non-blocking) after a short delay
    // This allows the page to render quickly while keeping data fresh
    const syncTimer = setTimeout(() => {
      const syncInBackground = async () => {
        try {
          console.log('[BOOKINGS] Starting auto-sync...');
          setSyncLoading(true);
          
          // Increase timeout to 120 seconds (2 minutes) for sync endpoint
          // Sync can take longer when processing multiple properties with iCal URLs
          const response = await retryOnUnavailable(() =>
            api.post('/api/calendar/sync', {}, { timeout: 120000 })
          );
          console.log('[BOOKINGS] Auto-sync completed:', response.data);
          
          // After sync completes, refresh data to show updates
          await Promise.all([fetchData(), fetchConfirmedLinks()]);
          console.log('[BOOKINGS] Data refreshed after sync');
        } catch (err: any) {
          // Sync failures are non-critical - data is already loaded
          console.error('[BOOKINGS] Auto-sync failed (non-critical):', err);
          console.error('[BOOKINGS] Error details:', err.response?.data || err.message);
        } finally {
          setSyncLoading(false);
          console.log('[BOOKINGS] Auto-sync finished');
        }
      };
      syncInBackground();
    }, 500); // Small delay to let page render first
    
    return () => {
      console.log('[BOOKINGS] Cleaning up auto-sync timer');
      clearTimeout(syncTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only bootstrap + auto-sync
  }, []);

  // Re-fetch when Check-in from/to change (they also bound the API stay window).
  const dateFetchSkipFirst = useRef(true);
  useEffect(() => {
    if (dateFetchSkipFirst.current) {
      dateFetchSkipFirst.current = false;
      return;
    }
    void fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only when date bounds change
  }, [dateFrom, dateTo]);

  useEffect(() => {
    let cancelled = false;
    const hydrateDbBackedState = async () => {
      const [predictionScopeDb, paymentScopeDb] = await Promise.all([
        getUserUiState(BOOKING_LINK_PRED_SCOPE_DB_KEY),
        getUserUiState(PAYMENTS_WORKSPACE_SCOPE_DB_KEY),
      ]);
      if (cancelled) return;

      if (predictionScopeDb && typeof predictionScopeDb === 'object') {
        if (typeof predictionScopeDb.min === 'string') setPredictionScopeMin(predictionScopeDb.min);
        if (typeof predictionScopeDb.max === 'string') setPredictionScopeMax(predictionScopeDb.max);
        if (typeof predictionScopeDb.propertyId === 'string') setPredictionScopePropertyId(predictionScopeDb.propertyId);
      }
      if (paymentScopeDb && typeof paymentScopeDb === 'object') {
        if (typeof paymentScopeDb.min === 'string') setPaymentsWsMinCheckIn(paymentScopeDb.min);
        if (typeof paymentScopeDb.max === 'string') setPaymentsWsMaxCheckIn(paymentScopeDb.max);
        if (typeof paymentScopeDb.propertyId === 'string') setPaymentsWsPropertyId(paymentScopeDb.propertyId);
        if (typeof paymentScopeDb.maxBookings === 'string') setPaymentsWsMaxBookings(paymentScopeDb.maxBookings);
      }
    };
    void hydrateDbBackedState();
    return () => {
      cancelled = true;
    };
  }, []);

  // Sync tabValue when URL changes; default missing/legacy list tabs to Bookings
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (!tabParam) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'bookings');
      if (!next.get('view')) next.set('view', bookingsViewMode);
      setSearchParams(next, { replace: true });
      if (tabValue !== 0) setTabValue(0);
      return;
    }
    const key = tabParam.toLowerCase();
    if (tabNameToIndex.hasOwnProperty(key)) {
      const newTabValue = tabNameToIndex[key];
      // Canonicalize legacy combined/ical/manual → bookings
      if (BOOKINGS_LIST_TAB_ALIASES.has(key) && key !== 'bookings') {
        const next = new URLSearchParams(searchParams);
        next.set('tab', 'bookings');
        if (!next.get('view')) next.set('view', bookingsViewMode);
        setSearchParams(next, { replace: true });
      }
      if (newTabValue !== tabValue) {
        setTabValue(newTabValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, tabValue]);

  // Sync Table/Calendar (?view=table|calendar; graph → calendar) from URL
  useEffect(() => {
    const raw = searchParams.get('view');
    const parsed = parseBookingsViewParam(raw);
    if (parsed && parsed !== bookingsViewMode) {
      setBookingsViewMode(parsed);
    }
    if (raw && raw.trim().toLowerCase() === 'graph') {
      const next = new URLSearchParams(searchParams);
      next.set('view', 'calendar');
      setSearchParams(next, { replace: true });
      return;
    }
    // Keep view in the URL on the Bookings list tab so the selection is shareable/bookmarkable
    const tab = (searchParams.get('tab') || 'bookings').toLowerCase();
    const onListTab = BOOKINGS_LIST_TAB_ALIASES.has(tab) || !searchParams.get('tab');
    if (onListTab && !raw) {
      const next = new URLSearchParams(searchParams);
      next.set('view', bookingsViewMode);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const setBookingsViewModeAndUrl = (nextMode: 'table' | 'calendar') => {
    setBookingsViewMode(nextMode);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('view', nextMode);
    setSearchParams(nextParams, { replace: true });
  };
  // Deep link: /bookings?focus_booking_id=… opens the dedicated booking details page.
  useEffect(() => {
    if (focusBookingId == null || loading) return;
    // Prefer navigating as soon as we know the id (list may still be loading for other reasons).
    navigate(`/bookings/${focusBookingId}`, { replace: true });
  }, [focusBookingId, loading, navigate]);

  // Linking tab: load confirmed + rejected links; predictions run when user sets scope and clicks Run
  useEffect(() => {
    if (tabValue !== 1) return;
    void (async () => {
      await Promise.all([fetchConfirmedLinks(), fetchRejectedLinks()]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  const fetchRevolutPaymentWorkspace = async () => {
    try {
      setRevolutWorkspaceLoading(true);
      setRevolutWorkspaceError('');
      const params: Record<string, string> = {};
      if (paymentsWsMinCheckIn.trim()) params.min_check_in = paymentsWsMinCheckIn.trim().slice(0, 10);
      if (paymentsWsMaxCheckIn.trim()) params.max_check_in = paymentsWsMaxCheckIn.trim().slice(0, 10);
      if (paymentsWsPropertyId.trim()) params.property_id = paymentsWsPropertyId.trim();
      const maxRaw = paymentsWsMaxBookings.trim();
      if (maxRaw) {
        const n = parseInt(maxRaw, 10);
        if (!Number.isNaN(n) && n >= 1) params.max_bookings = String(Math.min(n, 10000));
      }
      const response = await retryOnUnavailable(() =>
        api.get('/api/calendar/booking-links/revolut-payment-workspace', {
          params,
          timeout: 180000,
        })
      );
      setRevolutWorkspace(response.data);
      const pending: Record<number, number | ''> = {};
      for (const row of response.data.rows || []) {
        const bid = row.manual_booking?.id;
        if (bid == null) continue;
        const linked = row.manual_booking?.revolut_transaction_db_id;
        const declined = !!row.manual_booking?.revolut_payment_declined;
        if (linked != null) {
          pending[bid] = linked;
        } else if (!declined && row.candidate_transactions?.length) {
          pending[bid] = row.candidate_transactions[0].id;
        } else {
          pending[bid] = '';
        }
      }
      setRevolutPendingTxId(pending);
    } catch (err: any) {
      console.error('[BOOKINGS] Revolut workspace:', err);
      setRevolutWorkspaceError(userFacingDataLoadError(err, 'payments'));
      setRevolutWorkspace(null);
    } finally {
      setRevolutWorkspaceLoading(false);
    }
  };

  useEffect(() => {
    try {
      sessionStorage.setItem(
        PAYMENTS_WORKSPACE_SCOPE_FORM_KEY,
        JSON.stringify({
          min: paymentsWsMinCheckIn,
          max: paymentsWsMaxCheckIn,
          propertyId: paymentsWsPropertyId,
          maxBookings: paymentsWsMaxBookings,
        })
      );
    } catch {
      /* ignore */
    }
    void putUserUiState(
      PAYMENTS_WORKSPACE_SCOPE_DB_KEY,
      {
        min: paymentsWsMinCheckIn,
        max: paymentsWsMaxCheckIn,
        propertyId: paymentsWsPropertyId,
        maxBookings: paymentsWsMaxBookings,
      },
      'sessionStorage',
      true
    );
  }, [paymentsWsMinCheckIn, paymentsWsMaxCheckIn, paymentsWsPropertyId, paymentsWsMaxBookings]);

  useEffect(() => {
    if (tabValue !== 2) return;
    void fetchRevolutPaymentWorkspace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  const revolutPaymentSections = useMemo(() => {
    const rows = revolutWorkspace?.rows ?? [];
    const pending: any[] = [];
    const linked: any[] = [];
    const rejected: any[] = [];
    for (const r of rows) {
      const mb = r.manual_booking;
      const has = mb?.revolut_transaction_db_id != null;
      const decl = !!mb?.revolut_payment_declined;
      if (has) linked.push(r);
      else if (decl) rejected.push(r);
      else pending.push(r);
    }
    return { pending, linked, rejected };
  }, [revolutWorkspace]);

  /** Payments tab: show matching rules immediately (defaults mirror backend until workspace JSON loads). */
  const paymentsMatchingScopeDisplay = useMemo(() => {
    const w = revolutWorkspace;
    return {
      payoutLagDays: w?.payout_lag_days ?? REVOLUT_PAYMENTS_SCOPE_DEFAULT.payoutLagDays,
      windowDays: w?.payment_window_days_after_expectation ?? REVOLUT_PAYMENTS_SCOPE_DEFAULT.paymentWindowDaysAfterExpectation,
      vrboIrelandDays:
        w?.payment_window_days_vrbo_ireland_after_expectation ??
        REVOLUT_PAYMENTS_SCOPE_DEFAULT.vrboIrelandWindowDaysAfterExpectation,
      ukNick: w?.uk_property_nickname ?? REVOLUT_PAYMENTS_SCOPE_DEFAULT.ukPropertyNickname,
      preset: w?.payments_matching_preset ?? REVOLUT_PAYMENTS_SCOPE_DEFAULT.matchingPreset,
      confirmedFromApi: !!w,
    };
  }, [revolutWorkspace]);

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  // Stack order: keep green away from red/pink/orange for red–green colour vision.
  // Consecutive property segments use consecutive entries in this list.
  const propertyChartColors = [
    '#1976d2', // blue
    '#ed6c02', // orange
    '#6a1b9a', // deep purple
    '#2e7d32', // green
    '#00838f', // teal
    '#f9a825', // amber
    '#455a64', // blue-grey
    '#c2185b', // pink
    '#5d4037', // brown
    '#d32f2f', // red
  ];

  type ChartSeriesKey = { key: string; property: string; platform: string };

  const chartSeriesKey = (property: string, platform: string) => `${property} (${platform})`;

  /** Spread alphabetically-adjacent nicknames apart in the stack (67RR / 87HP). */
  const orderPropertyKeysForChart = (labels: string[]) => {
    const sorted = [...labels].sort((a, b) => a.localeCompare(b));
    const odds = sorted.filter((_, i) => i % 2 === 1);
    const evens = sorted.filter((_, i) => i % 2 === 0);
    return [...odds, ...evens];
  };

  const mixHexToward = (hex: string, toward: number, amount: number) => {
    const raw = hex.replace('#', '');
    if (raw.length !== 6) return hex;
    const mix = (c: number) => Math.round(c + (toward - c) * amount);
    const r = mix(parseInt(raw.slice(0, 2), 16));
    const g = mix(parseInt(raw.slice(2, 4), 16));
    const b = mix(parseInt(raw.slice(4, 6), 16));
    return `#${[r, g, b].map((n) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0')).join('')}`;
  };

  /** Same property shares a base hue; platform shifts lightness (Airbnb / VRBO / other). */
  const getSeriesChartColor = (series: ChartSeriesKey, propertyKeys: string[]) => {
    const idx = propertyKeys.indexOf(series.property);
    const i = idx >= 0 ? idx : 0;
    const base = propertyChartColors[i % propertyChartColors.length];
    const plat = series.platform.toLowerCase();
    if (plat.includes('vrbo') || plat.includes('homeaway')) {
      return mixHexToward(base, 255, 0.35);
    }
    if (plat.includes('manual')) {
      return mixHexToward(base, 0, 0.25);
    }
    if (plat.includes('booking')) {
      return mixHexToward(base, 0, 0.12);
    }
    // Airbnb (and default): full base colour
    return base;
  };

  const bookingChartDateStr = (
    b: {
      check_in_date?: string;
      booked_at_display?: string;
      booked_at_fallback?: string;
      booked_at?: string;
      created_at?: string;
    },
    mode: 'check_in' | 'booked'
  ): string => {
    if (mode === 'booked') {
      return String(b.booked_at_display || b.booked_at_fallback || b.booked_at || b.created_at || '').trim();
    }
    return String(b.check_in_date || '').trim();
  };

  const monthKeyFromDateStr = (dateStr: string): { monthKey: string; monthLabel: string } | null => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    return {
      monthKey: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      monthLabel: `${monthNames[d.getMonth()]} ${d.getFullYear()}`,
    };
  };

  const bookingChartAmount = (b: Booking): number => {
    const n = Number(b.total_payout_amount ?? b.host_payout_amount ?? 0);
    return Number.isFinite(n) ? n : 0;
  };

  /** Occupied night count in a calendar month for one property (merged intervals, no double-count). */
  const occupiedNightsInMonth = (propertyBookings: Booking[], year: number, monthIndex: number): number => {
    const monthStart = new Date(year, monthIndex, 1);
    const dim = daysInMonth(monthStart);
    const monthEndExclusive = new Date(year, monthIndex, dim + 1);
    const intervals: Array<{ startOffset: number; endOffsetExclusive: number }> = [];
    for (const b of propertyBookings) {
      const ci = parseBookingDateOnly(b.check_in_date);
      const coRaw = parseBookingDateOnly(b.check_out_date);
      if (!ci || !coRaw) continue;
      const co =
        coRaw.getTime() <= ci.getTime()
          ? new Date(ci.getFullYear(), ci.getMonth(), ci.getDate() + 1)
          : coRaw;
      if (!(ci < monthEndExclusive && co > monthStart)) continue;
      const start = ci < monthStart ? monthStart : ci;
      const end = co > monthEndExclusive ? monthEndExclusive : co;
      const startOffset = Math.round((start.getTime() - monthStart.getTime()) / 86400000);
      const endOffsetExclusive = Math.round((end.getTime() - monthStart.getTime()) / 86400000);
      if (endOffsetExclusive > startOffset) {
        intervals.push({ startOffset, endOffsetExclusive });
      }
    }
    return mergeDayIntervals(intervals).reduce(
      (sum, iv) => sum + (iv.endOffsetExclusive - iv.startOffset),
      0
    );
  };

  /**
   * Occupancy % by calendar month × property (not platform).
   * A night is occupied if any stay covers it; overlapping sources are merged.
   */
  const buildOccupancyChartData = (sourceBookings: Booking[]) => {
    const byProperty = new Map<string, Booking[]>();
    const monthKeys = new Set<string>();

    for (const b of sourceBookings) {
      const propertyLabel = (b.property_nickname || '').trim() || `Property ${b.property_id}`;
      const list = byProperty.get(propertyLabel) || [];
      list.push(b);
      byProperty.set(propertyLabel, list);

      const ci = parseBookingDateOnly(b.check_in_date);
      const coRaw = parseBookingDateOnly(b.check_out_date);
      if (!ci || !coRaw) continue;
      const lastNight =
        coRaw.getTime() <= ci.getTime()
          ? ci
          : addDays(coRaw, -1);
      let cursor = startOfMonth(ci);
      const endMonth = startOfMonth(lastNight);
      while (cursor.getTime() <= endMonth.getTime()) {
        monthKeys.add(ymdKey(cursor).slice(0, 7));
        cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
      }
    }

    const propertyKeys = orderPropertyKeysForChart(Array.from(byProperty.keys()));
    const seriesKeys: ChartSeriesKey[] = propertyKeys.map((property) => ({
      key: property,
      property,
      platform: 'Occupancy',
    }));

    const data = Array.from(monthKeys)
      .sort((a, b) => a.localeCompare(b))
      .map((monthKey) => {
        const [ys, ms] = monthKey.split('-').map(Number);
        const monthStart = new Date(ys, ms - 1, 1);
        const dim = daysInMonth(monthStart);
        const row: Record<string, string | number> = {
          month: `${monthNames[ms - 1]} ${ys}`,
          monthKey,
        };
        for (const property of propertyKeys) {
          const occ = occupiedNightsInMonth(byProperty.get(property) || [], ys, ms - 1);
          row[property] = Math.round((occ / dim) * 1000) / 10; // 1 decimal %
        }
        return row;
      });

    return { data, propertyKeys, seriesKeys };
  };

  const buildStackedChartData = (
    sourceBookings: Booking[],
    mode: 'check_in' | 'booked',
    valueMode: 'count' | 'amount' | 'occupancy' | 'lead_by_size' = 'count'
  ) => {
    if (valueMode === 'lead_by_size') {
      // Lead/size uses a separate chart builder; keep this path typed for shared chartValueMode.
      return { data: [] as Array<Record<string, string | number>>, propertyKeys: [] as string[], seriesKeys: [] as ChartSeriesKey[] };
    }
    if (valueMode === 'occupancy') {
      return buildOccupancyChartData(sourceBookings);
    }
    const byMonth = new Map<string, Record<string, string | number>>();
    const seriesMap = new Map<string, ChartSeriesKey>();

    for (const b of sourceBookings) {
      const monthMeta = monthKeyFromDateStr(bookingChartDateStr(b, mode));
      if (!monthMeta) continue;
      const { monthKey, monthLabel } = monthMeta;
      const propertyLabel = (b.property_nickname || '').trim() || `Property ${b.property_id}`;
      const platformLabel = (b.platform || '').trim() || 'Unknown';
      const key = chartSeriesKey(propertyLabel, platformLabel);
      seriesMap.set(key, { key, property: propertyLabel, platform: platformLabel });

      if (!byMonth.has(monthKey)) {
        byMonth.set(monthKey, { month: monthLabel, monthKey });
      }
      const row = byMonth.get(monthKey)!;
      const current = typeof row[key] === 'number' ? (row[key] as number) : 0;
      const delta = valueMode === 'amount' ? bookingChartAmount(b) : 1;
      row[key] = current + delta;
    }

    const propertyKeys = orderPropertyKeysForChart(
      Array.from(new Set(Array.from(seriesMap.values()).map((s) => s.property)))
    );
    const seriesKeys: ChartSeriesKey[] = [];
    for (const property of propertyKeys) {
      const platforms = Array.from(seriesMap.values())
        .filter((s) => s.property === property)
        .map((s) => s.platform)
        .sort((a, b) => a.localeCompare(b));
      for (const platform of platforms) {
        const key = chartSeriesKey(property, platform);
        const meta = seriesMap.get(key);
        if (meta) seriesKeys.push(meta);
      }
    }

    return {
      data: Array.from(byMonth.values()).sort((a, b) => String(a.monthKey).localeCompare(String(b.monthKey))),
      propertyKeys,
      seriesKeys,
    };
  };

  // Date range filter uses check-in; month chip from chart click uses chartMonthMode date.
  const dateFilteredBookings = useMemo(() => {
    return bookings.filter((b) => {
      const checkInStr = b.check_in_date || '';
      if (!checkInStr) return false;
      const checkInDate = new Date(checkInStr);
      if (isNaN(checkInDate.getTime())) return false;
      const checkIn = checkInDate.toISOString().slice(0, 10);
      if (dateFrom && checkIn < dateFrom) return false;
      if (dateTo && checkIn > dateTo) return false;
      if (selectedMonthFilter) {
        const monthMeta = monthKeyFromDateStr(bookingChartDateStr(b, chartMonthMode));
        if (!monthMeta || monthMeta.monthKey !== selectedMonthFilter.monthKey) return false;
      }
      return true;
    });
  }, [bookings, dateFrom, dateTo, selectedMonthFilter, chartMonthMode]);

  /** Stale synthetic iCal ghosts → A/B/C (classified against full loaded set so twins outside date filter still count). */
  const ghostCategoryById = useMemo(() => classifyStaleIcalGhosts(bookings), [bookings]);
  /** Nested shrinking iCal remnants with no Export twin (category N). */
  const nestingRemnantById = useMemo(() => classifyIcalNestingRemnants(bookings), [bookings]);

  /** Linking predictions that do not involve nesting-remnant iCal rows. */
  const linkingPredictions = useMemo(
    () =>
      predictions.filter((p) => {
        const icalId = Number(p?.ical_booking_id);
        if (!Number.isFinite(icalId)) return true;
        return !nestingRemnantById.has(icalId);
      }),
    [predictions, nestingRemnantById]
  );

  const paymentsWorkspacePropertyOptions = useMemo(() => {
    const m = new Map<number, string>();
    for (const b of bookings) {
      if (b.property_id == null) continue;
      const label = (b.property_nickname || '').trim() || `Property ${b.property_id}`;
      if (!m.has(b.property_id)) m.set(b.property_id, label);
    }
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [bookings]);

  // Chart data: iCal vs Manual counts/amounts per month (for Linking tab)
  const linkingChartData = useMemo(() => {
    const byMonth = new Map<string, { month: string; monthKey: string; iCal: number; Manual: number }>();
    for (const b of dateFilteredBookings) {
      const monthMeta = monthKeyFromDateStr(bookingChartDateStr(b, chartMonthMode));
      if (!monthMeta) continue;
      const { monthKey, monthLabel } = monthMeta;
      if (!byMonth.has(monthKey)) {
        byMonth.set(monthKey, { month: monthLabel, monthKey, iCal: 0, Manual: 0 });
      }
      const row = byMonth.get(monthKey)!;
      const delta = chartValueMode === 'amount' ? bookingChartAmount(b) : 1;
      if (b.source === 'iCal') row.iCal += delta;
      else if (isManualOrExportSource(b.source)) row.Manual += delta;
    }
    return Array.from(byMonth.values()).sort((a, b) => a.monthKey.localeCompare(b.monthKey));
  }, [dateFilteredBookings, chartMonthMode, chartValueMode]);

  /** Linking tab occupancy: property-level % (iCal vs Manual split is not meaningful for nights). */
  const linkingOccupancyChartData = useMemo(
    () => buildOccupancyChartData(dateFilteredBookings),
    [dateFilteredBookings]
  );

  const withLeadTimes = (rows: Booking[]) =>
    rows.map((b) => ({ ...b, lead_time: bookingLeadTimeDays(b) }));

  const linkingLeadBySizeChart = useMemo(
    () => buildLeadTimeByStaySizeChart(withLeadTimes(dateFilteredBookings)),
    [dateFilteredBookings]
  );

  // Bookings-tab charts (combinedChartData / lead-time) are built after enrichedBookings
  // so they use the same column filters as the table (platform, account owner, etc.).

  const renderLeadTimeCharts = (lead: ReturnType<typeof buildLeadTimeChartData>) => {
    const hasHist = lead.histogram.some((r) => r.count > 0);
    const hasMonth = lead.byMonth.length > 0;
    if (!hasHist && !hasMonth) {
      return (
        <Alert severity="info" sx={{ mt: 2, py: 1.5 }}>
          No lead-time data (need both booked date and check-in).
        </Alert>
      );
    }
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
          Lead time (days booked → check-in)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {lead.stats.withLeadTime} booking{lead.stats.withLeadTime === 1 ? '' : 's'} with lead time
          {lead.stats.avg != null ? ` · avg ${lead.stats.avg}d` : ''}
          {lead.stats.median != null ? ` · median ${lead.stats.median}d` : ''}
          {lead.stats.unknown ? ` · ${lead.stats.unknown} unknown` : ''}
        </Typography>
        {hasHist ? (
          <Box sx={{ width: '100%', height: 260, mb: hasMonth ? 3 : 0 }}>
            <ResponsiveContainer>
              <BarChart data={lead.histogram} margin={{ top: 8, right: 20, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="bucket" tick={{ fontSize: 12 }} interval={0} angle={-30} textAnchor="end" height={60} />
                <YAxis allowDecimals={false} />
                <RechartsTooltip />
                <Bar dataKey="count" name="Bookings" fill="#2e7d32" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : null}
        {hasMonth ? (
          <>
            <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
              Average lead time by {chartMonthMode === 'booked' ? 'booked' : 'check-in'} month
            </Typography>
            <Box sx={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={lead.byMonth} margin={{ top: 8, right: 20, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} unit="d" />
                  <RechartsTooltip
                    formatter={(value: any, _name: any, item: any) => [
                      `${value} days (n=${item?.payload?.count ?? 0})`,
                      'Avg lead time',
                    ]}
                  />
                  <Bar
                    dataKey="avgLeadTime"
                    name="Avg lead time (days)"
                    fill="#1565c0"
                    onClick={(e: any) => e?.payload && handleBarClick(e.payload)}
                    cursor="pointer"
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </>
        ) : null}
      </Box>
    );
  };

  const chartValueModeToggle = (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={chartValueMode}
      onChange={(_, next: 'count' | 'amount' | 'occupancy' | 'lead_by_size' | null) => {
        if (!next) return;
        setChartValueMode(next);
        if (next === 'occupancy' || next === 'lead_by_size') {
          setChartMonthMode('check_in');
          setSelectedMonthFilter(null);
        }
      }}
      sx={{ flexShrink: 0 }}
    >
      <ToggleButton value="count">Bookings</ToggleButton>
      <ToggleButton value="amount">Amount</ToggleButton>
      <ToggleButton value="occupancy">Occupancy %</ToggleButton>
      <ToggleButton value="lead_by_size">Lead / size</ToggleButton>
    </ToggleButtonGroup>
  );

  const chartMonthModeToggle = (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={chartMonthMode}
      onChange={(_, next: 'check_in' | 'booked' | null) => {
        if (!next) return;
        setChartMonthMode(next);
        setSelectedMonthFilter(null);
      }}
      sx={{ flexShrink: 0 }}
      disabled={chartValueMode === 'occupancy' || chartValueMode === 'lead_by_size'}
    >
      <ToggleButton value="check_in">Check-in month</ToggleButton>
      <ToggleButton value="booked">Booked month</ToggleButton>
    </ToggleButtonGroup>
  );

  const chartControls = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {chartValueModeToggle}
      {chartMonthModeToggle}
    </Box>
  );

  const chartMonthModeTitle = (() => {
    if (chartValueMode === 'occupancy') {
      return 'Occupancy % by calendar month (by property; overlapping stays merged)';
    }
    if (chartValueMode === 'lead_by_size') {
      return 'Lead time by booking size (avg lead days + lead÷nights ratio)';
    }
    const metric = chartValueMode === 'amount' ? 'Payout amount' : 'Bookings';
    const by =
      chartMonthMode === 'booked' ? 'made per month' : 'by check-in month';
    return `${metric} ${by} (stacked by property · platform)`;
  })();

  const chartYAxisProps =
    chartValueMode === 'amount'
      ? {
          allowDecimals: true,
          tickFormatter: (v: number) =>
            new Intl.NumberFormat('en-GB', {
              notation: 'compact',
              maximumFractionDigits: 1,
            }).format(Number(v) || 0),
        }
      : chartValueMode === 'occupancy'
        ? {
            allowDecimals: true,
            domain: [0, 100] as [number, number],
            tickFormatter: (v: number) => `${Number(v) || 0}%`,
          }
        : chartValueMode === 'lead_by_size'
          ? { allowDecimals: true }
          : { allowDecimals: false };

  const chartTooltipFormatter = ((value: unknown, name: unknown) => {
    const n = typeof value === 'number' ? value : Number(value);
    if (chartValueMode === 'amount') {
      const label = Number.isFinite(n)
        ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'EUR' }).format(n)
        : String(value ?? '');
      return [label, String(name ?? '')];
    }
    if (chartValueMode === 'occupancy') {
      const label = Number.isFinite(n) ? `${n.toFixed(1)}%` : String(value ?? '');
      return [label, String(name ?? '')];
    }
    if (chartValueMode === 'lead_by_size') {
      const label = Number.isFinite(n) ? String(n) : String(value ?? '');
      return [label, String(name ?? '')];
    }
    return [Number.isFinite(n) ? n : value, String(name ?? '')];
  }) as never;

  const chartBarStackId = chartValueMode === 'occupancy' ? undefined : 'property';

  const renderLeadBySizeChart = (chart: ReturnType<typeof buildLeadTimeByStaySizeChart>) => {
    if (chart.data.length === 0) {
      return (
        <Alert severity="info" sx={{ py: 2 }}>
          No lead-time + nights data to display.
        </Alert>
      );
    }
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {chart.stats.withBoth} booking{chart.stats.withBoth === 1 ? '' : 's'} with lead time and nights
          {chart.stats.skipped ? ` · ${chart.stats.skipped} skipped` : ''}. Lead÷nights = lead days /
          stay nights.
        </Typography>
        <Box sx={{ width: '100%', height: 320 }}>
          <ResponsiveContainer>
            <BarChart data={chart.data} margin={{ top: 8, right: 20, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="size" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals />
              <RechartsTooltip
                formatter={((value: unknown, name: unknown) => {
                  const n = typeof value === 'number' ? value : Number(value);
                  return [Number.isFinite(n) ? n : String(value ?? ''), String(name ?? '')];
                }) as never}
              />
              <Legend />
              <Bar dataKey="avgLeadTime" name="Avg lead time (days)" fill="#6a1b9a" />
              <Bar dataKey="leadPerNight" name="Lead ÷ nights" fill="#00838f" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    );
  };
  // Must be before any early return (RBAC) — Rules of Hooks
  const sortedPredictions = useMemo(() => {
    const list = [...linkingPredictions];
    return stableSort(list, (a, b) => {
      const va = getPredictionSortValue(a, predictionSortBy);
      const vb = getPredictionSortValue(b, predictionSortBy);
      let cmp: number;
      if (typeof va === 'number' && typeof vb === 'number') {
        cmp = va - vb;
      } else {
        cmp = String(va).localeCompare(String(vb), undefined, { numeric: true });
      }
      if (predictionSortOrder === 'desc') cmp = -cmp;
      return cmp;
    });
  }, [linkingPredictions, predictionSortBy, predictionSortOrder]);

  const filteredSortedPredictions = useMemo(() => {
    if (predictionYesRequiredKeys.length === 0) return sortedPredictions;
    return sortedPredictions.filter((p) => predictionPassesYesFilters(p, predictionYesRequiredKeys));
  }, [sortedPredictions, predictionYesRequiredKeys]);

  const linkingPropertyOptions = useMemo(() => {
    const byId = new Map<number, string>();
    for (const b of bookings) {
      if (!byId.has(b.property_id)) {
        byId.set(b.property_id, b.property_nickname || `Property ${b.property_id}`);
      }
    }
    return Array.from(byId.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [bookings]);


  const handleSync = async () => {
    try {
      console.log('[BOOKINGS] Manual sync triggered by user');
      setSyncLoading(true);
      setError('');
      
      // Increase timeout to 120 seconds (2 minutes) for sync endpoint
      // Sync can take longer when processing multiple properties with iCal URLs
      const response = await retryOnUnavailable(() =>
        api.post('/api/calendar/sync', {}, { timeout: 120000 })
      );
      console.log('[BOOKINGS] Manual sync completed:', response.data);
      
      await Promise.all([fetchData(), fetchConfirmedLinks()]);
      console.log('[BOOKINGS] Data refreshed after manual sync');

      const data = response.data || {};
      const created = Number(data.created_count ?? data.synced_count ?? 0);
      const updated = Number(data.updated_count ?? 0);
      const unchanged = Number(data.unchanged_count ?? 0);
      const processed = Number(data.processed_count ?? created + updated + unchanged);
      const propsSynced = Number(data.properties_synced ?? 0);
      const autoLinks = data.confirmation_code_auto_links || {};
      const linksCreated = Number(autoLinks.created ?? 0);
      const linksUpdated = Number(autoLinks.updated ?? 0);
      const enrich = data.ical_enrichment || {};
      const enrichUpdated = Number(enrich.updated ?? 0);
      const syncErrors: string[] = Array.isArray(data.errors) ? data.errors : [];

      const parts = [
        `${created} new`,
        `${updated} updated`,
        `${unchanged} unchanged`,
      ];
      if (linksCreated || linksUpdated) {
        parts.push(`${linksCreated + linksUpdated} confirmation-code link(s)`);
      }
      if (enrichUpdated) {
        parts.push(`${enrichUpdated} iCal row(s) enriched`);
      }
      let message =
        `Calendar sync finished for ${propsSynced} propert${propsSynced === 1 ? 'y' : 'ies'}: ` +
        `${parts.join(', ')}` +
        (processed ? ` (${processed} event(s) processed).` : '.');
      if (syncErrors.length > 0) {
        message += ` ${syncErrors.length} warning(s): ${syncErrors.slice(0, 2).join('; ')}`;
      }

      setToast(
        createToast(syncErrors.length > 0 ? 'warning' : 'success', message)
      );
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to sync calendar';
      console.error('[BOOKINGS] Manual sync failed:', err);
      console.error('[BOOKINGS] Error details:', err.response?.data || err.message);
      setError(errorMsg);
      setToast(toastFromApiError(err, errorMsg));
    } finally {
      setSyncLoading(false);
    }
  };

  const handleOpenDialog = (booking?: Booking) => {
    if (booking) {
      setEditingBooking(booking);
      setFormData({
        property_id: booking.property_id.toString(),
        platform: booking.platform,
        source: booking.source,
        external_booking_id: booking.external_booking_id || '',
        guest_name: booking.guest_name || '',
        guest_email: booking.guest_email || '',
        guest_phone_last_4: booking.guest_phone_last_4 || '',
        reservation_url: booking.reservation_url || '',
        check_in_date: booking.check_in_date,
        check_out_date: booking.check_out_date,
        nights: booking.nights,
        adults_count: booking.adults_count,
        children_count: booking.children_count,
        total_payout_amount: booking.total_payout_amount?.toString() || '',
        host_payout_amount: booking.host_payout_amount?.toString() || '',
        currency: booking.currency,
        status: booking.status,
        booking_type: booking.booking_type,
        summary: booking.summary || '',
        description: booking.description || '',
        notes: booking.notes || '',
        booked_at: booking.booked_at ? booking.booked_at.slice(0, 16) : ''
      });
      setFlightInForm(flightToFormFields(booking.flight_in));
      setFlightOutForm(flightToFormFields(booking.flight_out));
    } else {
      setEditingBooking(null);
      setFormData({
        property_id: '',
        platform: 'Airbnb',
        source: 'Manual',
        external_booking_id: '',
        guest_name: '',
        guest_email: '',
        guest_phone_last_4: '',
        reservation_url: '',
        check_in_date: '',
        check_out_date: '',
        nights: 0,
        adults_count: 1,
        children_count: 0,
        total_payout_amount: '',
        host_payout_amount: '',
        currency: 'EUR',
        status: 'confirmed',
        booking_type: 'reservation',
        summary: '',
        description: '',
        notes: '',
        booked_at: ''
      });
      setFlightInForm(EMPTY_FLIGHT_FORM);
      setFlightOutForm(EMPTY_FLIGHT_FORM);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingBooking(null);
  };

  const handleSubmit = async () => {
    try {
      setError('');
      
      const bookingData = {
        ...formData,
        property_id: parseInt(formData.property_id),
        nights: formData.nights,
        adults_count: formData.adults_count,
        children_count: formData.children_count,
        total_payout_amount: formData.total_payout_amount ? parseFloat(formData.total_payout_amount) : null,
        host_payout_amount: formData.host_payout_amount ? parseFloat(formData.host_payout_amount) : null,
        booked_at: formData.booked_at || null,
        flight_in: buildFlightPayload(flightInForm),
        flight_out: buildFlightPayload(flightOutForm),
      };

      if (editingBooking) {
        await api.put(`/api/calendar/unified/bookings/${editingBooking.id}`, bookingData);
      } else {
        await api.post('/api/calendar/unified/bookings', bookingData);
      }

      await fetchData();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save booking');
      console.error('Error saving booking:', err);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDelete = async (booking: Booking) => {
    if (window.confirm('Are you sure you want to delete this booking?')) {
      try {
        await api.delete(`/api/calendar/unified/bookings/${booking.id}`);
        await fetchData();
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to delete booking');
        console.error('Error deleting booking:', err);
      }
    }
  };

  const handleToggleSelectBooking = useCallback((bookingId: number) => {
    setSelectedBookingIds((prev) => {
      const next = new Set(prev);
      if (next.has(bookingId)) next.delete(bookingId);
      else next.add(bookingId);
      return next;
    });
  }, []);

  const handleSelectAllVisibleBookings = useCallback((visible: Booking[], checked: boolean) => {
    setSelectedBookingIds((prev) => {
      const next = new Set(prev);
      visible.forEach((b) => {
        if (checked) next.add(b.id);
        else next.delete(b.id);
      });
      return next;
    });
  }, []);

  /** Expand merged Combined rows so both underlying IDs are deleted. */
  const resolveSelectedBookingIdsForDelete = (visible: Booking[]): number[] => {
    const ids = new Set<number>();
    visible.forEach((b) => {
      if (!selectedBookingIds.has(b.id)) return;
      ids.add(b.id);
      if (b.is_merged_pair) {
        if (b.linked_ical_booking_id != null) ids.add(b.linked_ical_booking_id);
        if (b.linked_manual_booking_id != null) ids.add(b.linked_manual_booking_id);
      }
    });
    // Also keep any selected ids not currently visible (e.g. selected then filtered away)
    selectedBookingIds.forEach((id) => ids.add(id));
    return Array.from(ids).sort((a, b) => a - b);
  };

  const handleConfirmBulkDelete = async (visible: Booking[]) => {
    const ids = resolveSelectedBookingIdsForDelete(visible);
    if (!ids.length) {
      setBulkDeleteDialogOpen(false);
      return;
    }
    setBulkDeleting(true);
    setError('');
    try {
      const response = await api.post('/api/calendar/unified/bookings/bulk-delete-ids', {
        booking_ids: ids,
      });
      const deleted = response.data?.deleted_count ?? ids.length;
      setBulkDeleteDialogOpen(false);
      setSelectedBookingIds(new Set());
      await fetchData();
      setToast(
        createToast(
          'success',
          `Deleted ${deleted} booking${deleted === 1 ? '' : 's'}`
        )
      );
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete selected bookings');
      console.error('Bulk delete failed:', err);
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleImportFiles = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.xlsx,.xls,.pdf';
    input.multiple = true;
    input.onchange = async (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files && files.length > 0) {
        try {
          setImportingFiles(true);
          setError('');
          let totalImported = 0;
          let totalCreated = 0;
          let totalUpdated = 0;
          let totalUnchanged = 0;
          let totalSkippedDupes = 0;

          const orderedFiles = Array.from(files).sort((a, b) => {
            const payoutScore = (name: string) => (/payment|payout/i.test(name) ? 1 : 0);
            return payoutScore(a.name) - payoutScore(b.name);
          });
          for (let i = 0; i < orderedFiles.length; i++) {
            const file = orderedFiles[i];
            const fileType = file.name.toLowerCase().endsWith('.pdf') ? 'pdf' :
              file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls') ? 'excel' : 'csv';

            const formData = new FormData();
            formData.append('file', file);
            formData.append('fileType', fileType);

            const response = await retryOnUnavailable(() =>
              api.post('/api/calendar/unified/import-file', formData, {
                timeout: 300000, // 5 min for large files
              })
            );
            const data = response.data || {};
            totalImported += data.imported_count ?? 0;
            totalCreated += data.created_count ?? 0;
            totalUpdated += data.updated_count ?? 0;
            totalUnchanged += data.unchanged_count ?? 0;
            totalSkippedDupes += data.skipped_duplicate_rows ?? 0;
          }

          await fetchData();
          setTabValue(0);
          const nextParams = new URLSearchParams(searchParams);
          nextParams.set('tab', 'bookings');
          setSearchParams(nextParams, { replace: true });

          const fileCount = files.length;
          const detail =
            `${fileCount} file${fileCount === 1 ? '' : 's'} · ${totalImported} booking(s) processed ` +
            `(${totalCreated} new, ${totalUpdated} updated, ${totalUnchanged} unchanged` +
            (totalSkippedDupes ? `, ${totalSkippedDupes} duplicate row(s) skipped in file` : '') +
            '). Existing bookings were updated, not duplicated.';
          alert(`Import complete. ${detail}`);
        } catch (err: any) {
          console.error('❌ Error importing files:', err);
          const status = err.response?.status;
          const isUnavailable = status === 502 || status === 503 || status === 504 || err.code === 'ERR_NETWORK';
          const msg = isUnavailable
            ? 'The server may be starting up or busy. Please wait a minute and try again.'
            : (err.response?.data?.error || err.response?.data?.message || err.message || 'Unknown error');
          setError(msg);
          alert(`Import failed: ${msg}`);
        } finally {
          setImportingFiles(false);
        }
      }
    };
    input.click();
  };


  const handleRequestSort = useCallback(
    (_event: React.MouseEvent<unknown>, property: keyof Booking | 'actions') => {
      if (property === 'actions') return;

      // Date-like columns: first click = newest/latest first (desc).
      const preferDescFirst =
        property === 'check_in_date' ||
        property === 'check_out_date' ||
        property === 'last_sync' ||
        property === 'booked_at' ||
        property === 'lead_time' ||
        property === 'created_at' ||
        property === 'updated_at';

      if (orderBy === property) {
        setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        return;
      }
      setOrder(preferDescFirst ? 'desc' : 'asc');
      setOrderBy(property as keyof Booking);
    },
    [orderBy]
  );

  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    const aVal = a[orderBy];
    const bVal = b[orderBy];
    
    // Handle undefined and null values - put them at the end
    if ((aVal === undefined || aVal === null) && (bVal === undefined || bVal === null)) return 0;
    if (aVal === undefined || aVal === null) return 1;
    if (bVal === undefined || bVal === null) return -1;
    
    if (bVal < aVal) {
      return -1;
    }
    if (bVal > aVal) {
      return 1;
    }
    return 0;
  }

  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
  ): (a: any, b: any) => number {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  const handlePredictionRequestSort = (property: PredictionSortKey) => {
    const isAsc = predictionSortBy === property && predictionSortOrder === 'asc';
    setPredictionSortOrder(isAsc ? 'desc' : 'asc');
    setPredictionSortBy(property);
  };

  // Get unique properties for filter dropdown
  const uniqueProperties = useMemo(
    () =>
      Array.from(
        new Set(
          bookings
            .map((b) => b.property_nickname || `Property ${b.property_id}`)
            .filter(Boolean)
        )
      ).sort(),
    [bookings]
  );

  const uniqueStatuses = useMemo(
    () =>
      Array.from(
        new Set(bookings.map((b) => String(b.status || '').trim()).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b)),
    [bookings]
  );

  // Confirm a match
  const confirmMatch = async (prediction: any) => {
    try {
      setLinkingError('');
      const response = await api.post('/api/calendar/booking-links', {
        ical_booking_id: prediction.ical_booking_id,
        manual_booking_id: prediction.manual_booking_id,
        uid: prediction.uid,
        confidence_score: prediction.confidence_score
      });
      
      console.log('[BOOKINGS] Match confirmed:', response.data);

      const rowKey = predictionLinkKey(prediction);
      setSelectedPredictionUids((prev) => {
        const next = new Set(prev);
        next.delete(rowKey);
        return next;
      });
      setPredictions((prev) => prev.filter((p) => predictionLinkKey(p) !== rowKey));
      await fetchConfirmedLinks();
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to confirm match';
      console.error('[BOOKINGS] Error confirming match:', err);
      setLinkingError(errorMsg);
    }
  };

  /** Persist rejected pair in booking_links so predictions exclude it. */
  const rejectMatch = async (prediction: any) => {
    if (
      !window.confirm(
        'Reject this suggested match? This iCal/manual pair will not appear as a prediction again.'
      )
    ) {
      return;
    }
    try {
      setLinkingError('');
      await api.post('/api/calendar/booking-links/bulk', {
        action: 'reject',
        items: [
          {
            ical_booking_id: prediction.ical_booking_id,
            manual_booking_id: prediction.manual_booking_id,
            uid: prediction.uid,
            confidence_score: prediction.confidence_score,
          },
        ],
      });
      setSelectedPredictionUids((prev) => {
        const next = new Set(prev);
        next.delete(predictionLinkKey(prediction));
        return next;
      });
      const rowKey = predictionLinkKey(prediction);
      setPredictions((prev) => prev.filter((p) => predictionLinkKey(p) !== rowKey));
      await fetchRejectedLinks();
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to reject match';
      console.error('[BOOKINGS] Error rejecting match:', err);
      setLinkingError(errorMsg);
    }
  };

  const togglePredictionSelected = (prediction: any) => {
    const key = predictionLinkKey(prediction);
    setSelectedPredictionUids((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAllPredictions = () => {
    setSelectedPredictionUids(new Set(filteredSortedPredictions.map((p) => predictionLinkKey(p))));
  };

  const deselectAllPredictions = () => {
    setSelectedPredictionUids(new Set());
  };

  const getSelectedPredictions = () =>
    linkingPredictions.filter((p) => selectedPredictionUids.has(predictionLinkKey(p)));

  const bulkConfirmPredictions = async () => {
    const sel = getSelectedPredictions();
    if (sel.length === 0) return;
    try {
      setBulkLinking(true);
      setLinkingError('');
      await api.post('/api/calendar/booking-links/bulk', {
        action: 'confirm',
        items: sel.map((p) => ({
          ical_booking_id: p.ical_booking_id,
          manual_booking_id: p.manual_booking_id,
          uid: p.uid,
          confidence_score: p.confidence_score,
        })),
      });
      deselectAllPredictions();
      const keys = new Set(sel.map((p) => predictionLinkKey(p)));
      setPredictions((prev) => prev.filter((p) => !keys.has(predictionLinkKey(p))));
      await fetchConfirmedLinks();
    } catch (err: any) {
      setLinkingError(err.response?.data?.error || 'Failed to confirm selected matches');
      console.error('[BOOKINGS] Bulk confirm:', err);
    } finally {
      setBulkLinking(false);
    }
  };

  const bulkRejectPredictions = async () => {
    const sel = getSelectedPredictions();
    if (sel.length === 0) return;
    try {
      setBulkLinking(true);
      setLinkingError('');
      await api.post('/api/calendar/booking-links/bulk', {
        action: 'reject',
        items: sel.map((p) => ({
          ical_booking_id: p.ical_booking_id,
          manual_booking_id: p.manual_booking_id,
          uid: p.uid,
          confidence_score: p.confidence_score,
        })),
      });
      deselectAllPredictions();
      const keys = new Set(sel.map((p) => predictionLinkKey(p)));
      setPredictions((prev) => prev.filter((p) => !keys.has(predictionLinkKey(p))));
      await fetchRejectedLinks();
    } catch (err: any) {
      setLinkingError(err.response?.data?.error || 'Failed to reject selected matches');
      console.error('[BOOKINGS] Bulk reject:', err);
    } finally {
      setBulkLinking(false);
    }
  };

  // Filter bookings for the Bookings list tab (uses date-filtered data)
  const getTabFilteredBookings = () => {
    if (tabValue !== 0) return dateFilteredBookings;
    // Bookings tab — iCal and/or Manual (incl. AirBnB Export), per combinedSources toggle
    const wantIcal = combinedSources.includes('iCal');
    const wantManual = combinedSources.includes('Manual');
    return dateFilteredBookings.filter((b) => {
      if (b.source === 'iCal') return wantIcal;
      if (isManualOrExportSource(b.source)) return wantManual;
      return false;
    });
  };

  const tabFilteredBookings = getTabFilteredBookings();

  const filteredBookings = tabFilteredBookings.filter(booking => {
    const focusedMatch =
      (focusBookingId == null || booking.id === focusBookingId) &&
      (!focusExternalBookingId || String(booking.external_booking_id || '').trim() === focusExternalBookingId) &&
      (!focusIcalUid || String((booking as Booking & { ical_uid?: string }).ical_uid || '').trim() === focusIcalUid);
    if (!focusedMatch) return false;
    return true;
  });

  // Enrich with semantic updates_count (booked_at-only churn excluded), Airbnb booking ref, lead time.
  const enrichedBookingsBase = filteredBookings.map(b => ({
    ...b,
    updates_count: getMeaningfulUpdatesCount(b.booking_history),
    booking_ref: airbnbConfirmationCodeFromBooking(b),
    lead_time: bookingLeadTimeDays(b),
  }));

  // Bookings list tab: one row for high-confidence confirmed iCal↔Manual pairs.
  const enrichedBookingsUnfiltered =
    tabValue === 0
      ? mergeHighConfidenceCombinedBookings(enrichedBookingsBase, confirmedLinks).map((b) => ({
          ...b,
          booking_ref: b.booking_ref || airbnbConfirmationCodeFromBooking(b),
          lead_time: b.lead_time ?? bookingLeadTimeDays(b),
          updates_count: b.updates_count ?? getMeaningfulUpdatesCount(b.booking_history),
        }))
      : enrichedBookingsBase;

  const ymd = (value?: string | null) => {
    if (!value) return '';
    const s = String(value);
    return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : s;
  };

  const enrichedBookings = enrichedBookingsUnfiltered.filter((booking) => {
    const propName = booking.property_nickname || `Property ${booking.property_id}`;
    const sourceValue = booking.is_merged_pair || booking.source === 'Merged' ? 'Merged' : booking.source;
    const sourceMatch = sourceFilters.length === 0 || sourceFilters.includes(sourceValue);
    // Merged rows also match if user selected the underlying Manual/Export/iCal chip.
    const sourceMatchLoose =
      sourceMatch ||
      (booking.is_merged_pair &&
        sourceFilters.some((s) => s === 'iCal' || s === 'Manual' || s === 'AirBnB Export' || s === 'VRBO Export'));
    const platformMatch = platformFilters.length === 0 || platformFilters.includes(booking.platform);
    const accountOwnerValue = String(booking.account_owner || '').trim() || 'Company';
    const accountOwnerMatch =
      accountOwnerFilters.length === 0 || accountOwnerFilters.includes(accountOwnerValue);
    const propertyMatch = propertyFilters.length === 0 || propertyFilters.includes(propName);
    const statusMatch =
      statusFilters.length === 0 || statusFilters.includes(String(booking.status || ''));
    if (hideCancelled && isCancelledBookingStatus(booking.status)) return false;
    if (hideNestingRemnants && nestingRemnantById.has(booking.id)) return false;

    const idMatch = bookingIdMatchesFilter(booking, idFilter);

    const refQ = bookingRefFilter.trim().toUpperCase();
    const refVal = String(booking.booking_ref || '').toUpperCase();
    const refMatch = !refQ || refVal.includes(refQ);

    const guestQ = guestFilter.trim().toLowerCase();
    const guestVal = String(booking.guest_name || '').toLowerCase();
    const guestMatch = !guestQ || guestVal.includes(guestQ);

    const cout = ymd(booking.check_out_date);
    if (checkOutFrom && (!cout || cout < checkOutFrom)) return false;
    if (checkOutTo && (!cout || cout > checkOutTo)) return false;

    const nights = Number(booking.nights);
    if (nightsMin !== '' && Number.isFinite(Number(nightsMin)) && !(Number.isFinite(nights) && nights >= Number(nightsMin))) {
      return false;
    }
    if (nightsMax !== '' && Number.isFinite(Number(nightsMax)) && !(Number.isFinite(nights) && nights <= Number(nightsMax))) {
      return false;
    }

    const lead = booking.lead_time;
    if (leadTimeMin !== '' && Number.isFinite(Number(leadTimeMin))) {
      if (lead == null || lead < Number(leadTimeMin)) return false;
    }
    if (leadTimeMax !== '' && Number.isFinite(Number(leadTimeMax))) {
      if (lead == null || lead > Number(leadTimeMax)) return false;
    }

    return sourceMatchLoose && platformMatch && accountOwnerMatch && propertyMatch && statusMatch && idMatch && refMatch && guestMatch;
  });

  // Bookings-tab charts use the same filtered rows as the table (account owner, platform, etc.).
  const combinedChartData = useMemo(
    () => buildStackedChartData(enrichedBookings, chartMonthMode, chartValueMode),
    [enrichedBookings, chartMonthMode, chartValueMode]
  );
  const combinedLeadTimeChart = useMemo(
    () =>
      buildLeadTimeChartData(
        enrichedBookings,
        chartMonthMode,
        monthKeyFromDateStr,
        bookingChartDateStr
      ),
    [enrichedBookings, chartMonthMode]
  );
  const combinedLeadBySizeChart = useMemo(
    () => buildLeadTimeByStaySizeChart(enrichedBookings),
    [enrichedBookings]
  );

  const removeSourceFilter = (source: string) => setSourceFilters(prev => prev.filter(s => s !== source));
  const removePlatformFilter = (platform: string) => setPlatformFilters(prev => prev.filter(p => p !== platform));
  const removeAccountOwnerFilter = (owner: string) =>
    setAccountOwnerFilters((prev) => prev.filter((o) => o !== owner));
  const removePropertyFilter = (property: string) => setPropertyFilters(prev => prev.filter(p => p !== property));
  const removeStatusFilter = (status: string) => setStatusFilters(prev => prev.filter(s => s !== status));
  const clearDateFilter = () => { setDateFrom(''); setDateTo(''); };
  const clearCheckOutFilter = () => { setCheckOutFrom(''); setCheckOutTo(''); };
  const clearMonthFilter = () => setSelectedMonthFilter(null);
  const clearAllColumnFilters = () => {
    setSourceFilters([]);
    setPlatformFilters([]);
    setAccountOwnerFilters([]);
    setPropertyFilters([]);
    setStatusFilters([]);
    setHideCancelled(false);
    setHideNestingRemnants(false);
    setIdFilter('');
    setBookingRefFilter('');
    setGuestFilter('');
    setNightsMin('');
    setNightsMax('');
    setLeadTimeMin('');
    setLeadTimeMax('');
    setCheckOutFrom('');
    setCheckOutTo('');
    setDateFrom('');
    setDateTo('');
    setSelectedMonthFilter(null);
  };

  const hasActiveColumnFilters =
    sourceFilters.length > 0 ||
    platformFilters.length > 0 ||
    accountOwnerFilters.length > 0 ||
    propertyFilters.length > 0 ||
    statusFilters.length > 0 ||
    hideCancelled ||
    hideNestingRemnants ||
    !!idFilter.trim() ||
    !!bookingRefFilter.trim() ||
    !!guestFilter.trim() ||
    nightsMin !== '' ||
    nightsMax !== '' ||
    leadTimeMin !== '' ||
    leadTimeMax !== '' ||
    !!dateFrom ||
    !!dateTo ||
    !!checkOutFrom ||
    !!checkOutTo ||
    !!selectedMonthFilter;

  const handleBarClick = (data: { monthKey?: string; month?: string }) => {
    if (data?.monthKey && data?.month) {
      setSelectedMonthFilter({ monthKey: data.monthKey, monthLabel: data.month });
    }
  };

  const filteredSortedBookingsForExport = useMemo(
    () => stableSort(enrichedBookings, getComparator(order, orderBy)),
    [enrichedBookings, order, orderBy]
  );

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };
  const handleExportMenuClose = () => setExportMenuAnchor(null);

  const handleExportXlsx = () => {
    handleExportMenuClose();
    if (!filteredSortedBookingsForExport.length) return;
    try {
      setExportingBookings(true);
      exportBookingsToXlsx(filteredSortedBookingsForExport);
      setToast(
        createToast(
          'success',
          `Exported ${filteredSortedBookingsForExport.length} stay${
            filteredSortedBookingsForExport.length === 1 ? '' : 's'
          } to Excel`
        )
      );
    } catch (err: any) {
      setToast(createToast('error', err?.message || 'Excel export failed'));
    } finally {
      setExportingBookings(false);
    }
  };

  const handleExportPdf = async () => {
    handleExportMenuClose();
    if (!filteredSortedBookingsForExport.length) return;
    try {
      setExportingBookings(true);
      await exportBookingsToPdf(filteredSortedBookingsForExport);
      setToast(
        createToast(
          'success',
          `Exported ${filteredSortedBookingsForExport.length} stay${
            filteredSortedBookingsForExport.length === 1 ? '' : 's'
          } to PDF`
        )
      );
    } catch (err: any) {
      setToast(createToast('error', err?.message || 'PDF export failed'));
    } finally {
      setExportingBookings(false);
    }
  };

  const SOURCE_OPTIONS = ['iCal', 'AirBnB Export', 'VRBO Export', 'Manual', 'Merged'];
  const PLATFORM_OPTIONS = ['Airbnb', 'VRBO', 'Booking.com', 'Manual'];
  const ACCOUNT_OWNER_OPTIONS = useMemo(
    () =>
      Array.from(
        new Set([
          'Company',
          'Niall Crotty',
          ...enrichedBookingsUnfiltered
            .map((b) => String(b.account_owner || '').trim())
            .filter(Boolean),
        ])
      ).sort((a, b) => a.localeCompare(b)),
    [enrichedBookingsUnfiltered]
  );

  const formatDate = formatBookingTableDate;
  const formatDateTime = formatBookingTableDateTime;
  const formatAmount = formatBookingTableAmount;

  const icalCount = dateFilteredBookings.filter(b => b.source === 'iCal').length;
  const manualCount = dateFilteredBookings.filter(b => isManualOrExportSource(b.source)).length;
  const bookingsCountText = (() => {
    if (tabValue === 1) {
      const parts: string[] = [];
      parts.push(`${linkingPredictions.length} predicted`);
      parts.push(`${confirmedLinks.length} confirmed`);
      parts.push(`${rejectedLinks.length} rejected`);
      parts.push(`${icalCount} iCal`);
      parts.push(`${manualCount} Manual`);
      return parts.join(' · ');
    }
    if (tabValue === 2 && revolutWorkspace) {
      const rows = revolutWorkspace.rows ?? [];
      let p = 0;
      let l = 0;
      let rj = 0;
      for (const r of rows) {
        const mb = r.manual_booking;
        const has = mb?.revolut_transaction_db_id != null;
        const decl = !!mb?.revolut_payment_declined;
        if (has) l += 1;
        else if (decl) rj += 1;
        else p += 1;
      }
      return `${rows.length} manual · ${p} pending · ${l} linked · ${rj} rejected`;
    }
    if (tabValue === 3) {
      return 'Airbnb guest communications';
    }
    if (loading && bookings.length === 0) {
      return '';
    }
    if (tabValue === 0) {
      const mergedPairs = enrichedBookings.filter((b) => b.is_merged_pair).length;
      const rowWord = enrichedBookings.length === 1 ? 'row' : 'rows';
      const base =
        enrichedBookingsUnfiltered.length === 0
          ? 'No bookings'
          : `Showing ${enrichedBookings.length} ${rowWord}`;
      if (mergedPairs > 0) {
        return `${base} (${mergedPairs} linked iCal+Manual pair${mergedPairs === 1 ? '' : 's'} merged)`;
      }
      if (hasActiveColumnFilters && enrichedBookings.length !== enrichedBookingsUnfiltered.length) {
        return `${base} of ${enrichedBookingsUnfiltered.length}`;
      }
      return base;
    }
    if (enrichedBookingsUnfiltered.length === 0 && tabFilteredBookings.length === 0) return 'No bookings';
    if (enrichedBookings.length === enrichedBookingsUnfiltered.length) {
      return `Showing ${enrichedBookings.length} bookings`;
    }
    return `Showing ${enrichedBookings.length} of ${enrichedBookingsUnfiltered.length} bookings`;
  })();

  const renderBookingsCountRow = () => {
    if (!bookingsCountText && tabValue !== 0) return null;
    const showGhostLegend =
      bookingsViewMode === 'table' &&
      tabValue === 0 &&
      (ghostCategoryById.size > 0 || nestingRemnantById.size > 0);
    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 auto', minWidth: 0 }}>
          {bookingsCountText}
        </Typography>
        {tabValue === 0 ? (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={bookingsViewMode}
            onChange={(_, next) => {
              if (next === 'table' || next === 'calendar') setBookingsViewModeAndUrl(next);
            }}
            aria-label="Bookings view mode"
            sx={{ flexShrink: 0 }}
          >
            <ToggleButton value="table" aria-label="Table view">
              <TableRowsIcon fontSize="small" sx={{ mr: 0.5 }} />
              Table
            </ToggleButton>
            <ToggleButton value="calendar" aria-label="Calendar view">
              <CalendarViewMonthIcon fontSize="small" sx={{ mr: 0.5 }} />
              Calendar
            </ToggleButton>
          </ToggleButtonGroup>
        ) : null}
        {showGhostLegend ? (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1.25,
              ml: { xs: 0, md: 'auto' },
            }}
          >
            {ghostCategoryById.size > 0 ? (
              <>
                <Typography variant="caption" color="text.secondary">
                  Stale iCal ghosts:
                </Typography>
                {STALE_ICAL_GHOST_LEGEND.map((item) => (
                  <Box
                    key={item.category}
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: 0.5,
                        bgcolor: item.swatch,
                        border: '1px solid rgba(0,0,0,0.12)',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </>
            ) : null}
            {nestingRemnantById.size > 0 ? (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: 0.5,
                    bgcolor: ICAL_NESTING_REMNANT_LEGEND.swatch,
                    border: '1px solid rgba(0,0,0,0.12)',
                    flexShrink: 0,
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {ICAL_NESTING_REMNANT_LEGEND.label}
                </Typography>
              </Box>
            ) : null}
          </Box>
        ) : null}
      </Box>
    );
  };

  const handleSelectAllFilteredBookings = useCallback(
    (checked: boolean) => {
      handleSelectAllVisibleBookings(filteredSortedBookingsForExport, checked);
    },
    [filteredSortedBookingsForExport, handleSelectAllVisibleBookings]
  );

  const renderEnrichedBookingsBody = (opts: {
    ariaLabel: string;
    emptyTitle: string;
    emptyHint: string;
  }) => {
    if (bookingsViewMode === 'calendar') {
      const calendarBookings = filterBookingsForStayCalendar(enrichedBookings, nestingRemnantById);
      return (
        <BookingsStayCalendar
          bookings={calendarBookings}
          onOpenBooking={(id) => navigate(`/bookings/${id}`)}
        />
      );
    }
    return (
      <BookingsDataTable
        ariaLabel={opts.ariaLabel}
        order={order}
        orderBy={orderBy}
        onRequestSort={handleRequestSort}
        bookings={filteredSortedBookingsForExport}
        ghostCategoryById={ghostCategoryById}
        nestingRemnantById={nestingRemnantById}
        selectedIds={selectedBookingIds}
        onToggleSelect={handleToggleSelectBooking}
        onSelectAllVisible={handleSelectAllFilteredBookings}
        loadingEmpty={loading && bookings.length === 0}
        emptyTitle={opts.emptyTitle}
        emptyHint={opts.emptyHint}
        formatDate={formatDate}
        formatAmount={formatAmount}
        onOpenComms={setCommsModalBooking}
      />
    );
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    console.log('[BOOKINGS] Tab changed to:', newValue);
    setTabValue(newValue);

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tabIndexToName[newValue] || 'bookings');
    setSearchParams(newSearchParams, { replace: true });

    // Reset filters when switching tabs
    clearAllColumnFilters();
    setSelectedBookingIds(new Set());
    // Linking data is loaded by useEffect when tabValue becomes 1
  };

  // RBAC — after all hooks so rules-of-hooks stays valid
  if (!user || (user.role !== 'admin' && !user.can_view_calendar)) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            📅 Bookings
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Access Restricted
            </Typography>
            <Typography variant="body2">
              You don't have permission to view the calendar and bookings. Please contact your administrator to request access.
            </Typography>
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Box>
      {loading && bookings.length === 0 && (
        <LinearProgress sx={{ mb: 0.5, borderRadius: 1 }} />
      )}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" component="h2">
              <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Bookings
            </Typography>
            <Box>
              {tabValue === 0 && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={
                      exportingBookings ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <DownloadIcon />
                      )
                    }
                    onClick={handleExportMenuOpen}
                    disabled={exportingBookings || filteredSortedBookingsForExport.length === 0}
                    sx={{ mr: 2 }}
                    title="Export currently filtered bookings"
                  >
                    Export
                  </Button>
                  <Menu
                    anchorEl={exportMenuAnchor}
                    open={Boolean(exportMenuAnchor)}
                    onClose={handleExportMenuClose}
                  >
                    <MenuItem onClick={handleExportXlsx}>Download as Excel (.xlsx)</MenuItem>
                    <MenuItem onClick={() => void handleExportPdf()}>Download as PDF</MenuItem>
                  </Menu>
                </>
              )}
              <Button
                variant="outlined"
                startIcon={<BarChartIcon />}
                onClick={() => setShowChart(!showChart)}
                sx={{ mr: 2 }}
              >
                {showChart ? 'Hide Chart' : 'Show Chart'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<SyncIcon />}
                onClick={handleSync}
                disabled={syncLoading}
                sx={{ mr: 2 }}
              >
                {syncLoading ? 'Syncing...' : 'Sync Calendar'}
              </Button>
              <Button
                variant="outlined"
                startIcon={importingFiles ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                onClick={handleImportFiles}
                disabled={importingFiles}
                color="info"
                sx={{ mr: 2 }}
                title="Airbnb or VRBO reservation CSVs, plus VRBO payout reports"
              >
                {importingFiles ? 'Importing...' : 'Import Files'}
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                color="primary"
              >
                Add Booking
              </Button>
            </Box>
          </Box>

          {importingFiles && (
            <Alert severity="info" sx={{ mb: 2 }} icon={<CircularProgress size={20} />}>
              Importing bookings... This may take a minute for large files.
            </Alert>
          )}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
              action={
                <Button color="inherit" size="small" onClick={() => { setError(''); fetchData(); }}>
                  Retry
                </Button>
              }
            >
              <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-line' }}>
                {error}
              </Typography>
            </Alert>
          )}

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="bookings tabs">
              <Tab label="Bookings" {...a11yProps(0)} />
              <Tab label="Linking" {...a11yProps(1)} />
              <Tab label="Payments" {...a11yProps(2)} />
              <Tab label="Comms" {...a11yProps(3)} />
            </Tabs>
          </Box>

          {/* Active filter chips - Bookings list tab only */}
          {tabValue === 0 && hasActiveColumnFilters && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
              {idFilter.trim() && (
                <Chip size="small" label={`ID: ${idFilter.trim()}`} onDelete={() => setIdFilter('')} deleteIcon={<CloseIcon />} />
              )}
              {bookingRefFilter.trim() && (
                <Chip size="small" label={`Ref: ${bookingRefFilter.trim()}`} onDelete={() => setBookingRefFilter('')} deleteIcon={<CloseIcon />} color="primary" />
              )}
              {guestFilter.trim() && (
                <Chip size="small" label={`Guest: ${guestFilter.trim()}`} onDelete={() => setGuestFilter('')} deleteIcon={<CloseIcon />} />
              )}
              {sourceFilters.map((s) => (
                <Chip
                  key={`source-${s}`}
                  label={`Source: ${s}`}
                  size="small"
                  onDelete={() => removeSourceFilter(s)}
                  deleteIcon={<CloseIcon />}
                  color="info"
                  variant="filled"
                />
              ))}
              {platformFilters.map((p) => (
                <Chip
                  key={`platform-${p}`}
                  label={`Platform: ${p}`}
                  size="small"
                  onDelete={() => removePlatformFilter(p)}
                  deleteIcon={<CloseIcon />}
                  color="primary"
                  variant="filled"
                />
              ))}
              {accountOwnerFilters.map((o) => (
                <Chip
                  key={`account-owner-${o}`}
                  label={`Account owner: ${o}`}
                  size="small"
                  onDelete={() => removeAccountOwnerFilter(o)}
                  deleteIcon={<CloseIcon />}
                  color="primary"
                  variant="outlined"
                />
              ))}
              {propertyFilters.map((p) => (
                <Chip
                  key={`property-${p}`}
                  label={`Property: ${p}`}
                  size="small"
                  onDelete={() => removePropertyFilter(p)}
                  deleteIcon={<CloseIcon />}
                  color="secondary"
                  variant="filled"
                />
              ))}
              {statusFilters.map((s) => (
                <Chip
                  key={`status-${s}`}
                  label={`Status: ${s}`}
                  size="small"
                  onDelete={() => removeStatusFilter(s)}
                  deleteIcon={<CloseIcon />}
                  color="warning"
                  variant="filled"
                />
              ))}
              {hideCancelled && (
                <Chip
                  key="hide-cancelled"
                  label="Hide cancelled"
                  size="small"
                  onDelete={() => setHideCancelled(false)}
                  deleteIcon={<CloseIcon />}
                  color="error"
                  variant="filled"
                />
              )}
              {hideNestingRemnants && (
                <Chip
                  key="hide-nesting"
                  label="Hide nesting remnants"
                  size="small"
                  onDelete={() => setHideNestingRemnants(false)}
                  deleteIcon={<CloseIcon />}
                  color="default"
                  variant="filled"
                />
              )}
              {(dateFrom || dateTo) && (
                <Chip
                  key="date-range"
                  label={`Check-in: ${dateFrom || '…'} – ${dateTo || '…'}`}
                  size="small"
                  onDelete={clearDateFilter}
                  deleteIcon={<CloseIcon />}
                  color="success"
                  variant="filled"
                />
              )}
              {(checkOutFrom || checkOutTo) && (
                <Chip
                  key="checkout-range"
                  label={`Check-out: ${checkOutFrom || '…'} – ${checkOutTo || '…'}`}
                  size="small"
                  onDelete={clearCheckOutFilter}
                  deleteIcon={<CloseIcon />}
                  color="success"
                  variant="filled"
                />
              )}
              {(nightsMin !== '' || nightsMax !== '') && (
                <Chip
                  size="small"
                  label={`Nights: ${nightsMin || '…'}–${nightsMax || '…'}`}
                  onDelete={() => { setNightsMin(''); setNightsMax(''); }}
                  deleteIcon={<CloseIcon />}
                />
              )}
              {(leadTimeMin !== '' || leadTimeMax !== '') && (
                <Chip
                  size="small"
                  label={`Lead time: ${leadTimeMin || '…'}–${leadTimeMax || '…'}d`}
                  onDelete={() => { setLeadTimeMin(''); setLeadTimeMax(''); }}
                  deleteIcon={<CloseIcon />}
                />
              )}
              {selectedMonthFilter && (
                <Chip
                  key="month"
                  label={`${chartMonthMode === 'booked' ? 'Booked' : 'Check-in'} month: ${selectedMonthFilter.monthLabel}`}
                  size="small"
                  onDelete={clearMonthFilter}
                  deleteIcon={<CloseIcon />}
                  color="success"
                  variant="filled"
                />
              )}
              <Button size="small" onClick={clearAllColumnFilters}>
                Clear all
              </Button>
            </Box>
          )}

          {/* Column filters — accordion on Bookings list tab */}
          {tabValue === 0 && (
            <Accordion
              expanded={showFilters}
              onChange={(_, expanded) => {
                // Avoid re-blocking the UI while the large bookings table re-renders.
                startTransition(() => setShowFilters(expanded));
              }}
              variant="outlined"
              sx={{ mb: 0.5 }}
              slotProps={{
                transition: {
                  // Instant open/close — Collapse height animation felt slow with many Autocompletes.
                  timeout: 0,
                  unmountOnExit: true,
                },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <FilterListIcon fontSize="small" color="action" />
                  <Typography variant="subtitle1" fontWeight="medium">
                    Filters
                  </Typography>
                  {hasActiveColumnFilters && (
                    <Chip
                      size="small"
                      label="Active"
                      color="primary"
                      variant="outlined"
                      onClick={(e) => e.stopPropagation()}
                      onDelete={(e) => {
                        e.stopPropagation();
                        clearAllColumnFilters();
                      }}
                      deleteIcon={<CloseIcon />}
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {tabValue === 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 1.5,
                      mb: 2,
                      pb: 2,
                      borderBottom: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Show sources
                    </Typography>
                    <ToggleButtonGroup
                      size="small"
                      value={combinedSources}
                      onChange={(_, next: Array<'iCal' | 'Manual'>) => {
                        if (!next || next.length === 0) return;
                        setCombinedSources(next);
                      }}
                      aria-label="Bookings source filter"
                    >
                      <ToggleButton value="iCal">iCal</ToggleButton>
                      <ToggleButton value="Manual">Manual</ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant="caption" color="text.secondary" sx={{ flex: '1 1 220px' }}>
                      Manual includes AirBnB Export and VRBO Export. Confirmed same-booking pairs (confirmation code or
                      user confirm) show as one row. Use Source below for finer filtering (Export /
                      Merged).
                    </Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr 1fr',
                      sm: 'repeat(3, 1fr)',
                      md: 'repeat(4, 1fr)',
                      lg: 'repeat(6, 1fr)',
                    },
                    gap: 1.25,
                    alignItems: 'start',
                  }}
                >
                  <TextField
                    size="small"
                    label="ID"
                    placeholder="I / E / M + number"
                    value={idFilter}
                    onChange={(e) => setIdFilter(e.target.value)}
                    fullWidth
                  />
                  <Autocomplete
                    multiple
                    size="small"
                    options={uniqueProperties}
                    value={propertyFilters}
                    onChange={(_, v) => setPropertyFilters(v)}
                    renderInput={(params) => <TextField {...params} label="Property" />}
                    limitTags={1}
                  />
                  <Autocomplete
                    multiple
                    size="small"
                    options={PLATFORM_OPTIONS}
                    value={platformFilters}
                    onChange={(_, v) => setPlatformFilters(v)}
                    renderInput={(params) => <TextField {...params} label="Platform" />}
                    limitTags={1}
                  />
                  <Autocomplete
                    multiple
                    size="small"
                    options={ACCOUNT_OWNER_OPTIONS}
                    value={accountOwnerFilters}
                    onChange={(_, v) => setAccountOwnerFilters(v)}
                    renderInput={(params) => <TextField {...params} label="Account owner" />}
                    limitTags={1}
                  />
                  <TextField
                    size="small"
                    label="Booking Ref"
                    value={bookingRefFilter}
                    onChange={(e) => setBookingRefFilter(e.target.value)}
                    fullWidth
                  />
                  <Autocomplete
                    multiple
                    size="small"
                    options={SOURCE_OPTIONS}
                    value={sourceFilters}
                    onChange={(_, v) => setSourceFilters(v)}
                    renderInput={(params) => <TextField {...params} label="Source" />}
                    limitTags={1}
                  />
                  <TextField
                    size="small"
                    label="Guest"
                    value={guestFilter}
                    onChange={(e) => setGuestFilter(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="Check-in from"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="Check-in to"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="Check-out from"
                    value={checkOutFrom}
                    onChange={(e) => setCheckOutFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="Check-out to"
                    value={checkOutTo}
                    onChange={(e) => setCheckOutTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="Nights min"
                    value={nightsMin}
                    onChange={(e) => setNightsMin(e.target.value)}
                    inputProps={{ min: 0 }}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="Nights max"
                    value={nightsMax}
                    onChange={(e) => setNightsMax(e.target.value)}
                    inputProps={{ min: 0 }}
                    fullWidth
                  />
                  <Autocomplete
                    multiple
                    size="small"
                    options={uniqueStatuses}
                    value={statusFilters}
                    onChange={(_, v) => setStatusFilters(v)}
                    renderInput={(params) => <TextField {...params} label="Status" />}
                    limitTags={1}
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="Lead time min"
                    value={leadTimeMin}
                    onChange={(e) => setLeadTimeMin(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="Lead time max"
                    value={leadTimeMax}
                    onChange={(e) => setLeadTimeMax(e.target.value)}
                    fullWidth
                  />
                </Box>
                <FormControlLabel
                  sx={{ mt: 1, ml: 0.25 }}
                  control={
                    <Switch
                      size="small"
                      checked={hideCancelled}
                      onChange={(_, checked) => setHideCancelled(checked)}
                      inputProps={{ 'aria-label': 'Hide cancelled bookings' }}
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">
                      Hide cancelled bookings
                    </Typography>
                  }
                />
                <FormControlLabel
                  sx={{ mt: 0.5, ml: 0.25 }}
                  control={
                    <Switch
                      size="small"
                      checked={hideNestingRemnants}
                      onChange={(_, checked) => setHideNestingRemnants(checked)}
                      inputProps={{ 'aria-label': 'Hide nesting remnant bookings' }}
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">
                      Hide nesting remnants (N)
                      {nestingRemnantById.size > 0 ? ` · ${nestingRemnantById.size} flagged` : ''}
                    </Typography>
                  }
                />
                {hasActiveColumnFilters && (
                  <Box sx={{ mt: 1.5 }}>
                    <Button size="small" onClick={clearAllColumnFilters}>
                      Clear all filters
                    </Button>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          )}

          {tabValue === 0 && selectedBookingIds.size > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1.5,
                mb: 0.5,
                mt: 0,
                p: 1.25,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'action.hover',
              }}
            >
              <Typography variant="body2">
                {selectedBookingIds.size} row{selectedBookingIds.size === 1 ? '' : 's'} selected
                {(() => {
                  const expanded = resolveSelectedBookingIdsForDelete(enrichedBookings).length;
                  return expanded !== selectedBookingIds.size
                    ? ` (${expanded} booking IDs including merged pairs)`
                    : '';
                })()}
              </Typography>
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={() => setBulkDeleteDialogOpen(true)}
              >
                Delete selected
              </Button>
              <Button size="small" onClick={() => setSelectedBookingIds(new Set())}>
                Clear selection
              </Button>
            </Box>
          )}


          {/* Tab Panels */}


          <TabPanel value={tabValue} index={0}>
            <Accordion
              expanded={showChart}
              onChange={(_, expanded) => setShowChart(expanded)}
              variant="outlined"
              sx={{ mb: 1.5, mt: 0 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BarChartIcon fontSize="small" color="action" />
                  <Typography variant="subtitle1" fontWeight="medium">
                    Chart
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="medium">
                    {chartMonthModeTitle}
                  </Typography>
                  {chartControls}
                </Box>
                {chartValueMode === 'lead_by_size' ? (
                  renderLeadBySizeChart(combinedLeadBySizeChart)
                ) : combinedChartData.data.length > 0 ? (
                  <Box sx={{ width: '100%', height: 300 }}>
                    <ResponsiveContainer>
                      <BarChart data={combinedChartData.data} margin={{ top: 8, right: 20, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 12 }} />
                        <YAxis {...chartYAxisProps} />
                        <RechartsTooltip formatter={chartTooltipFormatter} />
                        <Legend />
                        {combinedChartData.seriesKeys.map((series) => (
                          <Bar
                            key={series.key}
                            dataKey={series.key}
                            stackId={chartBarStackId}
                            fill={getSeriesChartColor(series, combinedChartData.propertyKeys)}
                            name={series.key}
                            onClick={(e: any) => e?.payload && handleBarClick(e.payload)}
                            cursor="pointer"
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                ) : loading && bookings.length === 0 ? (
                  <Box sx={{ height: 120 }} />
                ) : (
                  <Alert severity="info" sx={{ py: 2 }}>No bookings to display for the selected sources.</Alert>
                )}
                {chartValueMode !== 'lead_by_size' ? renderLeadTimeCharts(combinedLeadTimeChart) : null}
              </AccordionDetails>
            </Accordion>
            {renderBookingsCountRow()}
            {renderEnrichedBookingsBody({
              ariaLabel: 'combined bookings table',
              emptyTitle: 'No bookings found',
              emptyHint: 'Adjust the source filter above, or sync calendar / import files.',
            })}
          </TabPanel>

          {/* Linking Tab */}
          <TabPanel value={tabValue} index={1}>
            {linkingError && (
              <Alert severity="error" sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}>
                <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-line' }}>
                  {linkingError}
                </Typography>
              </Alert>
            )}

            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Prediction scope
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Set which bookings to scan, then run the matcher. There is no iCal data before{' '}
                <strong>1 Nov 2025</strong>, so matching for earlier check-ins usually adds little (manual bookings may
                still be in range)—you can still choose any <strong>Check-in from</strong> you want. Narrow by dates and
                property as needed. Results are cached until booking data or links change.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
                <TextField
                  label="Check-in from"
                  type="date"
                  size="small"
                  value={predictionScopeMin}
                  onChange={(e) => setPredictionScopeMin(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Check-in to (optional)"
                  type="date"
                  size="small"
                  value={predictionScopeMax}
                  onChange={(e) => setPredictionScopeMax(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  {/* Static caption avoids MUI Select + InputLabel + renderValue + empty value overlap / notch glitch */}
                  <Typography
                    id="prediction-scope-property-label"
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.5, lineHeight: 1.2 }}
                  >
                    Property
                  </Typography>
                  <Select
                    aria-labelledby="prediction-scope-property-label"
                    value={predictionScopePropertyId || ''}
                    displayEmpty
                    onChange={(e) => setPredictionScopePropertyId(e.target.value as string)}
                    renderValue={(selected) => {
                      if (selected === '' || selected == null) {
                        return 'All Properties';
                      }
                      const found = linkingPropertyOptions.find(([pid]) => String(pid) === String(selected));
                      return found ? found[1] : `Property #${selected}`;
                    }}
                  >
                    <MenuItem value="">All Properties</MenuItem>
                    {linkingPropertyOptions.map(([pid, label]) => (
                      <MenuItem key={pid} value={String(pid)}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  disabled={loadingPredictions}
                  onClick={() => void fetchPredictions()}
                >
                  Run predictions
                </Button>
              </Box>
            </Paper>

            {/* Linking tab: two-series chart */}
            <Accordion
              expanded={showChart}
              onChange={(_, expanded) => setShowChart(expanded)}
              variant="outlined"
              sx={{ mb: 1.5, mt: 0 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BarChartIcon fontSize="small" color="action" />
                  <Typography variant="subtitle1" fontWeight="medium">
                    Chart
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="medium">
                    {chartValueMode === 'lead_by_size'
                      ? 'Lead time by booking size (avg lead days + lead÷nights ratio)'
                      : chartValueMode === 'occupancy'
                      ? 'Occupancy % by calendar month (by property; overlapping stays merged)'
                      : chartValueMode === 'amount'
                        ? chartMonthMode === 'booked'
                          ? 'Payout amount made per month (iCal vs Manual)'
                          : 'Payout amount by check-in month (iCal vs Manual)'
                        : chartMonthMode === 'booked'
                          ? 'Bookings made per month (iCal vs Manual)'
                          : 'Bookings by check-in month (iCal vs Manual)'}
                  </Typography>
                  {chartControls}
                </Box>
                {chartValueMode === 'lead_by_size' ? (
                  renderLeadBySizeChart(linkingLeadBySizeChart)
                ) : chartValueMode === 'occupancy' ? (
                  linkingOccupancyChartData.data.length > 0 ? (
                    <Box sx={{ width: '100%', height: 360 }}>
                      <ResponsiveContainer>
                        <BarChart
                          data={linkingOccupancyChartData.data}
                          margin={{ top: 8, right: 20, left: 0, bottom: 8 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 12 }} />
                          <YAxis {...chartYAxisProps} />
                          <RechartsTooltip formatter={chartTooltipFormatter} />
                          <Legend />
                          {linkingOccupancyChartData.seriesKeys.map((series) => (
                            <Bar
                              key={series.key}
                              dataKey={series.key}
                              fill={getSeriesChartColor(series, linkingOccupancyChartData.propertyKeys)}
                              name={series.key}
                              onClick={(e: any) => e?.payload && handleBarClick(e.payload)}
                              cursor="pointer"
                            />
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : (
                    <Alert severity="info">No occupancy data to display.</Alert>
                  )
                ) : linkingChartData.length > 0 ? (
                  <Box sx={{ width: '100%', height: 360 }}>
                    <ResponsiveContainer>
                      <BarChart data={linkingChartData} margin={{ top: 8, right: 20, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 12 }} />
                        <YAxis {...chartYAxisProps} />
                        <RechartsTooltip formatter={chartTooltipFormatter} />
                        <Legend />
                        <Bar dataKey="iCal" fill="#1976d2" name="iCal (Airbnb/VRBO)" onClick={(e: any) => e?.payload && handleBarClick(e.payload)} cursor="pointer" />
                        <Bar dataKey="Manual" fill="#2e7d32" name="Manual" onClick={(e: any) => e?.payload && handleBarClick(e.payload)} cursor="pointer" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Alert severity="info">
                    No booking data to display. Sync calendar or add manual bookings.
                  </Alert>
                )}
              </AccordionDetails>
            </Accordion>
            {bookingsCountText && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {bookingsCountText}
              </Typography>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Predictions Section */}
            <Accordion defaultExpanded variant="outlined" sx={{ mb: 2 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="predicted-matches-content"
                id="predicted-matches-header"
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    width: '100%',
                    pr: 1,
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" component="div">
                      Predicted Matches
                      {predictionsQueryCompleted ? ` (${linkingPredictions.length})` : ''}
                    </Typography>
                    {predictionScopeApplied && (
                      <Typography variant="caption" color="text.secondary" component="div">
                        Last run: check-in{' '}
                        {predictionScopeApplied.min_check_in}
                        {predictionScopeApplied.max_check_in
                          ? ` → ${predictionScopeApplied.max_check_in}`
                          : ' (no end date)'}
                        {predictionScopeApplied.property_id != null
                          ? ` · property #${predictionScopeApplied.property_id}`
                          : ' · all properties'}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails id="predicted-matches-content" sx={{ pt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  {linkingPredictions.length > 0 && (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={selectAllPredictions}
                        disabled={
                          loadingPredictions || bulkLinking || filteredSortedPredictions.length === 0
                        }
                      >
                        Select all
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={deselectAllPredictions}
                        disabled={loadingPredictions || bulkLinking || selectedPredictionUids.size === 0}
                      >
                        Deselect all
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        disabled={loadingPredictions || bulkLinking || selectedPredictionUids.size === 0}
                        onClick={() => void bulkConfirmPredictions()}
                      >
                        Accept selected ({selectedPredictionUids.size})
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        disabled={loadingPredictions || bulkLinking || selectedPredictionUids.size === 0}
                        onClick={() => void bulkRejectPredictions()}
                      >
                        Reject selected ({selectedPredictionUids.size})
                      </Button>
                    </>
                  )}
                <Tooltip title="Bypass server cache and recompute (same scope)">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={loadingPredictions ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
                    onClick={() => {
                      void (async () => {
                        await fetchPredictions({ refresh: true });
                        await fetchConfirmedLinks();
                        await fetchRejectedLinks();
                      })();
                    }}
                    disabled={loadingPredictions}
                  >
                    {loadingPredictions ? 'Loading…' : 'Force refresh'}
                  </Button>
                </Tooltip>
                </Box>
              </Box>
              {predictionsQueryCompleted && linkingPredictions.length > 0 && !loadingPredictions && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>Match filters:</strong> show only rows with <strong>Yes</strong> on{' '}
                    <em>all</em> toggled criteria (Property, Check-in, Check-out, Guest, Nights).
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                    <ToggleButtonGroup
                      value={predictionYesRequiredKeys}
                      exclusive={false}
                      onChange={(_, v) =>
                        setPredictionYesRequiredKeys((v as PredictionMatchFilterKey[] | null) ?? [])
                      }
                      size="small"
                      aria-label="Require Yes for match columns"
                    >
                      <ToggleButton value="sameProperty" aria-label="Same property">
                        Property
                      </ToggleButton>
                      <ToggleButton value="sameCheckIn" aria-label="Same check-in">
                        Check-in
                      </ToggleButton>
                      <ToggleButton value="sameCheckOut" aria-label="Same check-out">
                        Check-out
                      </ToggleButton>
                      <ToggleButton value="sameGuest" aria-label="Same guest">
                        Guest
                      </ToggleButton>
                      <ToggleButton value="sameNights" aria-label="Same nights">
                        Nights
                      </ToggleButton>
                    </ToggleButtonGroup>
                    {predictionYesRequiredKeys.length > 0 && (
                      <Button size="small" onClick={() => setPredictionYesRequiredKeys([])}>
                        Clear filters
                      </Button>
                    )}
                    <Typography variant="caption" color="text.secondary" component="span">
                      Showing {filteredSortedPredictions.length} of {linkingPredictions.length}
                    </Typography>
                  </Box>
                </Box>
              )}
              {predictionsQueryCompleted &&
                linkingPredictions.length > 0 &&
                !scopeMatchesLastPredictionRun && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Form scope differs from the last prediction run. Click <strong>Run predictions</strong> above to
                    refresh the list, or <strong>Force refresh</strong> to bypass cache.
                  </Alert>
                )}
              {loadingPredictions ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 1.5 }}>
                  <CircularProgress />
                  {predictionsAttemptLine && (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 640 }}>
                      {predictionsAttemptLine}
                    </Typography>
                  )}
                  <BackendReachabilityAlert banner={backendReachabilityBanner} healthUrl={getApiHealthUrl()} />
                </Box>
              ) : !predictionsQueryCompleted ? (
                <Alert severity="info">
                  Set <strong>Prediction scope</strong> above and click <strong>Run predictions</strong> to load suggested
                  iCal/manual pairs for review.
                </Alert>
              ) : linkingPredictions.length === 0 ? (
                <Alert severity="info">
                  No predicted matches for this scope. You need both <strong>iCal</strong> and <strong>Manual</strong> (or
                  export) bookings in range, or pairs may already be confirmed/rejected.
                </Alert>
              ) : filteredSortedPredictions.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No rows match the current match filters ({linkingPredictions.length}{' '}
                  {linkingPredictions.length === 1 ? 'row' : 'rows'} hidden). Turn off toggles or tap <strong>Clear filters</strong>.
                </Alert>
              ) : (
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ width: 48 }}>
                          <Checkbox
                            indeterminate={
                              filteredSortedPredictions.some((p) =>
                                selectedPredictionUids.has(predictionLinkKey(p))
                              ) &&
                              !filteredSortedPredictions.every((p) =>
                                selectedPredictionUids.has(predictionLinkKey(p))
                              )
                            }
                            checked={
                              filteredSortedPredictions.length > 0 &&
                              filteredSortedPredictions.every((p) =>
                                selectedPredictionUids.has(predictionLinkKey(p))
                              )
                            }
                            onChange={() => {
                              if (
                                filteredSortedPredictions.length > 0 &&
                                filteredSortedPredictions.every((p) =>
                                  selectedPredictionUids.has(predictionLinkKey(p))
                                )
                              ) {
                                deselectAllPredictions();
                              } else {
                                selectAllPredictions();
                              }
                            }}
                            disabled={bulkLinking}
                          />
                        </TableCell>
                        <TableCell
                          sortDirection={predictionSortBy === 'confidence' ? predictionSortOrder : false}
                          sx={{
                            width: 118,
                            minWidth: 118,
                            maxWidth: 118,
                            verticalAlign: 'bottom',
                            textAlign: 'center',
                            lineHeight: 1.2,
                            px: 0.5,
                          }}
                        >
                          <TableSortLabel
                            active={predictionSortBy === 'confidence'}
                            direction={predictionSortBy === 'confidence' ? predictionSortOrder : 'asc'}
                            onClick={() => handlePredictionRequestSort('confidence')}
                            sx={{
                              whiteSpace: 'nowrap',
                              flexDirection: 'column',
                              alignItems: 'center',
                              '& .MuiTableSortLabel-icon': { marginLeft: '0 !important', marginTop: 0.25 },
                            }}
                          >
                            Confidence
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          sortDirection={predictionSortBy === 'sameProperty' ? predictionSortOrder : false}
                          sx={{
                            maxWidth: 58,
                            width: 58,
                            minWidth: 52,
                            whiteSpace: 'normal',
                            verticalAlign: 'bottom',
                            textAlign: 'center',
                            lineHeight: 1.15,
                            px: 0.25,
                          }}
                        >
                          <TableSortLabel
                            active={predictionSortBy === 'sameProperty'}
                            direction={predictionSortBy === 'sameProperty' ? predictionSortOrder : 'asc'}
                            onClick={() => handlePredictionRequestSort('sameProperty')}
                            sx={{
                              whiteSpace: 'normal',
                              flexDirection: 'column',
                              alignItems: 'center',
                              '& .MuiTableSortLabel-icon': { marginLeft: '0 !important', marginTop: 0.25 },
                            }}
                          >
                            Same
                            <br />
                            property
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          sortDirection={predictionSortBy === 'sameCheckIn' ? predictionSortOrder : false}
                          sx={{
                            maxWidth: 58,
                            width: 58,
                            minWidth: 52,
                            whiteSpace: 'normal',
                            verticalAlign: 'bottom',
                            textAlign: 'center',
                            lineHeight: 1.15,
                            px: 0.25,
                          }}
                        >
                          <TableSortLabel
                            active={predictionSortBy === 'sameCheckIn'}
                            direction={predictionSortBy === 'sameCheckIn' ? predictionSortOrder : 'asc'}
                            onClick={() => handlePredictionRequestSort('sameCheckIn')}
                            sx={{
                              whiteSpace: 'normal',
                              flexDirection: 'column',
                              alignItems: 'center',
                              '& .MuiTableSortLabel-icon': { marginLeft: '0 !important', marginTop: 0.25 },
                            }}
                          >
                            Same
                            <br />
                            check-in
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          sortDirection={predictionSortBy === 'sameCheckOut' ? predictionSortOrder : false}
                          sx={{
                            maxWidth: 58,
                            width: 58,
                            minWidth: 52,
                            whiteSpace: 'normal',
                            verticalAlign: 'bottom',
                            textAlign: 'center',
                            lineHeight: 1.15,
                            px: 0.25,
                          }}
                        >
                          <TableSortLabel
                            active={predictionSortBy === 'sameCheckOut'}
                            direction={predictionSortBy === 'sameCheckOut' ? predictionSortOrder : 'asc'}
                            onClick={() => handlePredictionRequestSort('sameCheckOut')}
                            sx={{
                              whiteSpace: 'normal',
                              flexDirection: 'column',
                              alignItems: 'center',
                              '& .MuiTableSortLabel-icon': { marginLeft: '0 !important', marginTop: 0.25 },
                            }}
                          >
                            Same
                            <br />
                            check-out
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          sortDirection={predictionSortBy === 'sameGuest' ? predictionSortOrder : false}
                          sx={{
                            maxWidth: 58,
                            width: 58,
                            minWidth: 52,
                            whiteSpace: 'normal',
                            verticalAlign: 'bottom',
                            textAlign: 'center',
                            lineHeight: 1.15,
                            px: 0.25,
                          }}
                        >
                          <TableSortLabel
                            active={predictionSortBy === 'sameGuest'}
                            direction={predictionSortBy === 'sameGuest' ? predictionSortOrder : 'asc'}
                            onClick={() => handlePredictionRequestSort('sameGuest')}
                            sx={{
                              whiteSpace: 'normal',
                              flexDirection: 'column',
                              alignItems: 'center',
                              '& .MuiTableSortLabel-icon': { marginLeft: '0 !important', marginTop: 0.25 },
                            }}
                          >
                            Same
                            <br />
                            guest
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          sortDirection={predictionSortBy === 'sameNights' ? predictionSortOrder : false}
                          sx={{
                            maxWidth: 58,
                            width: 58,
                            minWidth: 52,
                            whiteSpace: 'normal',
                            verticalAlign: 'bottom',
                            textAlign: 'center',
                            lineHeight: 1.15,
                            px: 0.25,
                          }}
                        >
                          <TableSortLabel
                            active={predictionSortBy === 'sameNights'}
                            direction={predictionSortBy === 'sameNights' ? predictionSortOrder : 'asc'}
                            onClick={() => handlePredictionRequestSort('sameNights')}
                            sx={{
                              whiteSpace: 'normal',
                              flexDirection: 'column',
                              alignItems: 'center',
                              '& .MuiTableSortLabel-icon': { marginLeft: '0 !important', marginTop: 0.25 },
                            }}
                          >
                            Same
                            <br />
                            nights
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          sortDirection={predictionSortBy === 'icalCheckIn' ? predictionSortOrder : false}
                          sx={{
                            width: '30%',
                            verticalAlign: 'bottom',
                            lineHeight: 1.15,
                            pr: 0.75,
                            pl: 0.5,
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                          }}
                        >
                          <TableSortLabel
                            active={predictionSortBy === 'icalCheckIn'}
                            direction={predictionSortBy === 'icalCheckIn' ? predictionSortOrder : 'asc'}
                            onClick={() => handlePredictionRequestSort('icalCheckIn')}
                            sx={{
                              whiteSpace: 'normal',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              '& .MuiTableSortLabel-icon': { marginLeft: '0 !important', marginTop: 0.25, alignSelf: 'center' },
                            }}
                          >
                            iCal
                            <br />
                            booking
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          sortDirection={predictionSortBy === 'manualCheckIn' ? predictionSortOrder : false}
                          sx={{
                            width: '30%',
                            verticalAlign: 'bottom',
                            lineHeight: 1.15,
                            pl: 0.75,
                            pr: 0.5,
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                          }}
                        >
                          <TableSortLabel
                            active={predictionSortBy === 'manualCheckIn'}
                            direction={predictionSortBy === 'manualCheckIn' ? predictionSortOrder : 'asc'}
                            onClick={() => handlePredictionRequestSort('manualCheckIn')}
                            sx={{
                              whiteSpace: 'normal',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              '& .MuiTableSortLabel-icon': { marginLeft: '0 !important', marginTop: 0.25, alignSelf: 'center' },
                            }}
                          >
                            Manual
                            <br />
                            booking
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          sx={{
                            width: 120,
                            minWidth: 120,
                            whiteSpace: 'normal',
                            verticalAlign: 'bottom',
                            textAlign: 'center',
                            lineHeight: 1.15,
                            px: 0.5,
                          }}
                        >
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredSortedPredictions.map((prediction) => {
                        const flags = computePredictionReasonFlags(
                          prediction.ical_booking,
                          prediction.manual_booking
                        );
                        return (
                        <TableRow key={predictionLinkKey(prediction)}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedPredictionUids.has(predictionLinkKey(prediction))}
                              onChange={() => togglePredictionSelected(prediction)}
                              disabled={bulkLinking}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ width: 118, maxWidth: 118 }}>
                            <Chip 
                              label={`${(prediction.confidence_score * 100).toFixed(0)}%`}
                              color={prediction.confidence_score >= 0.7 ? 'success' : prediction.confidence_score >= 0.5 ? 'warning' : 'default'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <ReasonYesNo ok={flags.sameProperty} />
                          </TableCell>
                          <TableCell align="center">
                            <ReasonYesNo ok={flags.sameCheckIn} />
                          </TableCell>
                          <TableCell align="center">
                            <ReasonYesNo ok={flags.sameCheckOut} />
                          </TableCell>
                          <TableCell align="center">
                            <ReasonYesNo ok={flags.sameGuestName} />
                          </TableCell>
                          <TableCell align="center">
                            <ReasonYesNo ok={flags.sameNights} />
                          </TableCell>
                          <TableCell
                            sx={{
                              width: '30%',
                              verticalAlign: 'top',
                              pr: 0.75,
                              pl: 0.5,
                              wordBreak: 'break-word',
                              overflowWrap: 'anywhere',
                            }}
                          >
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                              <Typography variant="body2" fontWeight="medium" component="span">
                                {prediction.ical_booking.property_nickname || `Property ${prediction.ical_booking.property_id}`}
                              </Typography>
                              {isCancelledBookingStatus(prediction.ical_booking.status) && (
                                <Chip
                                  size="small"
                                  label={String(prediction.ical_booking.status ?? 'Cancelled').trim() || 'Cancelled'}
                                  color="error"
                                  variant="outlined"
                                  sx={{
                                    height: 'auto',
                                    minHeight: 22,
                                    maxWidth: '100%',
                                    '& .MuiChip-label': {
                                      whiteSpace: 'normal',
                                      py: 0.25,
                                      display: 'block',
                                      textAlign: 'left',
                                    },
                                  }}
                                />
                              )}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(prediction.ical_booking.check_in_date)} - {formatDate(prediction.ical_booking.check_out_date)}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              Guest: {prediction.ical_booking.guest_name || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              ID: {prediction.ical_booking.id} | External: {prediction.ical_external_id || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: '30%',
                              verticalAlign: 'top',
                              pl: 0.75,
                              pr: 0.5,
                              wordBreak: 'break-word',
                              overflowWrap: 'anywhere',
                            }}
                          >
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                              <Typography variant="body2" fontWeight="medium" component="span">
                                {prediction.manual_booking.property_nickname || `Property ${prediction.manual_booking.property_id}`}
                              </Typography>
                              {isCancelledBookingStatus(prediction.manual_booking.status) && (
                                <Chip
                                  size="small"
                                  label={String(prediction.manual_booking.status ?? 'Cancelled').trim() || 'Cancelled'}
                                  color="error"
                                  variant="outlined"
                                  sx={{
                                    height: 'auto',
                                    minHeight: 22,
                                    maxWidth: '100%',
                                    '& .MuiChip-label': {
                                      whiteSpace: 'normal',
                                      py: 0.25,
                                      display: 'block',
                                      textAlign: 'left',
                                    },
                                  }}
                                />
                              )}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(prediction.manual_booking.check_in_date)} - {formatDate(prediction.manual_booking.check_out_date)}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              Guest: {prediction.manual_booking.guest_name || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              ID: {prediction.manual_booking.id} | External: {prediction.manual_external_id || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ width: 120, minWidth: 120, verticalAlign: 'top' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'stretch' }}>
                              <Button
                                variant="outlined"
                                color="inherit"
                                size="small"
                                startIcon={<VisibilityIcon fontSize="small" />}
                                onClick={() => setInspectPrediction(prediction)}
                                disabled={bulkLinking}
                              >
                                View
                              </Button>
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => void confirmMatch(prediction)}
                                disabled={bulkLinking}
                              >
                                Confirm Match
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={() => void rejectMatch(prediction)}
                                disabled={bulkLinking}
                              >
                                Reject
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded variant="outlined" sx={{ mb: 2 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="confirmed-matches-content"
                id="confirmed-matches-header"
              >
                <Typography variant="h6" component="span">
                  Confirmed Matches
                  {confirmedLinks.length > 0 ? ` (${confirmedLinks.length})` : ''}
                </Typography>
              </AccordionSummary>
              <AccordionDetails id="confirmed-matches-content" sx={{ pt: 1 }}>
              {confirmedLinks.length === 0 ? (
                <Alert severity="info">
                  No confirmed matches yet. Confirm predicted pairs above to create links.
                </Alert>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Status</TableCell>
                        <TableCell>iCal Booking</TableCell>
                        <TableCell>Manual Booking</TableCell>
                        <TableCell>Confidence</TableCell>
                        <TableCell>Confirmed At</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {confirmedLinks.map((link) => (
                        <TableRow key={link.id}>
                          <TableCell>
                            <Chip label="Linked" color="success" size="small" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              ID: {link.ical_booking_id}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              External: {link.ical_external_id || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              ID: {link.manual_booking_id}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              External: {link.manual_external_id || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {link.confidence_score ? `${(link.confidence_score * 100).toFixed(0)}%` : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {link.confirmed_at ? formatDateTime(link.confirmed_at) : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={async () => {
                                if (window.confirm('Are you sure you want to delete this link?')) {
                                  try {
                                    await api.delete(`/api/calendar/booking-links/${link.id}`);
                                    await fetchConfirmedLinks();
                                    await fetchPredictions();
                                  } catch (err: any) {
                                    console.error('Error deleting link:', err);
                                    setLinkingError(err.response?.data?.error || 'Failed to delete link');
                                  }
                                }
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded={false} variant="outlined" sx={{ mb: 2 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="rejected-matches-content"
                id="rejected-matches-header"
              >
                <Typography variant="h6" component="span">
                  Rejected Matches
                  {rejectedLinks.length > 0 ? ` (${rejectedLinks.length})` : ''}
                </Typography>
              </AccordionSummary>
              <AccordionDetails id="rejected-matches-content" sx={{ pt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Pairs you rejected are stored so they stay out of predictions. Delete a row to allow that pair to be
                  suggested again.
                </Typography>
                {rejectedLinks.length === 0 ? (
                  <Alert severity="info">No rejected matches yet.</Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Status</TableCell>
                          <TableCell>iCal booking</TableCell>
                          <TableCell>Manual / export booking</TableCell>
                          <TableCell>Confidence</TableCell>
                          <TableCell>Updated</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rejectedLinks.map((link) => {
                          const ical = link.ical_booking;
                          const manual = link.manual_booking;
                          return (
                            <TableRow key={link.id}>
                              <TableCell>
                                <Chip label="Rejected" color="default" size="small" />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  ID: {link.ical_booking_id}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {ical?.property_nickname || `Property ${ical?.property_id ?? '—'}`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {ical?.check_in_date
                                    ? `${formatDate(ical.check_in_date)} → ${formatDate(ical?.check_out_date || '')}`
                                    : '—'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {ical?.guest_name || 'Guest N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  ID: {link.manual_booking_id}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {manual?.property_nickname || `Property ${manual?.property_id ?? '—'}`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {manual?.check_in_date
                                    ? `${formatDate(manual.check_in_date)} → ${formatDate(manual?.check_out_date || '')}`
                                    : '—'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {manual?.guest_name || 'Guest N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {link.confidence_score != null
                                  ? `${(Number(link.confidence_score) * 100).toFixed(0)}%`
                                  : 'N/A'}
                              </TableCell>
                              <TableCell>
                                {link.updated_at ? formatDateTime(link.updated_at) : link.created_at ? formatDateTime(link.created_at) : 'N/A'}
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="Remove rejection (pair may appear as a prediction again)">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={async () => {
                                      if (
                                        !window.confirm(
                                          'Remove this rejected pair? It may show up in predictions again after you refresh.'
                                        )
                                      ) {
                                        return;
                                      }
                                      try {
                                        await api.delete(`/api/calendar/booking-links/${link.id}`);
                                        await fetchRejectedLinks();
                                        await fetchPredictions();
                                        setLinkingError('');
                                      } catch (err: any) {
                                        console.error('Error deleting rejected link:', err);
                                        setLinkingError(err.response?.data?.error || 'Failed to remove rejection');
                                      }
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </AccordionDetails>
            </Accordion>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {bookingsCountText && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {bookingsCountText}
              </Typography>
            )}
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Payments</strong> is built from every <strong>manual / export</strong> booking: link the Revolut
              payout to that upload using the exact <strong>host or total payout</strong> (including cents). Matching the
              calendar (<strong>iCal</strong>) to the same stay is optional and happens on the Linking tab; when a
              confirmed iCal↔manual pair exists, it is shown here so you can see all three aligned. Payout timing starts{' '}
              <strong>a few days after check-in</strong> (see below).{' '}
              <strong>{revolutWorkspace?.uk_property_nickname ?? '67RR'}</strong> uses the <strong>UK</strong> Revolut
              account; other properties use <strong>Ireland</strong>.
            </Alert>
            {revolutWorkspace?.uk_ledger_alternate_note && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {revolutWorkspace.uk_ledger_alternate_note}
              </Alert>
            )}
            {revolutWorkspace?.vrbo_ireland_counterparty_note && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {revolutWorkspace.vrbo_ireland_counterparty_note}
              </Alert>
            )}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" component="h3" gutterBottom sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1 }}>
                Matching rules (preset {paymentsMatchingScopeDisplay.preset})
                {!paymentsMatchingScopeDisplay.confirmedFromApi && (
                  <Typography component="span" variant="caption" color="text.secondary">
                    server defaults — confirm after workspace loads
                  </Typography>
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary" component="div" sx={{ maxWidth: 760 }}>
                For each manual / export booking with check-in and a payout figure: expected first payout day is check-in
                plus <strong>{paymentsMatchingScopeDisplay.payoutLagDays}</strong> calendar days. Revolut{' '}
                <strong>completed</strong> txs must match <strong>currency</strong> and <strong>exact amount</strong>; dates
                use <code>created_at_revolut</code> within the per-booking window (standard +{paymentsMatchingScopeDisplay.windowDays}{' '}
                days; VRBO Ireland +{paymentsMatchingScopeDisplay.vrboIrelandDays}).
              </Typography>
              <Box component="ul" sx={{ pl: 2.25, mt: 1, mb: 0 }}>
                <Typography component="li" variant="body2" color="text.secondary">
                  <strong>Ledgers:</strong> {paymentsMatchingScopeDisplay.ukNick} → UK; others → Ireland (preset 1-3-3 may
                  add flagged Ireland candidates for UK rows).
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>
                Workspace query
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Narrow which <strong>manual / export bookings</strong> are loaded (newest check-in first). Use this when the
                full list times out. Choices persist for this browser session.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end', mb: 1 }}>
                <TextField
                  label="Check-in from"
                  type="date"
                  size="small"
                  value={paymentsWsMinCheckIn}
                  onChange={(e) => setPaymentsWsMinCheckIn(e.target.value)}
                  disabled={revolutWorkspaceLoading}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Check-in to"
                  type="date"
                  size="small"
                  value={paymentsWsMaxCheckIn}
                  onChange={(e) => setPaymentsWsMaxCheckIn(e.target.value)}
                  disabled={revolutWorkspaceLoading}
                  InputLabelProps={{ shrink: true }}
                />
                <FormControl size="small" sx={{ minWidth: 200 }} disabled={revolutWorkspaceLoading}>
                  <InputLabel id="payments-ws-property-label">Property</InputLabel>
                  <Select
                    labelId="payments-ws-property-label"
                    label="Property"
                    value={paymentsWsPropertyId}
                    onChange={(e) => setPaymentsWsPropertyId(String(e.target.value))}
                  >
                    <MenuItem value="">
                      <em>All properties</em>
                    </MenuItem>
                    {paymentsWorkspacePropertyOptions.map(([pid, label]) => (
                      <MenuItem key={pid} value={String(pid)}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Max bookings"
                  type="number"
                  size="small"
                  value={paymentsWsMaxBookings}
                  onChange={(e) => setPaymentsWsMaxBookings(e.target.value)}
                  disabled={revolutWorkspaceLoading}
                  inputProps={{ min: 1, max: 10000 }}
                  sx={{ width: 120 }}
                  helperText="Blank = no cap (slow)"
                />
              </Box>
              {revolutWorkspace?.scope_applied && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Last response: <strong>{revolutWorkspace.scope_applied.manual_bookings_fetched}</strong> manual rows
                  fetched → <strong>{revolutWorkspace.scope_applied.workspace_rows}</strong> workspace rows
                  {revolutWorkspace.scope_applied.max_bookings != null
                    ? ` (cap ${revolutWorkspace.scope_applied.max_bookings})`
                    : ' (no cap)'}
                  {revolutWorkspace.scope_applied.min_check_in || revolutWorkspace.scope_applied.max_check_in
                    ? ` · check-in ${revolutWorkspace.scope_applied.min_check_in ?? '…'}–${revolutWorkspace.scope_applied.max_check_in ?? '…'}`
                    : ''}
                  {revolutWorkspace.scope_applied.property_id != null
                    ? ` · property id ${revolutWorkspace.scope_applied.property_id}`
                    : ''}
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={revolutWorkspaceLoading}
                  onClick={() => void fetchRevolutPaymentWorkspace()}
                >
                  {revolutWorkspaceLoading ? 'Loading workspace…' : 'Load workspace'}
                </Button>
                <Button
                  size="small"
                  disabled={revolutWorkspaceLoading}
                  onClick={() => {
                    setPaymentsWsMinCheckIn('');
                    setPaymentsWsMaxCheckIn('');
                    setPaymentsWsPropertyId('');
                    setPaymentsWsMaxBookings('200');
                  }}
                >
                  Reset filters
                </Button>
                {dateFrom || dateTo ? (
                  <Button
                    size="small"
                    disabled={revolutWorkspaceLoading}
                    onClick={() => {
                      if (dateFrom) setPaymentsWsMinCheckIn(dateFrom);
                      if (dateTo) setPaymentsWsMaxCheckIn(dateTo);
                    }}
                  >
                    Copy Bookings tab date filter
                  </Button>
                ) : null}
              </Box>
            </Paper>
            {revolutWorkspaceError && (
              <Alert severity="error" sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}>
                <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-line' }}>
                  {revolutWorkspaceError}
                </Typography>
              </Alert>
            )}
            {revolutWorkspaceLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, gap: 2 }}>
                <Alert severity="info" sx={{ maxWidth: 720 }} icon={<CircularProgress size={22} color="inherit" />}>
                  Building the payments workspace (up to{' '}
                  {paymentsWsMaxBookings.trim() ? paymentsWsMaxBookings.trim() : 'all matching'} manual rows per your
                  filters). Revolut candidate queries run per row—this can take several minutes on cloud hosts; timeout is
                  3 minutes per attempt with automatic retries.
                </Alert>
                <CircularProgress />
              </Box>
            ) : revolutWorkspace && revolutWorkspace.rows.length === 0 ? (
              <Alert severity="info">No manual or export bookings to reconcile.</Alert>
            ) : revolutWorkspace ? (
              <Box>
                {(
                  [
                    {
                      key: 'pending' as const,
                      title: 'Pending review',
                      sub:
                        'Not linked. The strongest match (first in list) is selected by default when candidates exist.',
                      rows: revolutPaymentSections.pending,
                      expanded: true,
                    },
                    {
                      key: 'linked' as const,
                      title: 'Linked to Revolut',
                      sub: 'Payout saved. Change the transaction and Accept to update; Reject removes the link and marks as skipped.',
                      rows: revolutPaymentSections.linked,
                      expanded: true,
                    },
                    {
                      key: 'rejected' as const,
                      title: 'Rejected / skipped',
                      sub: 'No Revolut match wanted. Undo reject to return to pending with suggestions.',
                      rows: revolutPaymentSections.rejected,
                      expanded: false,
                    },
                  ] as const
                ).map((acc) => (
                  <Accordion key={acc.key} defaultExpanded={acc.expanded} variant="outlined" sx={{ mb: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box>
                        <Typography fontWeight="medium">
                          {acc.title} ({acc.rows.length})
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {acc.sub}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      {acc.rows.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No rows in this section.
                        </Typography>
                      ) : (
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell align="center" sx={{ width: 72 }}>
                                  View
                                </TableCell>
                                <TableCell>Property</TableCell>
                                <TableCell>Check-in</TableCell>
                                <TableCell>Expected payout</TableCell>
                                <TableCell>iCal paired</TableCell>
                                <TableCell>Revolut account</TableCell>
                                <TableCell>Payment window</TableCell>
                                <TableCell>Linked</TableCell>
                                <TableCell>Match transaction</TableCell>
                                <TableCell align="right">Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {acc.rows.map((row: any) => {
                                const section = acc.key;
                                const bid = row.manual_booking?.id as number;
                                const pend = revolutPendingTxId[bid] ?? '';
                                const curLinked = row.manual_booking?.revolut_transaction_db_id;
                                const dirty =
                                  (pend === '' ? null : Number(pend)) !==
                                  (curLinked == null ? null : Number(curLinked));
                                const acceptDisabled = revolutSavingBookingId === bid || !dirty;
                                const exp =
                                  row.expected_amount != null && !Number.isNaN(Number(row.expected_amount))
                                    ? Number(row.expected_amount).toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })
                                    : null;
                                const ical = row.booking_link?.ical_booking;
                                return (
                                  <TableRow key={`${section}-${bid}`}>
                                    <TableCell align="center">
                                      <Tooltip title="View manual, iCal, and Revolut details">
                                        <IconButton
                                          size="small"
                                          aria-label="View payment row details"
                                          onClick={() => {
                                            setPaymentsDetailRow(row);
                                            setPaymentsDetailOpen(true);
                                          }}
                                        >
                                          <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell>{row.property_nickname || '—'}</TableCell>
                                    <TableCell>{formatDate(row.check_in)}</TableCell>
                                    <TableCell>
                                      {exp != null
                                        ? `${exp} ${row.currency}`
                                        : '— (no amount on booking)'}
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 200 }}>
                                      {ical ? (
                                        <Typography variant="caption" display="block">
                                          {ical.property_nickname || '—'} ·{' '}
                                          {ical.check_in_date ? formatDate(ical.check_in_date) : '—'}
                                        </Typography>
                                      ) : (
                                        <Typography variant="caption" color="text.secondary">
                                          —
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell>{row.revolut_account === 'uk' ? 'UK' : 'Ireland'}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                                      {formatDate(row.expected_payment_date_from)} –{' '}
                                      {formatDate(row.expected_payment_date_to)}
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 200 }}>
                                      {row.manual_booking?.revolut_transaction_summary ? (
                                        <Typography variant="caption" display="block">
                                          {Number(row.manual_booking.revolut_transaction_summary.amount).toLocaleString(
                                            undefined,
                                            { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                          )}{' '}
                                          {row.manual_booking.revolut_transaction_summary.currency} on{' '}
                                          {formatDateTime(row.manual_booking.revolut_transaction_summary.created_at || '')}
                                        </Typography>
                                      ) : (
                                        <Typography variant="caption" color="text.secondary">
                                          Not linked
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {section === 'rejected' ? (
                                        <Typography variant="caption" color="text.secondary">
                                          Skipped — use Undo reject
                                        </Typography>
                                      ) : (
                                        <>
                                          <FormControl size="small" fullWidth>
                                            <Select
                                              displayEmpty
                                              value={pend === '' ? '' : String(pend)}
                                              onChange={(e) => {
                                                const v = e.target.value;
                                                setRevolutPendingTxId((prev) => ({
                                                  ...prev,
                                                  [bid]: v === '' ? '' : Number(v),
                                                }));
                                              }}
                                            >
                                              <MenuItem value="">
                                                <em>None</em>
                                              </MenuItem>
                                              {row.candidate_transactions?.map((tx: any) => (
                                                <MenuItem key={tx.id} value={String(tx.id)}>
                                                  {Number(tx.amount).toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                  })}{' '}
                                                  {tx.currency} · {formatDate(tx.created_at)}{' '}
                                                  {(tx.description || '').slice(0, 40)}
                                                  {(tx.description || '').length > 40 ? '…' : ''}
                                                  {tx.vrbo_ireland_counterparty_match ? ' · VRBO (Niall Crotty)' : ''}
                                                  {tx.ledger_mismatch
                                                    ? ` · wrong ledger (${tx.transaction_revolut_account}, expected ${tx.expected_revolut_account})`
                                                    : ''}
                                                </MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                          {row.vrbo_ireland_niall_crotty_context &&
                                            (row.candidate_transactions?.length ?? 0) > 0 && (
                                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                VRBO / Ireland: Niall Crotty payouts are listed first when the description or
                                                counterparty matches.
                                              </Typography>
                                            )}
                                          {row.candidate_transactions?.length === 0 &&
                                            row.expected_amount != null && (
                                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                No matching Revolut rows in window (exact amount + currency).
                                              </Typography>
                                            )}
                                        </>
                                      )}
                                    </TableCell>
                                    <TableCell align="right">
                                      {section === 'rejected' ? (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          disabled={revolutSavingBookingId === bid}
                                          onClick={() => {
                                            void (async () => {
                                              try {
                                                setRevolutSavingBookingId(bid);
                                                setLinkingError('');
                                                await api.patch(`/api/calendar/unified/bookings/${bid}/revolut-transaction`, {
                                                  revolut_payment_declined: false,
                                                });
                                                await fetchRevolutPaymentWorkspace();
                                              } catch (err: any) {
                                                setLinkingError(
                                                  err.response?.data?.error || err.message || 'Failed to undo reject'
                                                );
                                              } finally {
                                                setRevolutSavingBookingId(null);
                                              }
                                            })();
                                          }}
                                        >
                                          Undo reject
                                        </Button>
                                      ) : (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'flex-end' }}>
                                          <Button
                                            size="small"
                                            variant="contained"
                                            color="success"
                                            disabled={acceptDisabled}
                                            onClick={() => {
                                              void (async () => {
                                                try {
                                                  setRevolutSavingBookingId(bid);
                                                  setLinkingError('');
                                                  const idNum = pend === '' || pend == null ? null : Number(pend);
                                                  const selectedCand = row.candidate_transactions?.find(
                                                    (c: any) => c.id === idNum
                                                  );
                                                  const body: Record<string, unknown> = {
                                                    revolut_transaction_db_id: idNum,
                                                  };
                                                  if (selectedCand?.ledger_mismatch) {
                                                    body.allow_mismatched_ledger = true;
                                                  }
                                                  await api.patch(`/api/calendar/unified/bookings/${bid}/revolut-transaction`, body);
                                                  await fetchRevolutPaymentWorkspace();
                                                } catch (err: any) {
                                                  setLinkingError(
                                                    err.response?.data?.error || err.message || 'Failed to save Revolut link'
                                                  );
                                                } finally {
                                                  setRevolutSavingBookingId(null);
                                                }
                                              })();
                                            }}
                                          >
                                            Accept
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            disabled={revolutSavingBookingId === bid}
                                            onClick={() => {
                                              void (async () => {
                                                try {
                                                  setRevolutSavingBookingId(bid);
                                                  setLinkingError('');
                                                  await api.patch(`/api/calendar/unified/bookings/${bid}/revolut-transaction`, {
                                                    revolut_payment_declined: true,
                                                  });
                                                  await fetchRevolutPaymentWorkspace();
                                                } catch (err: any) {
                                                  setLinkingError(
                                                    err.response?.data?.error || err.message || 'Failed to reject'
                                                  );
                                                } finally {
                                                  setRevolutSavingBookingId(null);
                                                }
                                              })();
                                            }}
                                          >
                                            Reject
                                          </Button>
                                        </Box>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            ) : null}
            {tabValue === 2 && linkingError && (
              <Alert severity="error" sx={{ mt: 1, '& .MuiAlert-message': { width: '100%' } }}>
                <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-line' }}>
                  {linkingError}
                </Typography>
              </Alert>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            {bookingsCountText && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {bookingsCountText}
              </Typography>
            )}
            <BookingCommsTab />
          </TabPanel>

        </CardContent>
      </Card>

      {/* Payments tab: full manual / iCal / Revolut detail */}
      <Dialog
        open={paymentsDetailOpen}
        onClose={() => {
          setPaymentsDetailOpen(false);
          setPaymentsDetailRow(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          Payment row details
          <IconButton
            aria-label="Close"
            onClick={() => {
              setPaymentsDetailOpen(false);
              setPaymentsDetailRow(null);
            }}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {paymentsDetailRow && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {paymentsDetailRow.property_nickname || '—'} · Manual check-in{' '}
                {paymentsDetailRow.check_in ? formatDate(paymentsDetailRow.check_in) : '—'}
              </Typography>
              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.05rem' }}>
                Reconciliation context
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5, mb: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Expected payout (from manual)
                  </Typography>
                  <Typography variant="body2">
                    {paymentsDetailRow.expected_amount != null && !Number.isNaN(Number(paymentsDetailRow.expected_amount))
                      ? `${Number(paymentsDetailRow.expected_amount).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })} ${paymentsDetailRow.currency || ''}`
                      : '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Revolut account
                  </Typography>
                  <Typography variant="body2">
                    {paymentsDetailRow.revolut_account === 'uk' ? 'UK' : 'Ireland'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Payment window
                  </Typography>
                  <Typography variant="body2">
                    {paymentsDetailRow.expected_payment_date_from
                      ? formatDate(paymentsDetailRow.expected_payment_date_from)
                      : '—'}{' '}
                    –{' '}
                    {paymentsDetailRow.expected_payment_date_to
                      ? formatDate(paymentsDetailRow.expected_payment_date_to)
                      : '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Payments status
                  </Typography>
                  <Typography variant="body2">
                    {paymentsDetailRow.manual_booking?.revolut_transaction_db_id
                      ? 'Linked to Revolut'
                      : paymentsDetailRow.manual_booking?.revolut_payment_declined
                        ? 'Rejected / skipped'
                        : 'Pending review'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Confirmed iCal link
                  </Typography>
                  <Typography variant="body2">
                    {paymentsDetailRow.booking_link?.id != null
                      ? `Link #${paymentsDetailRow.booking_link.id}${
                          paymentsDetailRow.booking_link.confirmed_at
                            ? ` · confirmed ${formatDateTime(paymentsDetailRow.booking_link.confirmed_at)}`
                            : ''
                        }`
                      : 'None'}
                  </Typography>
                </Box>
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Typography variant="caption" color="text.secondary">
                    Candidate transactions in window
                  </Typography>
                  <Typography variant="body2">
                    {(paymentsDetailRow.candidate_transactions?.length ?? 0) === 0
                      ? 'None'
                      : `${paymentsDetailRow.candidate_transactions.length} match(es) on amount + currency + account`}
                  </Typography>
                </Box>
                {paymentsDetailRow.vrbo_ireland_niall_crotty_context && (
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography variant="caption" color="text.secondary">
                      VRBO (Ireland)
                    </Typography>
                    <Typography variant="body2">
                      Expect Niall Crotty on the Revolut counterparty / description for many VRBO payouts (non-67RR).
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.05rem' }}>
                Manual booking (upload)
              </Typography>
              <PaymentsDetailFieldGrid
                data={(paymentsDetailRow.manual_booking || null) as Record<string, unknown> | null}
              />

              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.05rem' }}>
                iCal booking (confirmed pair, optional)
              </Typography>
              {paymentsDetailRow.booking_link?.ical_booking ? (
                <PaymentsDetailFieldGrid
                  data={paymentsDetailRow.booking_link.ical_booking as Record<string, unknown>}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No confirmed iCal↔manual link. Use the Linking tab to pair calendar rows.
                </Typography>
              )}

              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.05rem' }}>
                Revolut transaction
              </Typography>
              {(() => {
                const detailBid = paymentsDetailRow.manual_booking?.id as number | undefined;
                const pendTx =
                  detailBid != null ? revolutPendingTxId[detailBid] ?? '' : '';
                const pendId = pendTx === '' || pendTx == null ? null : Number(pendTx);
                const saved = paymentsDetailRow.linked_revolut_transaction as Record<string, unknown> | null | undefined;
                const pendingCand =
                  pendId != null
                    ? paymentsDetailRow.candidate_transactions?.find((c: any) => c.id === pendId)
                    : null;
                const savedId =
                  saved && (saved as { id?: unknown }).id != null
                    ? Number((saved as { id?: unknown }).id)
                    : null;

                return (
                  <Box>
                    {saved && Object.keys(saved).length > 0 ? (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Saved link (database)
                        </Typography>
                        <PaymentsDetailFieldGrid data={saved} />
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No Revolut transaction saved on this manual booking yet.
                      </Typography>
                    )}
                    {pendId != null && savedId !== pendId && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="subtitle2" color="warning.main" gutterBottom>
                          Selected in table (not saved yet)
                        </Typography>
                        {pendingCand ? (
                          <PaymentsDetailFieldGrid data={pendingCand as Record<string, unknown>} />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Selected id {pendId} is not in the current candidate list. Use Refresh on the Payments tab or
                            pick a listed transaction.
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPaymentsDetailOpen(false);
              setPaymentsDetailRow(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk delete confirmation */}
      <Dialog
        open={bulkDeleteDialogOpen}
        onClose={() => (!bulkDeleting ? setBulkDeleteDialogOpen(false) : undefined)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete selected bookings?</DialogTitle>
        <DialogContent>
          {(() => {
            const ids = resolveSelectedBookingIdsForDelete(enrichedBookings);
            const preview = ids.slice(0, 40).join(', ');
            const more = ids.length > 40 ? ` … +${ids.length - 40} more` : '';
            return (
              <>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  Are you sure you want to delete{' '}
                  <strong>
                    {ids.length} booking{ids.length === 1 ? '' : 's'}
                  </strong>
                  ? This cannot be undone.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Selected table rows: {selectedBookingIds.size}. Merged Combined rows expand to both
                  underlying iCal and Export/Manual IDs.
                </Typography>
                <Typography
                  variant="caption"
                  component="div"
                  sx={{
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    bgcolor: 'action.hover',
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  IDs: {preview}
                  {more}
                </Typography>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialogOpen(false)} disabled={bulkDeleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={bulkDeleting || selectedBookingIds.size === 0}
            startIcon={bulkDeleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            onClick={() => handleConfirmBulkDelete(enrichedBookings)}
          >
            {bulkDeleting ? 'Deleting…' : 'Yes, delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Booking Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingBooking ? 'Edit Booking' : 'Add New Booking'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth required>
                <InputLabel>Property</InputLabel>
                <Select
                  value={formData.property_id}
                  label="Property"
                  onChange={(e) => setFormData({ ...formData, property_id: e.target.value })}
                  disabled={!!editingBooking}
                >
                  <MenuItem value="1">Property 1</MenuItem>
                  <MenuItem value="2">Property 2</MenuItem>
                  <MenuItem value="3">Property 3</MenuItem>
                  <MenuItem value="4">Property 4</MenuItem>
                  <MenuItem value="5">Property 5</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Platform</InputLabel>
                <Select
                  value={formData.platform}
                  label="Platform"
                  onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                >
                  <MenuItem value="Airbnb">Airbnb</MenuItem>
                  <MenuItem value="VRBO">VRBO</MenuItem>
                  <MenuItem value="Manual">Manual</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Guest Name"
                value={formData.guest_name}
                onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })}
              />
              <TextField
                fullWidth
                label="Guest Email"
                type="email"
                value={formData.guest_email}
                onChange={(e) => setFormData({ ...formData, guest_email: e.target.value })}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Check-in Date"
                type="date"
                value={formData.check_in_date}
                onChange={(e) => setFormData({ ...formData, check_in_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
              <TextField
                fullWidth
                label="Check-out Date"
                type="date"
                value={formData.check_out_date}
                onChange={(e) => setFormData({ ...formData, check_out_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Box>

            <TextField
              fullWidth
              label="Booked (when guest made the reservation)"
              type="datetime-local"
              value={formData.booked_at}
              onChange={(e) => setFormData({ ...formData, booked_at: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Optional - when the guest made the booking on the platform"
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Adults Count"
                type="number"
                value={formData.adults_count}
                onChange={(e) => setFormData({ ...formData, adults_count: parseInt(e.target.value) })}
                inputProps={{ min: 0 }}
                required
              />
              <TextField
                fullWidth
                label="Children Count"
                type="number"
                value={formData.children_count}
                onChange={(e) => setFormData({ ...formData, children_count: parseInt(e.target.value) })}
                inputProps={{ min: 0 }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Total Payout Amount"
                type="number"
                value={formData.total_payout_amount}
                onChange={(e) => setFormData({ ...formData, total_payout_amount: e.target.value })}
                inputProps={{ step: "0.01" }}
              />
              <TextField
                fullWidth
                label="Host Payout Amount"
                type="number"
                value={formData.host_payout_amount}
                onChange={(e) => setFormData({ ...formData, host_payout_amount: e.target.value })}
                inputProps={{ step: "0.01" }}
              />
            </Box>

            <TextField
              fullWidth
              label="Reservation URL"
              value={formData.reservation_url}
              onChange={(e) => setFormData({ ...formData, reservation_url: e.target.value })}
              placeholder="https://airbnb.com/reservation/..."
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={formData.currency}
                  label="Currency"
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                >
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <FlightDetailsFormSection
              title="Flight IN (arrival)"
              value={flightInForm}
              onChange={setFlightInForm}
            />
            <FlightDetailsFormSection
              title="Flight OUT (departure)"
              value={flightOutForm}
              onChange={setFlightOutForm}
            />

            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingBooking ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Predicted match: side-by-side field comparison */}
      <Dialog
        open={inspectPrediction !== null}
        onClose={() => setInspectPrediction(null)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" component="span">
            Inspect predicted pair
          </Typography>
          <IconButton aria-label="Close" onClick={() => setInspectPrediction(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {inspectPrediction && (
            <>
              <Box
                sx={{
                  mb: 1.5,
                  px: 1.5,
                  py: 1,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: { xs: 1.5, sm: 2 },
                    alignItems: 'start',
                  }}
                >
                  {/* Left: score, stay dates, ids */}
                  <Box
                    sx={{
                      minWidth: 0,
                      pr: { sm: 1.5 },
                      borderRight: { sm: 1 },
                      borderBottom: { xs: 1, sm: 0 },
                      borderColor: 'divider',
                      pb: { xs: 1.5, sm: 0 },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                      {(Number(inspectPrediction.confidence_score ?? 0) * 100).toFixed(0)}%
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5, fontWeight: 400 }}>
                        confidence
                      </Typography>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                      Stay dates
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'max-content 1fr 1fr',
                        gap: 0.35,
                        columnGap: 1,
                        typography: 'caption',
                        mb: 1,
                      }}
                    >
                      <span />
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        iCal
                      </Typography>
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Manual
                      </Typography>
                      <Typography component="span" variant="caption" color="text.secondary">
                        Check-in
                      </Typography>
                      <Typography component="span" variant="caption">
                        {formatDate(String(inspectPrediction.ical_booking?.check_in_date ?? ''))}
                      </Typography>
                      <Typography component="span" variant="caption">
                        {formatDate(String(inspectPrediction.manual_booking?.check_in_date ?? ''))}
                      </Typography>
                      <Typography component="span" variant="caption" color="text.secondary">
                        Check-out
                      </Typography>
                      <Typography component="span" variant="caption">
                        {formatDate(String(inspectPrediction.ical_booking?.check_out_date ?? ''))}
                      </Typography>
                      <Typography component="span" variant="caption">
                        {formatDate(String(inspectPrediction.manual_booking?.check_out_date ?? ''))}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ fontFamily: 'monospace', wordBreak: 'break-all', display: 'block', mb: 0.5 }}
                    >
                      <strong>UID</strong> {inspectPrediction.uid ?? '—'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, typography: 'caption' }}>
                      <Typography component="span" variant="caption">
                        <strong>iCal</strong> #{inspectPrediction.ical_booking_id ?? '—'}
                      </Typography>
                      <Typography component="span" variant="caption">
                        <strong>Manual</strong> #{inspectPrediction.manual_booking_id ?? '—'}
                      </Typography>
                    </Box>
                  </Box>
                  {/* Right: all match criteria chips */}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.75 }}>
                      Match criteria
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                      {(() => {
                        const inspectFlags = computePredictionReasonFlags(
                          inspectPrediction.ical_booking ?? {},
                          inspectPrediction.manual_booking ?? {}
                        );
                        return BOOKING_LINK_INSPECT_CRITERIA.map(({ key, label }) => {
                          const ok = inspectFlags[key];
                          return (
                            <Chip
                              key={key}
                              label={`${label} · ${ok ? 'Yes' : 'No'}`}
                              size="small"
                              color={ok ? 'success' : 'error'}
                              variant={ok ? 'filled' : 'outlined'}
                              sx={{ height: 22, '& .MuiChip-label': { px: 0.75, py: 0, typography: 'caption' } }}
                            />
                          );
                        });
                      })()}
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Bookings — same field on both sides (rows highlighted when values differ)
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'min(70vh, 560px)' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, minWidth: 140, bgcolor: 'background.paper' }}>Field</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>iCal (Airbnb / VRBO)</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Manual / Export</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {buildBookingCompareRows(
                      inspectPrediction.ical_booking as Record<string, unknown>,
                      inspectPrediction.manual_booking as Record<string, unknown>
                    ).map((row) => (
                      <TableRow
                        key={row.key}
                        sx={(theme) =>
                          compareRowsDiffer(row.ical, row.manual)
                            ? { bgcolor: theme.palette.action.selected }
                            : {}
                        }
                      >
                        <TableCell
                          component="th"
                          scope="row"
                          sx={{ fontWeight: 600, verticalAlign: 'top' }}
                        >
                          {row.key}
                        </TableCell>
                        <TableCell
                          sx={{
                            verticalAlign: 'top',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            maxWidth: { xs: 120, sm: 240, md: 320 },
                          }}
                        >
                          {row.ical}
                        </TableCell>
                        <TableCell
                          sx={{
                            verticalAlign: 'top',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            maxWidth: { xs: 120, sm: 240, md: 320 },
                          }}
                        >
                          {row.manual}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInspectPrediction(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!commsModalBooking}
        onClose={() => setCommsModalBooking(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          Comms
          {commsModalBooking
            ? ` · Booking #${commsModalBooking.id}${
                airbnbConfirmationCodeFromBooking(commsModalBooking)
                  ? ` · ${airbnbConfirmationCodeFromBooking(commsModalBooking)}`
                  : ''
              }`
            : ''}
          <IconButton
            aria-label="Close Comms"
            onClick={() => setCommsModalBooking(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {commsModalBooking ? (
            <BookingCommsPanel
              booking={commsModalBooking}
              confirmationCode={airbnbConfirmationCodeFromBooking(commsModalBooking)}
              compactHeader
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            component="a"
            href={commsModalBooking ? `/bookings/${commsModalBooking.id}?tab=comms` : undefined}
            target="_blank"
            rel="noopener noreferrer"
            disabled={!commsModalBooking}
          >
            Open full page
          </Button>
          <Button onClick={() => setCommsModalBooking(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={8000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={APP_TOP_TOAST_SNACKBAR_SX}
      >
        <Alert
          onClose={() => setToast(null)}
          severity={toast?.severity || 'info'}
          sx={{ width: '100%', maxWidth: 480 }}
        >
          <Stack spacing={0.25}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {toast?.message}
            </Typography>
            <ToastCodeCaption
              code={toast?.diagnostics.code || ''}
              requestId={toast?.diagnostics.requestId}
              jobId={toast?.diagnostics.jobId}
              errorCode={toast?.diagnostics.errorCode}
              copyBody={toast?.message}
            />
          </Stack>
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default CalendarViewTable;
